/**
 * GameScene.js
 *
 * Phase 1 MVP of the KRAKEN 3D Gameplay feature.
 *
 * Responsibilities:
 *   - Initialize Babylon.js engine + scene on a provided canvas
 *   - Build a procedurally tinted ground plane + skybox driven by body metadata
 *   - Create an astronaut player rig with FPS + TPS camera modes
 *   - WASD movement + mouse-look via Pointer Lock
 *   - Place a spaceship near spawn that can be entered (stationary cockpit stub)
 *   - Expose simple API for React layer: setCameraMode, getState, dispose
 *
 * Notes:
 *   - Pure JS / Babylon, no React inside this file.
 *   - Phase 2+ will add real flight controls, system travel, biomes.
 */

import {
  Engine,
  Scene,
  Vector3,
  Color3,
  Color4,
  HemisphericLight,
  DirectionalLight,
  PointLight,
  SpotLight,
  MeshBuilder,
  Mesh,
  StandardMaterial,
  Texture,
  DynamicTexture,
  FreeCamera,
  ArcRotateCamera,
  TransformNode,
  Quaternion,
  Matrix,
  Tools,
  VertexBuffer,
  ParticleSystem,
  ShadowGenerator,
  GlowLayer,
  SceneLoader,
  DefaultRenderingPipeline,
  ColorCurves,
} from '@babylonjs/core';
// Side-effect import that registers the glTF/glb loader with Babylon's
// SceneLoader. Required for `ImportMeshAsync` to handle .glb files.
import '@babylonjs/loaders/glTF';

import CombatSystem from './CombatSystem';
import MiningSystem from './MiningSystem';
import CitySystem from './CitySystem';
import StructuresSystem from './StructuresSystem';
import EffectsSystem, { buildAsteroidField } from './EffectsSystem';
import NpcSystem from './NpcSystem';
import AnomalySystem from './AnomalySystem';
import DiscoverySystem from './DiscoverySystem';
import CaveSystem from './CaveSystem';
import DerelictSystem from './DerelictSystem';
import LegendarySystem from './LegendarySystem';
import StationInteriorSystem from './StationInteriorSystem';
import ShipInteriorSystem from './ShipInteriorSystem';
import WeatherSystem from './WeatherSystem';
import { getAudioSystem } from './AudioSystem';
import { buildShip } from './ShipBuilder';
import { createPlanetMaterial } from './PlanetTexture';

// --- Body color / mood derivation -----------------------------------------

const PLANET_PALETTES = {
  Terrestrial: { ground: '#3a5a3a', sky: '#5e8cc8', fog: '#2c3e50' },
  'Gas Giant': { ground: '#7a5a35', sky: '#c39a6b', fog: '#5e4424' },
  'Ice Giant': { ground: '#a8d4f0', sky: '#7fb4e6', fog: '#4a7aa1' },
  Dwarf: { ground: '#6a5e54', sky: '#1a1a2e', fog: '#0a0a1a' },
  Lava: { ground: '#7a1a0a', sky: '#7a2a1a', fog: '#3a0a05' },
  Ocean: { ground: '#1a4a7a', sky: '#5e9ec8', fog: '#0a2a4a' },
  Moon: { ground: '#8a8a8a', sky: '#0a0a18', fog: '#050510' },
  Default: { ground: '#6a6a6a', sky: '#1a1a2e', fog: '#0a0a1a' },
};

function paletteFor(body) {
  if (!body) return PLANET_PALETTES.Default;
  if (body.kind === 'moon') return PLANET_PALETTES.Moon;
  return PLANET_PALETTES[body.type] || PLANET_PALETTES.Default;
}

function hexToColor3(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  return new Color3(r, g, b);
}

// --- Main GameScene class --------------------------------------------------

export class GameScene {
  constructor(canvas, options) {
    this.canvas = canvas;
    this.body = options.body;          // { kind: 'planet'|'moon', type, atmosphere, habitability, name, ... }
    this.onStateChange = options.onStateChange || (() => {});
    this.onLandRequest = options.onLandRequest || (() => {});
    // Phase 7 combat hooks (optional)
    this.onDamageTaken = options.onDamageTaken || (() => {});
    this.onDeath = options.onDeath || (() => {});
    this.onKill = options.onKill || (() => {});
    this.onVitalsRegen = options.onVitalsRegen || (() => {});
    this.onMiningExtract = options.onMiningExtract || (() => {});
    this.onFire = options.onFire || (() => {});
    this.onCityProximity = options.onCityProximity || (() => {});
    // Phase 3 — fired once a guarded wreck's drones are all destroyed.
    this.onDerelictCleared = options.onDerelictCleared || (() => {});
    this._disposed = false;
    this.mode = 'surface'; // 'surface' | 'space'

    // June 2026 — Hangar: the active ship definition (visual + stats). Defaults
    // to the Scout if none is supplied. `_shipPerf` scales the flight model.
    this.shipDef = options.shipDef || null;
    this.shipId = options.activeShip || this.shipDef?.id || 'scout';
    const speedMult = Number(this.shipDef?.speed_mult || 1.0) || 1.0;
    this._shipPerf = { speedMult };
    // Skill-tree progression multipliers (applied to walk / ship / jump).
    this._progression = {
      walkMult: Number(options.progression?.walkMult || 1) || 1,
      shipSpeedMult: Number(options.progression?.shipSpeedMult || 1) || 1,
      jumpMult: Number(options.progression?.jumpMult || 1) || 1,
    };

    // Graphics quality tier: 'low' | 'medium' | 'cinematic'. Gates the costly
    // post-processing, shadow resolution, surface vegetation density, water
    // ripples and explosion FX so users can trade fidelity for FPS.
    this.graphicsQuality = options.graphicsQuality || 'cinematic';

    this.engine = new Engine(canvas, true, { stencil: true, preserveDrawingBuffer: true });
    // Cap the render-target resolution. Default Babylon renders at
    // devicePixelRatio (so a retina/4K screen renders ~4× the pixels of a 1×
    // panel → 4× fragment shader cost). Capping at 1.0 (one render pixel per
    // CSS pixel) is the single biggest perf win on Mac/iOS and is visually
    // almost identical thanks to FXAA + sharpen in the pipeline. The user
    // can raise this later if they want crisper text on retina screens.
    try { this.engine.setHardwareScalingLevel(1.0); } catch (_e) { /* noop */ }
    this.scene = new Scene(this.engine);

    // Ensure EVERY material can be lit by enough simultaneous lights. Babylon
    // defaults StandardMaterial to 4 lights and silently drops extras, which
    // meant the player flashlight (and built floodlights) only lit the ground
    // (raised to 8) while trees/rocks/ship/structures stayed dark. This
    // observer bumps the cap on every material created from here on.
    this.scene.onNewMaterialAddedObservable.add((mat) => {
      if (mat && typeof mat.maxSimultaneousLights === 'number' && mat.maxSimultaneousLights < 8) {
        mat.maxSimultaneousLights = 8;
      }
    });

    // Derive atmospheric mood + gravity from the body before lighting/skybox setup
    this.atmosphere = String(this.body?.atmosphere || 'None').toLowerCase();
    this.gravity = this._deriveGravity();

    const palette = paletteFor(this.body);
    this._buildAtmosphereTint(palette);
    this.scene.clearColor = Color4.FromColor3(this.skyTint, 1);
    this.scene.fogMode = Scene.FOGMODE_EXP2;
    this.scene.fogDensity = this.fogDensity;
    this.scene.fogColor = this.fogColor;

    this._setupLighting(palette);
    this._setupSkybox(palette);
    this._setupGround(palette);
    this._setupCelestials();
    this._setupClouds();
    this._setupPlayer();
    this._setupSpaceship();
    this._setupSpace();
    this._setupInput();
    this._setupAtmosphereProps(palette);

    this.cameraMode = 'fps';
    this.shipCameraMode = 'tps';  // chase by default while piloting
    this._applyCameraMode();

    // Post-processing pipeline — adds bloom + ACES tone mapping + FXAA +
    // subtle film grain + chromatic aberration. Built AFTER cameras exist
    // so we can attach to both first-person + flight cams.
    this._setupRenderingPipeline();

    // State exposed to React
    this.state = {
      cameraMode: this.cameraMode,
      flashlightOn: false,
      shipCameraMode: this.shipCameraMode,
      nearShip: false,
      inShip: false,
      paused: false,
      pointerLocked: false,
      pointerLockUnavailable: false,
      shipSpeed: 0,
      shipAltitude: 0,
      shipThrottle: 0,
      shipBoost: false,
      canExit: true,
      exitDenied: false,
      gravity: this.gravity,
      atmosphere: this.atmosphere,
      isGrounded: true,
      // Phase 5 system-travel state
      mode: 'surface',
      canLaunch: false,
      approachTarget: null,
      nearestBodyName: null,
      nearestBodyDistance: 0,
      // Phase X — orbital trading stations
      nearStation: null,
      bodyList: this._spaceBodyHudList(),
      // Phase 6 — expanded HUD state
      heading: 0,                // degrees 0..360, 0 = north (-Z)
      coords: { x: 0, y: 0, z: 0 }, // world-space player position
      biomeLabel: this._biomeLabel(),
      oxygen: 100,
      maxOxygen: 100,
      oxygenDepleting: this._atmosphereBreathable() === false,
      breathable: this._atmosphereBreathable(),
      shipBearing: 0,            // degrees from player toward ship
      shipDistance: 0,
      pois: [],                  // [{ id, kind: 'ship'|'deposit'|'enemy'|'city', x, z, label? }]
      // Phase 7 — combat state
      weapon: null,              // { weaponName, ammoCurrent, ammoMax, reloading, reloadProgress, recoilPitch }
      enemyPois: [],
      // Phase 8 — mining state
      mining: null,              // { active, progress, target: {symbol, name, rarity, yield, distance}, elementName }
      depositPois: [],
      atShipDock: false,
      // Phase 9 — cities & settlements
      city: { hasCity: false, nearCity: false, distance: 0, name: null },
      // Phase 11 — structures & build mode
      build: { active: false, typeId: null, valid: false },
      nearStructure: null,                    // {id, type, distance} or null
      // June 2026 — Underground caves
      inCave: false,
      nearCaveEntrance: null,                 // {id, distance} or null
      nearCaveExit: null,                     // {distance} or null
      cavePois: [],                           // surface entrance radar POIs
      // Phase 3 — Derelict ships & shipwrecks
      inDerelict: false,
      nearDerelict: null,                     // {id, name, distance} or null (exterior)
      nearDerelictExit: null,                 // {distance} or null
      nearDerelictContainer: null,            // {id, label, is_module, distance} or null
      nearDerelictLog: null,                  // {distance} or null
      derelictCleared: false,
      derelictPois: [],                       // exterior wreck radar POIs
      // June 2026 — Walkable station interiors
      inStation: false,
      stationInfo: null,                      // {id, name} while docked inside
      nearStationTerminal: null,              // {distance} or null
      nearStationExit: null,                  // {distance} or null
      nearStationShipyard: null,              // {distance} or null (Hangar)
      nearStationSkills: null,                // {distance} or null (Skill Tree)
      // June 2026 — Hangar: walkable ship interiors (large ships)
      inShipInterior: false,
      canBoardInterior: false,                // ship has interior + is landed
      nearShipInteriorExit: null,             // {distance} or null
      nearShipFabricator: null,               // {distance} or null (Phase 2)
      shipName: this.shipDef?.name || 'Scout',
      // June 2026 — Dynamic weather (makeup-driven)
      weather: { id: 'clear', label: 'Clear', icon: '☀', intensity: 0, severity: 'calm' },
      lightningAt: 0,
    };
    this._emit();

    // Phase 7 — combat must initialize AFTER `this.state` exists, because
    // CombatSystem emits an initial weapon state on construction.
    this._setupCombat();
    // Phase 8 — mining (same ordering requirement)
    this._setupMining();
    // Phase 9 — cities (idle until React supplies a city payload)
    this._setupCity();
    // Phase 11 — player structures + build preview
    this._setupStructures();
    // Phase 12a — particle FX, ship exhaust, biome haze, asteroid field
    this._setupEffects();
    // Phase 12b — NPC life (pedestrians, wildlife, ambient ships)
    this._setupNpcs();
    // Phase 13 (P3) — Survey anomalies (for survey_anomalies contract)
    this._setupAnomalies();
    // June 2026 — Underground caves (surface entrances + on-demand caverns)
    this._setupCaves();
    // Phase 3 — Derelict ships & shipwrecks (markers + on-demand interiors)
    this._setupDerelicts();
    // June 2026 — Walkable station interiors (built on demand when docking)
    this._setupStationInterior();
    // June 2026 — Walkable ship interiors (large ships; built on demand)
    this._setupShipInterior();
    // June 2026 — Dynamic, makeup-driven weather (surface only)
    this._setupWeather();

    this.engine.runRenderLoop(() => this._tick());
    this._onResize = () => this.engine.resize();
    window.addEventListener('resize', this._onResize);
  }

  // ---------- procedural terrain (Phase 4) ----------

  _hashStringToInt(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  // 2D hash → [0, 1]
  _hash2(x, y, seed) {
    let h = (x * 374761393 + y * 668265263 + seed) | 0;
    h = (h ^ (h >>> 13)) * 1274126177;
    h = h ^ (h >>> 16);
    return ((h >>> 0) % 100000) / 100000;
  }

  // Value noise with bilinear interpolation
  _valueNoise(x, y, seed) {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const xf = x - xi;
    const yf = y - yi;
    const r00 = this._hash2(xi, yi, seed);
    const r10 = this._hash2(xi + 1, yi, seed);
    const r01 = this._hash2(xi, yi + 1, seed);
    const r11 = this._hash2(xi + 1, yi + 1, seed);
    const sx = xf * xf * (3 - 2 * xf);
    const sy = yf * yf * (3 - 2 * yf);
    const a = r00 * (1 - sx) + r10 * sx;
    const b = r01 * (1 - sx) + r11 * sx;
    return a * (1 - sy) + b * sy;
  }

  // Fractal noise → centered around 0 with given amplitude
  _fbm(x, y, seed, octaves, freq, amp) {
    let total = 0;
    let f = freq;
    let a = amp;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      total += a * (this._valueNoise(x * f, y * f, seed + o * 1013) - 0.5) * 2;
      norm += a;
      a *= 0.5;
      f *= 2;
    }
    return total / Math.max(0.001, norm) * amp;
  }

  // Ridged multifractal — sharper peaks/valleys than vanilla FBM. Useful for
  // mountain ridge lines, crevasse fields, dune crests, and canyon networks.
  // Returns values in roughly [-amp, +amp].
  _ridgedNoise(x, y, seed, octaves, freq, amp) {
    let total = 0;
    let f = freq;
    let a = amp;
    let norm = 0;
    for (let o = 0; o < octaves; o++) {
      // 1 - |2n - 1|  → sharp ridge at noise = 0.5
      const v = this._valueNoise(x * f, y * f, seed + o * 1013);
      let n = 1 - Math.abs(v * 2 - 1);
      n = n * n; // sharpen
      total += a * n;
      norm += a;
      a *= 0.55;
      f *= 2.07;
    }
    return (total / Math.max(0.001, norm) - 0.5) * 2 * amp;
  }

  // Biome-specific macro shaper — adds large-scale recognisable terrain
  // signatures (mesas / dunes / glacier ridges / lava cones) on top of the
  // generic FBM + ridge pipeline so different biomes feel structurally
  // different, not just colored differently.
  _biomeShaper(biomeType, x, z, seed, amp) {
    switch (biomeType) {
      case 'lava': {
        // Volcanic terrain: ridge field punctuated by sharp cones
        const cones = this._ridgedNoise(x, z, seed + 313, 3, 0.006, amp * 0.9);
        return Math.max(0, cones) * 1.1;
      }
      case 'ocean': {
        // Gentle rolling swells; never enough to break the water plane.
        return this._fbm(x, z, seed + 222, 3, 0.005, amp * 0.35);
      }
      case 'ice': {
        // Glacial crevasse network — ridged at multiple scales.
        return this._ridgedNoise(x, z, seed + 444, 4, 0.012, amp * 0.7);
      }
      case 'terrestrial': {
        // Rolling foothills + occasional bluffs; combine soft FBM with mild
        // ridges to evoke valleys and ridgelines.
        const hills = this._fbm(x, z, seed + 555, 3, 0.004, amp * 0.55);
        const bluff = Math.max(0, this._ridgedNoise(x, z, seed + 556, 3, 0.009, amp * 0.45));
        return hills + bluff * 0.6;
      }
      case 'moon':
      case 'dwarf': {
        // Mostly flat regolith with occasional escarpments and gentle mounds.
        return this._fbm(x, z, seed + 777, 2, 0.0045, amp * 0.4);
      }
      case 'gas': {
        // Cloud-floor: faint banded shimmer only.
        return Math.sin(z * 0.005 + this._valueNoise(x * 0.002, z * 0.002, seed) * 4) * amp * 0.18;
      }
      default:
        return 0;
    }
  }

  _biomeParams() {
    const kind = this.body?.kind;
    const type = this.body?.type;
    if (kind === 'moon' || type === 'Moon') {
      return { type: 'moon', amp: 9, freq: 0.018, octaves: 4, craters: 22, vegetation: 0, ice: 0, lava: 0, water: false };
    }
    switch (type) {
      case 'Lava':
        return { type: 'lava', amp: 18, freq: 0.014, octaves: 4, craters: 4, vegetation: 0, ice: 0, lava: 18, water: false };
      case 'Ocean':
        return { type: 'ocean', amp: 8, freq: 0.01, octaves: 4, craters: 0, vegetation: 6, ice: 0, lava: 0, water: true, seaLevel: 1.2 };
      case 'Terrestrial':
        return { type: 'terrestrial', amp: 14, freq: 0.012, octaves: 4, craters: 2, vegetation: 60, ice: 0, lava: 0, water: true, seaLevel: -9 };
      case 'Ice Giant':
        return { type: 'ice', amp: 7, freq: 0.009, octaves: 3, craters: 0, vegetation: 0, ice: 38, lava: 0, water: false };
      case 'Gas Giant':
        return { type: 'gas', amp: 3, freq: 0.006, octaves: 3, craters: 0, vegetation: 0, ice: 0, lava: 0, water: false };
      case 'Dwarf':
        return { type: 'dwarf', amp: 10, freq: 0.02, octaves: 4, craters: 26, vegetation: 0, ice: 4, lava: 0, water: false };
      default:
        return { type: 'default', amp: 8, freq: 0.012, octaves: 4, craters: 6, vegetation: 8, ice: 0, lava: 0, water: true, seaLevel: -6 };
    }
  }

  _smoothstep(e0, e1, x) {
    const t = Math.max(0, Math.min(1, (x - e0) / Math.max(1e-6, e1 - e0)));
    return t * t * (3 - 2 * t);
  }

  // Flatten factor (0 at spawn → 1 far away) so arrival is always playable.
  _spawnFlatFactor(x, z) {
    const sx = x - 6;
    const sz = z + 15;
    const spawnDist = Math.sqrt(sx * sx + sz * sz);
    const flatRadius = 14;
    const flatFalloff = 30;
    if (spawnDist < flatRadius) return 0;
    if (spawnDist < flatRadius + flatFalloff) {
      const t = (spawnDist - flatRadius) / flatFalloff;
      return t * t * (3 - 2 * t);
    }
    return 1;
  }

  // Per-biome landform availability — multipliers for
  // [basin, plains, hills, mountains, canyon, mesa]. Lets each body type
  // favour a distinct mix of terrain regions.
  _biomeLandformMix(type) {
    switch (type) {
      case 'terrestrial': return [1.0, 1.0, 1.0, 1.0, 0.9, 0.8];
      case 'ocean':       return [1.35, 0.9, 0.6, 0.4, 0.45, 0.3];
      case 'ice':         return [0.9, 0.8, 0.95, 1.05, 1.15, 0.6];
      case 'lava':        return [0.8, 0.7, 0.8, 1.35, 1.05, 0.5];
      case 'moon':        return [0.6, 1.1, 0.8, 0.55, 0.4, 0.5];
      case 'dwarf':       return [0.7, 1.0, 0.85, 0.65, 0.55, 0.6];
      default:            return [0.9, 1.0, 0.9, 0.95, 0.7, 0.6];
    }
  }

  // Regional landform blend — partitions the surface into large regions that
  // each resolve to a recognisable landform (basin, plains, rolling hills,
  // mountains, incised canyons, stepped mesas) and blends smoothly between
  // neighbouring regions. This is what makes a single body feel varied as the
  // player walks across it, instead of one homogeneous noise signature.
  _landformHeight(x, z) {
    const { amp, freq, type } = this._biome;
    const seed = this._terrainSeed;
    const A = Math.max(amp, 1);
    const clamp01 = (v) => Math.max(0, Math.min(1, v));

    // Region selector — regions span ~200m so a short walk crosses several
    // distinct landforms (mountains → plains → canyon → hills, etc.), lightly
    // warped so boundaries are organic rather than blobby.
    const regFreq = freq * 0.45;
    const rw = 70 * (this._valueNoise(x * regFreq * 1.7 + 5.1, z * regFreq * 1.7 - 2.3, seed + 8800) - 0.5);
    const R = clamp01(this._valueNoise((x + rw) * regFreq + 31.4, (z - rw) * regFreq - 7.2, seed + 8801));

    // Archetype height generators — each region sits at a distinct base
    // elevation so mountains/mesas clearly tower over plains and basins.
    const basin = -(amp * 0.75 + Math.abs(this._fbm(x, z, seed + 211, 4, freq * 0.6, amp * 0.5)));
    const plains = this._fbm(x, z, seed + 311, 3, freq * 0.9, amp * 0.07);
    const hills = this._fbm(x, z, seed + 411, 4, freq * 1.0, amp * 0.55) + amp * 0.18;
    const mRidge = Math.max(0, this._ridgedNoise(x, z, seed + 511, 5, freq * 1.15, A)) / A;
    const mountains = amp * 1.1 + Math.pow(mRidge, 1.1) * amp * 2.7;
    const rn = clamp01((this._ridgedNoise(x, z, seed + 611, 4, freq * 1.0, 1) + 1) * 0.5);
    const cut = this._smoothstep(0.45, 0.95, rn);
    const canyon = amp * 0.85 - cut * amp * 3.1;
    const mraw = this._fbm(x, z, seed + 711, 3, freq * 0.7, 1);
    const stepN = 5;
    const mesa = amp * 0.4 + Math.round(clamp01(mraw * 0.5 + 0.5) * stepN) / stepN * amp * 1.6;

    const archetypes = [basin, plains, hills, mountains, canyon, mesa];
    const centers = [0.08, 0.27, 0.45, 0.63, 0.80, 0.95];
    const mult = this._biomeLandformMix(type);

    // Triangular blend across the 6 archetype centers → smooth transitions.
    const width = 0.16;
    let wsum = 0;
    const w = new Array(6);
    for (let i = 0; i < 6; i++) {
      w[i] = Math.max(0, 1 - Math.abs(R - centers[i]) / width);
      wsum += w[i];
    }
    if (wsum <= 0) { w[1] = 1; wsum = 1; }

    let h = 0;
    for (let i = 0; i < 6; i++) {
      h += (w[i] / wsum) * mult[i] * archetypes[i];
    }
    return h;
  }

  _terrainHeight(x, z) {
    if (!this._biome) return 0;
    const { amp, freq, type } = this._biome;
    const seed = this._terrainSeed;

    // Gas giants keep their faint banded cloud-floor (no solid landforms).
    if (type === 'gas') {
      let gh = Math.sin(z * 0.005 + this._valueNoise(x * 0.002, z * 0.002, seed) * 4) * amp * 0.5;
      gh += this._fbm(x, z, seed + 555, 2, freq * 4.2, amp * 0.14);
      return gh * this._spawnFlatFactor(x, z);
    }

    // 1) Domain warp for organic, non-grid-aligned shapes.
    const warpStrength = 70;
    const warpX = warpStrength * this._fbm(x, z, seed + 9101, 2, freq * 0.6, 1) / Math.max(amp, 1);
    const warpZ = warpStrength * this._fbm(x + 173, z - 89, seed + 9102, 2, freq * 0.6, 1) / Math.max(amp, 1);
    const wx = x + warpX;
    const wz = z + warpZ;

    // 2) Regional landform blend (mountains / hills / plains / canyon / mesa / basin).
    let h = this._landformHeight(wx, wz);

    // 3) Gentle continental tilt so whole regions sit at different base elevations.
    const macro = this._fbm(x, z, seed + 7777, 3, freq * 0.18, amp * 1.6);
    h += macro * 0.3;

    // 4) High-frequency detail — rocks & micro-bumps for close-up interest.
    h += this._fbm(x, z, seed + 555, 2, freq * 4.2, amp * 0.14);

    // 5) Crater depressions (Moon/Dwarf-heavy; layered on top of landforms).
    if (this._craters) {
      for (let i = 0; i < this._craters.length; i++) {
        const c = this._craters[i];
        const dx = x - c.x;
        const dz = z - c.z;
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d < c.radius) {
          const t = d / c.radius;
          let crater = -c.depth * (1 - t * t);
          if (t > 0.82) {
            crater += c.depth * 0.45 * (1 - (t - 0.82) / 0.18);
          }
          h += crater;
        }
      }
    }

    return h * this._spawnFlatFactor(x, z);
  }

  // ---------- atmosphere & gravity ----------

  _deriveGravity() {
    // Earth-relative gravity proxy using planetary radius.
    // Earth radius ≈ 6371 km, gravity 9.8 m/s².
    const earthRadius = 6371;
    const r = this.body?.radius_km || earthRadius * 0.5;
    let g = 9.8 * (r / earthRadius);
    if (this.body?.kind === 'moon') g *= 0.4;       // moons feel light
    if (this.body?.type === 'Gas Giant') g *= 1.4;  // crushing
    if (this.body?.type === 'Ice Giant') g *= 1.2;
    if (this.body?.type === 'Lava') g *= 1.05;
    if (this.body?.type === 'Dwarf') g *= 0.5;
    return Math.max(1.6, Math.min(22, g));
  }

  _buildAtmosphereTint(palette) {
    const baseSky = hexToColor3(palette.sky);
    const baseFog = hexToColor3(palette.fog);
    let skyTint = baseSky;
    let fogColor = baseFog;
    let fogDensity = 0.011;
    let hemiIntensity = 0.6;
    let starVisibility = 1; // how many backdrop stars are visible

    // Reference bright daytime sky (clear blue) — any body WITH an atmosphere
    // should read as a light sky at noon rather than the dark biome/fog tint.
    const dayBlue = new Color3(0.45, 0.62, 0.86);
    const lum = (c) => 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
    const lerp = (c, target, amt) => new Color3(
      c.r + (target.r - c.r) * amt,
      c.g + (target.g - c.g) * amt,
      c.b + (target.b - c.b) * amt,
    );

    // Normalise the (possibly descriptive) atmosphere string into a category.
    // Generator values: None / Thin / Moderate / Dense / Toxic.
    const a = this.atmosphere;
    let cat = 'normal';
    if (a === '' || a === 'none' || a === 'unknown') cat = 'none';
    else if (a.includes('toxic')) cat = 'toxic';
    else if (a.includes('thin')) cat = 'thin';
    else if (a.includes('dense')) cat = 'dense';
    else cat = 'normal'; // moderate / breathable / etc.

    switch (cat) {
      case 'none':
        // Airless body — barely-there pale sky so the starfield + sun read
        // clearly. Deliberately not pure black so the terrain silhouette
        // stays visible against the sky.
        skyTint = new Color3(0.040, 0.045, 0.075);
        fogColor = new Color3(0.005, 0.005, 0.015);
        fogDensity = 0.0015;
        hemiIntensity = 0.30;
        starVisibility = 1.6;
        break;
      case 'thin':
        // Thin but present air → a soft, slightly pale daytime sky.
        skyTint = lerp(baseSky, dayBlue, 0.5);
        fogColor = baseFog.scale(0.75);
        fogDensity = 0.005;
        hemiIntensity = 0.65;
        starVisibility = 0.5;
        break;
      case 'dense':
        // Thick air scatters strongly → bright, hazy sky.
        skyTint = lerp(baseSky, new Color3(0.80, 0.84, 0.90), 0.6);
        fogColor = lerp(baseFog, new Color3(0.72, 0.76, 0.82), 0.45);
        fogDensity = 0.02;
        hemiIntensity = 0.9;
        starVisibility = 0.0;
        break;
      case 'toxic':
        // Bright, sickly-green hazy daylight.
        skyTint = new Color3(0.46, 0.60, 0.26);
        fogColor = new Color3(0.22, 0.32, 0.12);
        fogDensity = 0.018;
        hemiIntensity = 0.7;
        starVisibility = 0.1;
        break;
      default:
        // Normal / moderate / breathable atmosphere → bright blue daytime
        // sky, tinted slightly by the biome hue so each world keeps identity.
        skyTint = lerp(baseSky, dayBlue, 0.62);
        fogColor = lerp(baseFog, dayBlue.scale(0.85), 0.35);
        fogDensity = 0.01;
        hemiIntensity = 0.78;
        starVisibility = 0.25;
        break;
    }

    if (cat !== 'none') {
      // Guarantee a genuinely LIGHT daytime sky for any body with air, even
      // when the biome palette (e.g. Dwarf/Default) is very dark.
      if (lum(skyTint) < 0.40) {
        skyTint = lerp(skyTint, dayBlue, 0.6);
      }
      skyTint = new Color3(
        Math.max(skyTint.r, 0.24),
        Math.max(skyTint.g, 0.32),
        Math.max(skyTint.b, 0.44),
      );
    } else {
      // Airless dark-sky floor — subtle lift so it isn't perfectly black.
      const minBrightness = 0.06;
      if (skyTint.r < minBrightness && skyTint.g < minBrightness && skyTint.b < minBrightness) {
        skyTint = new Color3(
          Math.max(skyTint.r, minBrightness),
          Math.max(skyTint.g, minBrightness),
          Math.max(skyTint.b, minBrightness + 0.02),
        );
      }
    }
    this.skyTint = skyTint;
    this.fogColor = fogColor;
    this.fogDensity = fogDensity;
    this.hemiIntensity = hemiIntensity;
    this.starVisibility = starVisibility;
  }

  // ---------- setup helpers ----------

  _setupRenderingPipeline() {
    // Babylon's DefaultRenderingPipeline bundles tone mapping, bloom, FXAA,
    // grain, sharpen, chromatic aberration and image grading. We attach it to
    // every camera the player might be on so the look stays consistent
    // across foot/ship/flight modes.
    const cams = [];
    if (this.fpsCamera) cams.push(this.fpsCamera);
    if (this.tpsCamera) cams.push(this.tpsCamera);
    if (this.flightCamera) cams.push(this.flightCamera);
    if (cams.length === 0) return;

    const pipe = new DefaultRenderingPipeline('fxPipeline', true, this.scene, cams);

    // --- Image processing: ACES tone mapping with neutral defaults ---
    // We deliberately keep exposure/contrast tame so the sky and atmospheric
    // tints stay readable. Earlier values (exposure 1.05, contrast 1.10 +
    // color curves) compounded with the bright sun corona's bloom to make
    // the sky read as a uniform pale wash on every planet.
    pipe.imageProcessingEnabled = true;
    if (pipe.imageProcessing) {
      pipe.imageProcessing.toneMappingEnabled = true;
      pipe.imageProcessing.toneMappingType = 1; // ACES
      pipe.imageProcessing.exposure = 1.05;
      pipe.imageProcessing.contrast = 1.08;
      // Vignette darkens only the screen corners — keeps the centre crisp
      // without affecting the sky readback.
      pipe.imageProcessing.vignetteEnabled = true;
      pipe.imageProcessing.vignetteWeight = 1.4;
      pipe.imageProcessing.vignetteStretch = 0.5;
      pipe.imageProcessing.vignetteColor = new Color4(0.02, 0.02, 0.05, 0);
      // Color curves disabled: the previous "filmic" hue shifts pushed
      // shadows cool + highlights warm, which on bright skies produced a
      // muddy near-white wash. Re-enable as an optional Cinematic toggle
      // if needed.
      // Gentle cinematic grade — a touch more saturation with a subtle
      // cool-shadow / warm-highlight split. Kept modest so bright skies don't
      // wash out (an earlier aggressive curve caused that).
      const curves = new ColorCurves();
      curves.globalSaturation = 16;
      curves.shadowsSaturation = 12;
      curves.shadowsHue = 215;
      curves.shadowsDensity = 8;
      curves.highlightsHue = 40;
      curves.highlightsDensity = 6;
      pipe.imageProcessing.colorCurves = curves;
      pipe.imageProcessing.colorCurvesEnabled = true;
    }

    // --- Bloom: only the very brightest pixels (sun core) bloom. ---
    pipe.bloomEnabled = true;
    pipe.bloomThreshold = 0.82;
    pipe.bloomWeight = 0.34;
    pipe.bloomKernel = 48;
    pipe.bloomScale = 0.6;

    // --- FXAA: cheap full-frame antialiasing → kills jaggies on terrain ---
    pipe.fxaaEnabled = true;
    // MSAA disabled — FXAA alone is enough and MSAA 4x roughly doubles
    // fragment cost across the entire frame. Re-enable if user complains
    // about pixel-thin jagged edges.
    pipe.samples = 1;

    // --- Sharpen: a tiny pass to recover crispness after FXAA ---
    pipe.sharpenEnabled = true;
    if (pipe.sharpen) {
      pipe.sharpen.edgeAmount = 0.15;
      pipe.sharpen.colorAmount = 1.0;
    }

    // Chromatic aberration + film grain — visually nice but each is its own
    // post-pass and they're the first things to cut when chasing FPS. Off by
    // default; can be re-enabled in a future "Cinematic Quality" toggle.
    // Subtle lens chromatic aberration that grows toward the frame edges.
    pipe.chromaticAberrationEnabled = true;
    if (pipe.chromaticAberration) {
      pipe.chromaticAberration.aberrationAmount = 7;
      pipe.chromaticAberration.radialIntensity = 0.7;
    }
    // Fine animated film grain breaks up banding in skies and gradients.
    pipe.grainEnabled = true;
    if (pipe.grain) {
      pipe.grain.intensity = 6;
      pipe.grain.animated = true;
    }

    // Glow layer already exists for emissive bloom; the pipeline bloom only
    // affects pixels above its threshold so they're complementary. We dial
    // the glow layer a notch down since the pipeline adds its own bloom.
    if (this.glow) this.glow.intensity = 0.55;

    this._fxPipeline = pipe;
    // Apply the current graphics-quality tier (toggles CA/grain/curves/bloom).
    this._applyGraphicsQuality();
  }

  /** Enable/disable the expensive post-processing passes per quality tier.
   *  Cheap to call live (just flips flags on the existing pipeline). */
  _applyGraphicsQuality() {
    const q = this.graphicsQuality || 'cinematic';
    const pipe = this._fxPipeline;
    if (pipe) {
      if (q === 'low') {
        pipe.chromaticAberrationEnabled = false;
        pipe.grainEnabled = false;
        if (pipe.imageProcessing) pipe.imageProcessing.colorCurvesEnabled = false;
        pipe.bloomThreshold = 0.92;
        pipe.bloomWeight = 0.16;
      } else if (q === 'medium') {
        pipe.chromaticAberrationEnabled = false;
        pipe.grainEnabled = false;
        if (pipe.imageProcessing) pipe.imageProcessing.colorCurvesEnabled = true;
        pipe.bloomThreshold = 0.85;
        pipe.bloomWeight = 0.28;
      } else {
        pipe.chromaticAberrationEnabled = true;
        pipe.grainEnabled = true;
        if (pipe.imageProcessing) pipe.imageProcessing.colorCurvesEnabled = true;
        pipe.bloomThreshold = 0.82;
        pipe.bloomWeight = 0.34;
      }
    }
    if (this.glow) this.glow.intensity = q === 'low' ? 0.3 : (q === 'medium' ? 0.45 : 0.55);
  }

  /** Switch graphics-quality tier at runtime. Post-FX update instantly; shadow
   *  resolution, vegetation density and water ripples refresh on the next
   *  planet load (Reload Engine). */
  setGraphicsQuality(level) {
    const q = (level === 'low' || level === 'medium' || level === 'cinematic') ? level : 'cinematic';
    if (q === this.graphicsQuality) return;
    this.graphicsQuality = q;
    this._applyGraphicsQuality();
  }

  _setupLighting(palette) {
    const sky = hexToColor3(palette.sky);
    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), this.scene);
    hemi.intensity = this.hemiIntensity || 0.6;
    hemi.groundColor = hexToColor3(palette.ground).scale(0.5);
    hemi.diffuse = this.skyTint || sky;
    this.hemi = hemi;

    // Directional sunlight — direction is finalized once the sun is placed.
    const sun = new DirectionalLight('sun', new Vector3(-0.5, -1, -0.3), this.scene);
    sun.intensity = this.atmosphere === 'none' ? 1.2 : 0.95;
    sun.diffuse = new Color3(1, 0.97, 0.9);
    // Position the light source far enough away that its orthographic shadow
    // frustum has headroom. We update this each tick to follow the player so
    // the shadow map always covers the visible area.
    sun.position = new Vector3(0, 200, 0);
    sun.shadowEnabled = true;
    sun.shadowMinZ = 1;
    sun.shadowMaxZ = 600;
    sun.autoUpdateExtends = true;
    sun.autoCalcShadowZBounds = false;
    sun.shadowOrthoScale = 0.4;
    this.sun = sun;

    // Shadow generator — PCF blur gives soft, "directional sun"-style shadows.
    // Resolution scales with the graphics-quality tier (low 512 → cinematic 1024).
    const shadowRes = this.graphicsQuality === 'low' ? 512 : (this.graphicsQuality === 'cinematic' ? 1024 : 768);
    const shadowGen = new ShadowGenerator(shadowRes, sun);
    shadowGen.usePercentageCloserFiltering = true;
    shadowGen.filteringQuality = ShadowGenerator.QUALITY_LOW;
    shadowGen.bias = 0.0009;
    shadowGen.normalBias = 0.02;
    shadowGen.darkness = 0.42;
    shadowGen.transparencyShadow = false;
    this.shadowGen = shadowGen;

    // Bloom/glow layer — makes the sun, halo, nav lights and other emissive
    // surfaces feel like they're actually emitting light into the camera.
    const glow = new GlowLayer('sceneGlow', this.scene, { mainTextureSamples: 2 });
    glow.intensity = 0.55;
    this.glow = glow;

    // Player flashlight — a head-mounted spotlight that follows the active
    // camera. Starts OFF; toggled with the "L" key / HUD button. Updated each
    // frame in the render loop to track the camera position + view direction.
    const torch = new SpotLight(
      'playerFlashlight',
      new Vector3(0, 2, 0),
      new Vector3(0, 0, 1),
      Math.PI / 3.0,   // ~60° cone
      2,               // soft, broad falloff
      this.scene,
    );
    torch.diffuse = new Color3(1, 0.96, 0.82);
    torch.specular = new Color3(0.5, 0.5, 0.45);
    torch.intensity = 18.0;   // bright enough to read even in daylight
    torch.range = 80;
    torch.setEnabled(false);
    this.flashlight = torch;
    this._flashlightOn = false;

    // A bright warm "pool" projected on the ground where the beam points, so
    // the flashlight is obviously ON in any lighting (the spotlight alone can
    // be hard to notice against bright daytime terrain).
    const pool = MeshBuilder.CreateDisc('flashlightPool', { radius: 5.5, tessellation: 36 }, this.scene);
    pool.rotation.x = Math.PI / 2;
    const poolMat = new StandardMaterial('flashlightPoolMat', this.scene);
    poolMat.emissiveColor = new Color3(1, 0.95, 0.78);
    poolMat.diffuseColor = new Color3(0, 0, 0);
    poolMat.specularColor = new Color3(0, 0, 0);
    poolMat.disableLighting = true;
    poolMat.disableDepthWrite = true;   // draw over terrain, no z-fighting
    poolMat.alpha = 0.28;
    poolMat.backFaceCulling = false;
    pool.material = poolMat;
    pool.isPickable = false;
    pool.setEnabled(false);
    this._flashlightPool = pool;

    // Phase 10 — day/night cycle state. We cycle "sun pitch" between day
    // (high) and night (below horizon) every `period` seconds, dimming the
    // hemispheric + directional lights and tinting the skybox accordingly.
    this._dayNight = {
      t: 300,                      // start at noon (period * 0.5) for nicer first frame
      period: 600,                 // full day-night cycle in seconds (10 minutes)
      hemiBase: hemi.intensity,    // captured base for night-time scaling
      hemiBaseColor: hemi.diffuse.clone(),
      hemiGroundBase: hemi.groundColor.clone(),
      sunBase: sun.intensity,
      sunBaseColor: sun.diffuse.clone(),
      // Skybox tint baseline captured later in _setupSkybox.
      skyBase: null,
    };
  }

  _setupSkybox(palette) {
    // skyAnchor follows the camera each tick so the sky never "moves away"
    // when the player roams far across the streamed terrain.
    this.skyAnchor = new TransformNode('skyAnchor', this.scene);
    // _skyDome is a child of skyAnchor that we rotate over the course of the
    // day/night cycle so the skybox + starfield appear to track the sun.
    // The sun mesh + halo stay on skyAnchor (NOT _skyDome) so we can position
    // them explicitly to match the directional light's direction.
    this._skyDome = new TransformNode('skyDome', this.scene);
    this._skyDome.parent = this.skyAnchor;

    const skyColor = this.skyTint || hexToColor3(palette.sky);
    // Build a GRAYSCALE horizon→zenith gradient + cloud banding. The
    // material's emissiveColor tints the whole texture so the biome's sky
    // colour reliably shows through — there's no way for the texture
    // itself to render as white because it's now only a brightness mask.
    const skyTex = this._buildSkyGradientTexture(new Color3(1, 1, 1));

    const skybox = MeshBuilder.CreateBox('skyBox', { size: 4000 }, this.scene);
    const mat = new StandardMaterial('skyMat', this.scene);
    mat.backFaceCulling = false;
    mat.disableLighting = true;
    mat.fogEnabled = false;
    // DIAGNOSTIC: temporarily disable the gradient texture so only the
    // emissiveColor renders. If the sky now reads as a solid biome tint
    // instead of white, the gradient texture is the culprit (likely a
    // colour-space / linear-sRGB clash in DynamicTexture) and we can swap
    // it for a generated procedural texture or a brightness LUT.
    mat.emissiveTexture = null;
    // Tint the skybox with the biome sky color directly. With no texture
    // the entire dome renders as this solid colour.
    mat.emissiveColor = skyColor.clone();
    mat.diffuseColor = new Color3(0, 0, 0);
    mat.specularColor = new Color3(0, 0, 0);
    skybox.material = mat;
    // Parent to _skyDome so it rotates with the celestial sphere.
    skybox.parent = this._skyDome;
    this.skybox = skybox; // tracked so we can disable it in space mode
    // Keep the texture object alive so we can hot-swap it back in once we
    // confirm the diagnostic. (Reference held on `this` to prevent GC.)
    this._skyTexUnused = skyTex;

    // Skybox should never participate in the bloom/glow pass.
    if (this.glow) {
      try { this.glow.addExcludedMesh(skybox); } catch (_e) { /* noop */ }
    }

    // Phase 10 — capture sky baseline for day/night blending. Day/night now
    // dims the biome sky color (not white), so dawn/dusk warmth still
    // reads correctly while keeping the biome hue at noon.
    if (this._dayNight) {
      this._dayNight.skyBase = skyColor.clone();
    }

    // Backdrop stars: density driven by atmosphere thickness.
    // Dense atmospheres effectively occlude the starfield.
    const starCount = Math.round(220 * (this.starVisibility ?? 1));
    if (starCount > 0) {
      const starParent = new TransformNode('stars', this.scene);
      // Parent stars to _skyDome so they rotate together with the sun.
      starParent.parent = this._skyDome;
      for (let i = 0; i < starCount; i++) {
        const s = MeshBuilder.CreateSphere(`star${i}`, { diameter: 0.5 + Math.random() * 0.8, segments: 4 }, this.scene);
        const r = 1500;
        const theta = Math.random() * Math.PI * 2;
        // Distribute stars across the full celestial sphere so when the dome
        // rotates we always see stars in the night-side hemisphere.
        const u = 2 * Math.random() - 1;
        const sxy = Math.sqrt(Math.max(0, 1 - u * u));
        s.position = new Vector3(
          r * sxy * Math.cos(theta),
          r * u,
          r * sxy * Math.sin(theta)
        );
        const sm = new StandardMaterial(`starMat${i}`, this.scene);
        sm.disableLighting = true;
        sm.fogEnabled = false; // never fog out the starfield
        sm.emissiveColor = new Color3(1, 1, 0.9);
        s.material = sm;
        s.isPickable = false;
        s.parent = starParent;
        if (this.glow) {
          try { this.glow.addExcludedMesh(s); } catch (_e) { /* noop */ }
        }
      }
    }
  }

  // Procedural horizon→zenith sky gradient + soft cloud bands. We bake into a
  // DynamicTexture so the skybox material has visible features (otherwise the
  // box rotation would be imperceptible because the surface is uniform color).
  _buildSkyGradientTexture(skyColor) {
    const W = 512, H = 512;
    const tex = new DynamicTexture('skyGradTex', { width: W, height: H }, this.scene, false);
    const ctx = tex.getContext();
    const toRgb = (c, k = 1) => `rgb(${Math.round(Math.max(0, Math.min(1, c.r * k)) * 255)},${Math.round(Math.max(0, Math.min(1, c.g * k)) * 255)},${Math.round(Math.max(0, Math.min(1, c.b * k)) * 255)})`;
    // Vertical gradient: deep zenith → bright horizon. Range is intentionally
    // compressed (0.55..0.95) so neither extreme washes to white — the user's
    // planet sky should always read as its biome tint, not a pale glow.
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0.00, toRgb(skyColor, 0.55));   // zenith (slightly darker)
    grad.addColorStop(0.45, toRgb(skyColor, 0.78));
    grad.addColorStop(0.70, toRgb(skyColor, 0.92));
    grad.addColorStop(0.90, toRgb(skyColor, 0.95));   // horizon glow
    grad.addColorStop(1.00, toRgb(skyColor, 0.72));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
    // Sparse cloud bands so rotation is unmistakable. Opacity is gentle so
    // they read as wispy banding rather than blanket whitewash.
    const bands = this.atmosphere === 'none' ? 4 : 8;
    for (let i = 0; i < bands; i++) {
      const y = Math.random() * H * 0.85 + H * 0.05;
      const h = 6 + Math.random() * 18;
      const a = (this.atmosphere === 'none' ? 0.05 : 0.10) + Math.random() * 0.08;
      ctx.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`;
      const x = Math.random() * W;
      const w = 80 + Math.random() * 260;
      ctx.beginPath();
      ctx.ellipse(x, y, w, h, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    tex.update();
    tex.wrapU = Texture.WRAP_ADDRESSMODE;
    tex.wrapV = Texture.WRAP_ADDRESSMODE;
    return tex;
  }

  // ---------------------------------------------------------------------------
  // Procedural ground texture pipeline.
  //
  // Each biome gets a unique 1024px diffuse texture painted from layered noise
  // + biome-specific accents (lava cracks, ice fractures, dust, vegetation
  // patches, etc.) so the surface reads as real terrain instead of a flat
  // color. We also bake a low-cost grayscale "bump" texture from the same
  // noise so Babylon's StandardMaterial can shade micro-relief without
  // needing per-vertex displacement.
  // ---------------------------------------------------------------------------
  _buildGroundTextures(biome, baseColor, palette) {
    const W = 1024, H = 1024;
    const diffuseTex = new DynamicTexture('groundDiffuse', { width: W, height: H }, this.scene, true);
    const dctx = diffuseTex.getContext();
    // Seedable PRNG for stable per-biome variation (so a moon always looks
    // the same when you revisit). Tied to the planet's terrain seed.
    const seed = (this._terrainSeed >>> 0) || 1;
    const rand = this._mulberry32(seed);
    const lerp = (a, b, t) => a + (b - a) * t;
    const clamp01 = (v) => Math.max(0, Math.min(1, v));
    const mix = (c1, c2, t) => ({
      r: lerp(c1.r, c2.r, t),
      g: lerp(c1.g, c2.g, t),
      b: lerp(c1.b, c2.b, t),
    });
    const toRgb = (c) => `rgb(${Math.round(clamp01(c.r) * 255)},${Math.round(clamp01(c.g) * 255)},${Math.round(clamp01(c.b) * 255)})`;

    // Resolve biome palette: a "soil" baseline, a darker accent, and a brighter
    // highlight. Tuned per biome for recognisable looks.
    const palettes = this._biomePalette(biome.type, baseColor, palette, rand);
    const { soil, dark, light, accent } = palettes;

    // --- 1) Mottled base layer (large-scale noise → smooth color zones) ---
    const tile = 64;
    const tiles = W / tile;
    for (let ty = 0; ty < tiles; ty++) {
      for (let tx = 0; tx < tiles; tx++) {
        // Blend factor mixes soil↔dark↔light based on positional noise.
        const n = this._fbm(tx, ty, seed, 4, 0.18, 1);
        const t = clamp01(0.5 + n * 0.6);
        const col = t < 0.5 ? mix(dark, soil, t * 2) : mix(soil, light, (t - 0.5) * 2);
        dctx.fillStyle = toRgb(col);
        dctx.fillRect(tx * tile, ty * tile, tile, tile);
      }
    }

    // --- 2) Fine-grain speckle for tactile detail (pebbles/grains) ---
    const speckleCount = biome.type === 'gas' ? 800 : 3200;
    for (let i = 0; i < speckleCount; i++) {
      const x = rand() * W;
      const y = rand() * H;
      const r = 0.6 + rand() * 1.6;
      const t = rand();
      const col = t < 0.5 ? mix(soil, dark, 0.6 + rand() * 0.3) : mix(soil, light, 0.4 + rand() * 0.4);
      dctx.fillStyle = toRgb(col);
      dctx.beginPath();
      dctx.arc(x, y, r, 0, Math.PI * 2);
      dctx.fill();
    }

    // --- 3) Biome-specific accents (lava cracks / ice fractures / etc.) ---
    this._paintBiomeAccents(dctx, null, W, H, biome.type, accent, rand);

    diffuseTex.update();
    // Tile the texture across the ground so detail stays visible up close.
    // Ground is 800u; repeating ~16x gives ~50u per texture tile.
    const repeat = biome.type === 'gas' ? 6 : 16;
    diffuseTex.uScale = repeat;
    diffuseTex.vScale = repeat;
    diffuseTex.wrapU = Texture.WRAP_ADDRESSMODE;
    diffuseTex.wrapV = Texture.WRAP_ADDRESSMODE;
    return { diffuseTex };
  }

  // Procedural tangent-space normal map for the ground material.
  //
  // For each output pixel we sample the same FBM heightfield used by the
  // diffuse texture at the four cardinal neighbours, compute the X/Y
  // gradient, and pack the resulting normal vector into RGB. Because B
  // (the up-axis component) is always ~1, the normals point "out of the
  // surface" and the directional sun light shades them correctly — fixes
  // the dark-ground bug that earlier procedural bump-maps caused.
  _buildGroundNormalMap(biome) {
    const W = 512, H = 512;
    const tex = new DynamicTexture('groundNormal', { width: W, height: H }, this.scene, true);
    const ctx = tex.getContext();
    const imgData = ctx.createImageData(W, H);
    const data = imgData.data;
    const seed = (this._terrainSeed >>> 0) || 1;
    // Match the diffuse texture's noise scale so micro-bumps line up with
    // the visible mottling — the eye reads them as "the same surface".
    const noiseScale = 0.20;
    const heightAt = (x, y) => this._fbm(x * noiseScale, y * noiseScale, seed, 4, 1, 1)
      + 0.35 * this._fbm(x * noiseScale * 3.1, y * noiseScale * 3.1, seed + 137, 2, 1, 1);
    // Per-biome bump strength — ice has sharper micro-fractures, gas-giant
    // floors are smoother than dirt, etc. Larger values = more pronounced
    // shading without making the geometry deform.
    const strength = biome.type === 'ice' ? 2.4
      : biome.type === 'lava' ? 2.6
      : biome.type === 'gas' ? 0.6
      : 1.8;
    let i = 0;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const hl = heightAt(x - 1, y);
        const hr = heightAt(x + 1, y);
        const hu = heightAt(x, y - 1);
        const hd = heightAt(x, y + 1);
        const dx = (hr - hl) * strength;
        const dy = (hd - hu) * strength;
        // Tangent-space normal — bumps lean AGAINST the gradient.
        let nx = -dx;
        let ny = -dy;
        const len2 = nx * nx + ny * ny;
        // Clamp the XY magnitude so B stays positive; otherwise we'd encode
        // an upside-down normal which would shade the bump as a pit.
        if (len2 > 0.85) {
          const k = Math.sqrt(0.85 / len2);
          nx *= k;
          ny *= k;
        }
        const nz = Math.sqrt(Math.max(0.05, 1 - (nx * nx + ny * ny)));
        data[i++] = Math.round((nx * 0.5 + 0.5) * 255);
        data[i++] = Math.round((ny * 0.5 + 0.5) * 255);
        data[i++] = Math.round(nz * 255);
        data[i++] = 255;
      }
    }
    ctx.putImageData(imgData, 0, 0);
    tex.update();
    tex.wrapU = Texture.WRAP_ADDRESSMODE;
    tex.wrapV = Texture.WRAP_ADDRESSMODE;
    return tex;
  }

  // Per-biome accent painter — adds the visual element that identifies the
  // surface (cracks, fractures, vegetation patches, foam, regolith ejecta).
  _paintBiomeAccents(dctx, _bctx, W, H, biomeType, accent, rand) {
    const toRgb = (c, a = 1) => `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},${a.toFixed(3)})`;
    if (biomeType === 'lava') {
      // Glowing magma cracks: organic broken-line network.
      for (let i = 0; i < 18; i++) {
        const x0 = rand() * W;
        const y0 = rand() * H;
        let x = x0, y = y0;
        let ang = rand() * Math.PI * 2;
        const segs = 10 + Math.floor(rand() * 14);
        dctx.strokeStyle = toRgb(accent, 0.92);
        dctx.lineWidth = 2 + rand() * 4;
        dctx.lineCap = 'round';
        dctx.shadowColor = toRgb(accent, 1);
        dctx.shadowBlur = 12;
        dctx.beginPath();
        dctx.moveTo(x, y);
        for (let s = 0; s < segs; s++) {
          ang += (rand() - 0.5) * 0.9;
          x += Math.cos(ang) * (8 + rand() * 12);
          y += Math.sin(ang) * (8 + rand() * 12);
          dctx.lineTo(x, y);
        }
        dctx.stroke();
        dctx.shadowBlur = 0;
      }
    } else if (biomeType === 'ice') {
      // Crystalline fractures + scattered frosty highlights.
      dctx.strokeStyle = toRgb(accent, 0.55);
      dctx.lineWidth = 1.2;
      for (let i = 0; i < 60; i++) {
        const x0 = rand() * W;
        const y0 = rand() * H;
        const branches = 3 + Math.floor(rand() * 3);
        for (let b = 0; b < branches; b++) {
          const ang = (b / branches) * Math.PI * 2 + rand() * 0.3;
          const len = 14 + rand() * 30;
          dctx.beginPath();
          dctx.moveTo(x0, y0);
          dctx.lineTo(x0 + Math.cos(ang) * len, y0 + Math.sin(ang) * len);
          dctx.stroke();
        }
      }
      // Frost glints
      for (let i = 0; i < 220; i++) {
        const x = rand() * W;
        const y = rand() * H;
        dctx.fillStyle = `rgba(255,255,255,${(0.25 + rand() * 0.4).toFixed(3)})`;
        dctx.beginPath();
        dctx.arc(x, y, 0.8 + rand() * 1.4, 0, Math.PI * 2);
        dctx.fill();
      }
    } else if (biomeType === 'ocean') {
      // Foam ripples + lighter shoals.
      for (let i = 0; i < 120; i++) {
        const x = rand() * W;
        const y = rand() * H;
        const r = 18 + rand() * 60;
        const grad = dctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, 'rgba(255,255,255,0.20)');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        dctx.fillStyle = grad;
        dctx.fillRect(x - r, y - r, r * 2, r * 2);
      }
    } else if (biomeType === 'terrestrial') {
      // Grassland: lots of vegetation patches + scattered bare-soil pockets so
      // the ground beneath the blade thin-instances reads as living turf.
      for (let i = 0; i < 200; i++) {
        const x = rand() * W;
        const y = rand() * H;
        const r = 10 + rand() * 48;
        // Mix of vivid greens (most) and warm browns (a few) so dry patches
        // peek through the meadow.
        const isVeg = rand() > 0.18;
        const veg = isVeg
          ? { r: 0.14 + rand() * 0.18, g: 0.34 + rand() * 0.26, b: 0.10 + rand() * 0.10 }
          : { r: 0.42 + rand() * 0.18, g: 0.32 + rand() * 0.14, b: 0.18 + rand() * 0.10 };
        const grad = dctx.createRadialGradient(x, y, 0, x, y, r);
        const cr = Math.round(veg.r * 255), cg = Math.round(veg.g * 255), cb = Math.round(veg.b * 255);
        grad.addColorStop(0, `rgba(${cr},${cg},${cb},${(0.55 + rand() * 0.30).toFixed(3)})`);
        grad.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
        dctx.fillStyle = grad;
        dctx.fillRect(x - r, y - r, r * 2, r * 2);
      }
      // Tiny darker speckle = "shadow under grass clumps" — ties the ground
      // visually to the cross-quad grass blades sitting on top.
      for (let i = 0; i < 600; i++) {
        const x = rand() * W;
        const y = rand() * H;
        dctx.fillStyle = `rgba(10,30,8,${(0.18 + rand() * 0.18).toFixed(3)})`;
        dctx.beginPath();
        dctx.arc(x, y, 0.7 + rand() * 1.4, 0, Math.PI * 2);
        dctx.fill();
      }
    } else if (biomeType === 'moon' || biomeType === 'dwarf') {
      // Regolith dust + small crater splats (ejecta).
      for (let i = 0; i < 70; i++) {
        const x = rand() * W;
        const y = rand() * H;
        const r = 6 + rand() * 22;
        const dark = `rgba(20,20,24,${(0.18 + rand() * 0.18).toFixed(3)})`;
        const grad = dctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, dark);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        dctx.fillStyle = grad;
        dctx.fillRect(x - r, y - r, r * 2, r * 2);
      }
      // Bright ejecta rays around a few impact points
      for (let i = 0; i < 8; i++) {
        const x = rand() * W;
        const y = rand() * H;
        const rays = 12 + Math.floor(rand() * 8);
        for (let a = 0; a < rays; a++) {
          const ang = (a / rays) * Math.PI * 2 + rand() * 0.2;
          const len = 30 + rand() * 80;
          dctx.strokeStyle = `rgba(220,220,222,${(0.10 + rand() * 0.12).toFixed(3)})`;
          dctx.lineWidth = 1 + rand() * 2;
          dctx.beginPath();
          dctx.moveTo(x, y);
          dctx.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
          dctx.stroke();
        }
      }
    }
    // Gas Giant / default: no extra accents — the mottled base reads well.
  }

  _biomePalette(biomeType, baseColor, palette, rand) {
    // Builds soil/dark/light/accent tints from the body's base ground color.
    // The accent is the "active material" — magma, ice, foam, vegetation, dust.
    const baseDark = { r: baseColor.r * 0.55, g: baseColor.g * 0.55, b: baseColor.b * 0.55 };
    const baseLight = { r: Math.min(1, baseColor.r * 1.35), g: Math.min(1, baseColor.g * 1.35), b: Math.min(1, baseColor.b * 1.30) };
    const soil = { r: baseColor.r, g: baseColor.g, b: baseColor.b };
    switch (biomeType) {
      case 'lava':
        return {
          soil: { r: 0.18, g: 0.10, b: 0.08 },
          dark: { r: 0.06, g: 0.04, b: 0.03 },
          light: { r: 0.28, g: 0.15, b: 0.10 },
          accent: { r: 1.0, g: 0.45, b: 0.12 },
        };
      case 'ocean':
        return {
          soil: { r: 0.06, g: 0.18, b: 0.32 },
          dark: { r: 0.02, g: 0.10, b: 0.20 },
          light: { r: 0.10, g: 0.28, b: 0.45 },
          accent: { r: 0.85, g: 0.95, b: 1.0 },
        };
      case 'ice':
        return {
          soil: { r: 0.70, g: 0.82, b: 0.93 },
          dark: { r: 0.45, g: 0.60, b: 0.78 },
          light: { r: 0.92, g: 0.96, b: 1.0 },
          accent: { r: 0.55, g: 0.78, b: 0.95 },
        };
      case 'terrestrial':
        return {
          // More saturated grassland greens and rich soil tones.
          soil: { r: 0.32, g: 0.46, b: 0.20 },        // medium meadow green
          dark: { r: 0.18, g: 0.28, b: 0.12 },        // shadowed grass / damp soil
          light: { r: 0.62, g: 0.65, b: 0.36 },       // sunlit grass / dry patch
          accent: { r: 0.20, g: 0.52, b: 0.16 },      // lush green clumps
        };
      case 'moon':
      case 'dwarf':
        return {
          soil: { r: 0.62, g: 0.62, b: 0.65 },
          dark: { r: 0.36, g: 0.36, b: 0.38 },
          light: { r: 0.84, g: 0.84, b: 0.86 },
          accent: { r: 0.92, g: 0.92, b: 0.94 },
        };
      case 'gas':
        // Rarely walked on, but provide a smooth banded look.
        return {
          soil: { r: baseColor.r * 0.7, g: baseColor.g * 0.7, b: baseColor.b * 0.7 },
          dark: baseDark,
          light: baseLight,
          accent: baseLight,
        };
      default:
        return { soil, dark: baseDark, light: baseLight, accent: baseLight };
    }
  }

  _mulberry32(seed) {
    let s = seed >>> 0;
    return function () {
      s = (s + 0x6D2B79F5) >>> 0;
      let t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return (((t ^ (t >>> 14)) >>> 0) / 4294967296);
    };
  }

  _setupGround(palette) {
    // Phase 4 — procedural terrain (with Phase 4.1 streaming for infinite world)
    this._terrainSeed = this._hashStringToInt((this.body?.name || 'planet') + (this.body?.type || ''));
    this._biome = this._biomeParams();
    this._groundCenterX = 0;
    this._groundCenterZ = 0;
    this._featureMeshes = [];

    // Tile-based crater generation around current ground center
    this._updateCraterField();

    const size = 800;
    const subdivisions = 128;
    this._groundSize = size;
    this._groundSub = subdivisions;
    const ground = MeshBuilder.CreateGround(
      'ground',
      { width: size, height: size, subdivisions, updatable: true },
      this.scene,
    );
    this._deformGround(ground, 0, 0);
    this.ground = ground;

    const mat = new StandardMaterial('groundMat', this.scene);
    const base = hexToColor3(palette.ground);
    // Procedural ground texture (mottled noise + biome-specific accents) makes
    // the surface read as actual rock/sand/ice rather than a flat color blob.
    const { diffuseTex } = this._buildGroundTextures(this._biome, base, palette);
    mat.diffuseTexture = diffuseTex;

    // Procedural NORMAL map (PBR pass 2/6) — encodes the gradient of the
    // surface heightmap noise as tangent-space normals so the directional
    // sun creates real micro-shading on the ground (highlights on facing
    // bumps, shadows on receding ones). Properly RGB-encoded so the
    // B-channel sits near 1.0 — fixes the "darkness" issue from the earlier
    // height-map-as-normal bug.
    const normalTex = this._buildGroundNormalMap(this._biome);
    mat.bumpTexture = normalTex;
    mat.bumpTexture.uScale = 16;
    mat.bumpTexture.vScale = 16;
    mat.bumpTexture.level = (this._biome.type === 'ice' ? 0.65
      : this._biome.type === 'ocean' ? 0.4
      : this._biome.type === 'gas' ? 0.2
      : 1.10);
    // Babylon's StandardMaterial samples normal maps with Y-up convention by
    // default; we keep the channels as-painted.
    mat.invertNormalMapX = false;
    mat.invertNormalMapY = false;

    // Per-biome material tuning — drives how the directional sun's specular
    // highlight reads off the surface (icy = sharp, dirt = matte).
    mat.diffuseColor = new Color3(1, 1, 1); // texture provides colour
    if (this._biome.type === 'ice') {
      mat.specularColor = new Color3(0.6, 0.68, 0.78);
      mat.specularPower = 96;
    } else if (this._biome.type === 'ocean') {
      mat.specularColor = base.scale(0.7);
      mat.specularPower = 96;
    } else if (this._biome.type === 'lava') {
      mat.specularColor = new Color3(0.25, 0.12, 0.06);
      mat.specularPower = 24;
    } else if (this._biome.type === 'moon' || this._biome.type === 'dwarf') {
      mat.specularColor = new Color3(0.05, 0.05, 0.06);
      mat.specularPower = 8;
    } else if (this._biome.type === 'gas') {
      mat.specularColor = new Color3(0.18, 0.20, 0.24);
      mat.specularPower = 40;
    } else {
      // Terrestrial / default — slightly matte soil
      mat.specularColor = base.scale(0.08);
      mat.specularPower = 18;
    }

    ground.material = mat;
    ground.checkCollisions = false;
    ground.receiveShadows = true;
    // Allow more than the default 4 simultaneous lights so player-built
    // floodlights / habitat lamps actually illuminate the surface alongside
    // the sun + hemispheric + ship lights.
    mat.maxSimultaneousLights = 8;
    this._groundBaseColor = base;

    // Initial feature scatter around origin
    this._rebuildFeatures();
  }

  _deformGround(mesh, centerX, centerZ) {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    if (!positions) return;
    for (let i = 0; i < positions.length; i += 3) {
      const wx = positions[i] + centerX;
      const wz = positions[i + 2] + centerZ;
      positions[i + 1] = this._terrainHeight(wx, wz);
    }
    mesh.updateVerticesData(VertexBuffer.PositionKind, positions);
    mesh.createNormals(true);
  }

  _updateCraterField() {
    this._craters = [];
    const biome = this._biome;
    if (!biome || !biome.craters) return;
    const tileSize = 200;
    // Heavier crater biomes (Moon/Dwarf) get a denser tile grid
    const tilesPerSide = biome.craters > 15 ? 7 : 5;
    const half = Math.floor(tilesPerSide / 2);
    const centerTileX = Math.floor(this._groundCenterX / tileSize);
    const centerTileZ = Math.floor(this._groundCenterZ / tileSize);
    const cratersPerTile = Math.max(1, Math.round(biome.craters / (tilesPerSide * tilesPerSide / 9)));
    for (let tx = centerTileX - half; tx <= centerTileX + half; tx++) {
      for (let tz = centerTileZ - half; tz <= centerTileZ + half; tz++) {
        const tileSeed = (this._hash2(tx + 4096, tz + 4096, this._terrainSeed) * 4294967295) | 0;
        for (let i = 0; i < cratersPerTile; i++) {
          const cx = tx * tileSize + this._hash2(i, 1, tileSeed) * tileSize;
          const cz = tz * tileSize + this._hash2(i, 2, tileSeed) * tileSize;
          const radius = 14 + this._hash2(i, 3, tileSeed) * 42;
          const depth = radius * (0.35 + this._hash2(i, 4, tileSeed) * 0.4);
          this._craters.push({ x: cx, z: cz, radius, depth });
        }
      }
    }
  }

  _disposeFeatures() {
    if (!this._featureMeshes) return;
    for (const m of this._featureMeshes) {
      try { m.dispose(); } catch (_e) { /* noop */ }
    }
    this._featureMeshes = [];
    // Dispose feature materials too (rocks/trees/mushrooms create fresh
    // materials each rebuild — track + free them to avoid leaks on recenter).
    if (this._featureMats) {
      for (const mat of this._featureMats) {
        try { mat.dispose(); } catch (_e) { /* noop */ }
      }
    }
    this._featureMats = [];
    // The GLB grass template lives outside _featureMeshes (it's a long-lived
    // cached mesh). Hide it during transitions so stale thin-instance
    // positions from the previous body don't render before _setupGrass
    // rebuilds the buffer.
    if (this._grassTemplate) {
      try { this._grassTemplate.setEnabled(false); } catch (_e) { /* noop */ }
    }
    this._grassMesh = null;
    this._grassCenter = null;
  }

  _rebuildFeatures() {
    this._disposeFeatures();
    this._setupBiomeFeatures(this._groundBaseColor);
    this._setupLegendary();
  }

  /**
   * Phase 4 — build the one-time legendary wonder (archetype landmark + a
   * guaranteed cluster of rich, over-sized mineable rocks). Self-guards so it
   * only builds once even though features rebuild as the terrain streams.
   */
  _setupLegendary() {
    if (this._legendaryBuilt) return;
    const leg = this.body?.legendary;
    if (!leg || !leg.is_legendary) return;
    this._legendaryBuilt = true;
    const cx = this._groundCenterX || 0;
    const cz = this._groundCenterZ || 0;
    const seed = this._hashStringToInt((this.body?.name || 'wonder') + (leg.archetype || ''));
    this.legendary = new LegendarySystem(this.scene, {
      glow: this.glow || null,
      getTerrainHeight: (x, z) => this._terrainHeight(x, z),
    });
    const out = this.legendary.build(leg.archetype, seed, { x: cx + 24, z: cz + 24 });
    this._legendaryRocks = out.richRocks || [];
    // Merge the jackpot rocks into the live boulder list so they survive any
    // later mining re-registration, and register immediately if mining exists.
    this._lastBoulders = (this._lastBoulders || []).concat(this._legendaryRocks);
    if (this.mining) this.mining.setBoulderTargets(this._lastBoulders);
  }

  _streamTerrain() {
    if (!this.ground) return;
    const cx = this.state?.inShip ? (this.shipPos?.x ?? 0) : (this.playerRoot?.position?.x ?? 0);
    const cz = this.state?.inShip ? (this.shipPos?.z ?? 0) : (this.playerRoot?.position?.z ?? 0);
    if (Math.abs(cx - this._groundCenterX) < 100 && Math.abs(cz - this._groundCenterZ) < 100) return;
    const newCx = Math.round(cx / 100) * 100;
    const newCz = Math.round(cz / 100) * 100;
    if (newCx === this._groundCenterX && newCz === this._groundCenterZ) return;
    this._recenterTerrain(newCx, newCz);
  }

  _recenterTerrain(newCx, newCz) {
    this._groundCenterX = newCx;
    this._groundCenterZ = newCz;
    this.ground.position.x = newCx;
    this.ground.position.z = newCz;
    this._updateCraterField();
    this._deformGround(this.ground, newCx, newCz);
    if (this.waterPlane) {
      this.waterPlane.position.x = newCx;
      this.waterPlane.position.z = newCz;
    }
    this._rebuildFeatures();
  }

  // ---- Deterministic, world-anchored feature scatter -------------------------
  // Surface objects are placed per fixed 100m tile, seeded by the ABSOLUTE tile
  // coordinates, so a given rock/tree/etc. always exists at the same world
  // position regardless of where the streaming ground is recentered. This fixes
  // the bug where walking ~100m re-rolled (changed) every surface object.
  // `densityPerTile` is the average number kept per tile (may be fractional);
  // `maxPerTile` candidates are generated per tile then gated to hit that
  // average. Only features inside the visible ±span/2 window are placed.
  _scatterTiles(cx, cz, span, salt, densityPerTile, maxPerTile, place) {
    if (densityPerTile <= 0 || maxPerTile <= 0) return;
    const tileSize = 100;
    const half = span * 0.5;
    const ctX = Math.round(cx / tileSize);
    const ctZ = Math.round(cz / tileSize);
    const viewR = Math.ceil(half / tileSize) + 1;
    const keepProb = Math.min(1, densityPerTile / maxPerTile);
    const baseSeed = (this._terrainSeed ^ salt) >>> 0;
    for (let tx = ctX - viewR; tx <= ctX + viewR; tx++) {
      for (let tz = ctZ - viewR; tz <= ctZ + viewR; tz++) {
        const tileSeed = (this._hash2(tx + 8192, tz + 8192, baseSeed) * 4294967295) >>> 0;
        for (let i = 0; i < maxPerTile; i++) {
          if (this._hash2(i, 999, tileSeed) >= keepProb) continue;
          const sx = tx * tileSize + this._hash2(i, 1, tileSeed) * tileSize;
          const sz = tz * tileSize + this._hash2(i, 2, tileSeed) * tileSize;
          if (Math.abs(sx - cx) > half || Math.abs(sz - cz) > half) continue;
          place(sx, sz, i, tileSeed, tx, tz);
        }
      }
    }
  }


  _setupBiomeFeatures(baseColor) {
    const biome = this._biome;
    if (!biome) return;
    const cx = this._groundCenterX || 0;
    const cz = this._groundCenterZ || 0;
    const span = (this._groundSize || 800) * 0.92;
    // Average count → per-tile density conversion (features are now placed per
    // 100m world tile so they stay put as the ground streams; see _scatterTiles).
    const tilesInField = (span / 100) * (span / 100);
    // Retained for grass + scan-flavor sparkles (small disc around player).
    const localSeed = (this._terrainSeed ^ (((cx | 0) * 73856093) ^ (((cz | 0) * 19349663)))) >>> 0;

    // ---- Rocks: multiple stone types per body, wide size range, rare giants ----
    this._featureMats = this._featureMats || [];
    const trackMat = (m) => { this._featureMats.push(m); return m; };
    const clampC = (v) => Math.max(0, Math.min(1, v));

    // Build 2–4 rock material variants for THIS body so each world has its own
    // mix of stone shades rather than one flat colour.
    const rockVariants = 2 + Math.floor(this._hash2(0, 70, this._terrainSeed) * 3); // 2..4
    const rockMats = [];
    const rockShadeBase = biome.type === 'lava' ? 0.42 : 0.72;
    for (let v = 0; v < rockVariants; v++) {
      const m = new StandardMaterial(`rockMat${cx}_${cz}_${v}`, this.scene);
      const shade = rockShadeBase * (0.55 + this._hash2(v, 71, this._terrainSeed) * 0.9);
      const base = baseColor.scale(shade);
      m.diffuseColor = new Color3(
        clampC(base.r + (this._hash2(v, 72, this._terrainSeed) - 0.5) * 0.18),
        clampC(base.g + (this._hash2(v, 73, this._terrainSeed) - 0.5) * 0.14),
        clampC(base.b + (this._hash2(v, 74, this._terrainSeed) - 0.5) * 0.18),
      );
      m.specularColor = new Color3(0.02, 0.02, 0.02);
      m.maxSimultaneousLights = 6;
      rockMats.push(trackMat(m));
    }

    // Terrestrial worlds carry denser scatter (trees/rocks/fungi) now that the
    // ground-cover grass has been removed there. On 'low' graphics we thin it
    // out to recover frames.
    const isTerrestrial = biome.type === 'terrestrial';
    const vegScale = this.graphicsQuality === 'low' ? 0.5 : 1.0;
    const rockCount = biome.type === 'gas' ? 30 : (isTerrestrial ? Math.round(220 * vegScale) : 175);
    const mineableRocks = [];
    this._scatterTiles(cx, cz, span, 0x1001, rockCount / tilesInField, 6, (sx, sz, i, tileSeed, tx, tz) => {
      // Size distribution: mostly small/medium, occasional big boulders, and a
      // rare monolith that gets VERY large.
      const roll = this._hash2(i, 18, tileSeed);
      let size;
      if (roll > 0.97) {
        size = 12 + this._hash2(i, 19, tileSeed) * 10;                       // 12..22 monolith
      } else if (roll > 0.82) {
        size = 5 + this._hash2(i, 20, tileSeed) * 6;                         // 5..11 big boulder
      } else {
        size = 1.1 + Math.pow(this._hash2(i, 14, tileSeed), 1.6) * 4.2;      // 1.1..5.3
      }
      const r = MeshBuilder.CreatePolyhedron(`rock_${tx}_${tz}_${i}`, {
        type: Math.floor(this._hash2(i, 13, tileSeed) * 6),                  // 0..5 shapes (more variety)
        size,
      }, this.scene);
      // Non-uniform scaling → slabs, spires and squashed boulders.
      const syScale = 0.5 + this._hash2(i, 22, tileSeed) * 1.5;
      r.scaling = new Vector3(
        0.7 + this._hash2(i, 21, tileSeed) * 0.8,
        syScale,
        0.7 + this._hash2(i, 23, tileSeed) * 0.8,
      );
      const effH = size * syScale;
      r.position = new Vector3(sx, this._terrainHeight(sx, sz) + effH * 0.32, sz);
      r.rotation = new Vector3(
        this._hash2(i, 15, tileSeed) * Math.PI,
        this._hash2(i, 16, tileSeed) * Math.PI,
        this._hash2(i, 17, tileSeed) * Math.PI,
      );
      r.material = rockMats[Math.floor(this._hash2(i, 24, tileSeed) * rockMats.length)];
      r.isPickable = false;
      this._featureMeshes.push(r);
      // EVERY rock is a mineable ore source (found via the radar). Yield scales
      // with size inside MiningSystem.
      mineableRocks.push({
        id: `b_${Math.round(sx)}_${Math.round(sz)}`,
        pos: r.position.clone(),
        radius: size,
        mesh: r,
      });
    });
    // ---- Volcano landmark (lava worlds + some arid terrestrial) ----
    this._buildVolcano(biome, cx, cz, mineableRocks);

    // Hand all rocks to the mining system so they become mineable. Stored so we
    // can re-register once MiningSystem exists — features can scatter before
    // mining is constructed.
    this._lastBoulders = mineableRocks;
    // Phase 4 — keep the legendary "jackpot" rocks registered across every
    // terrain rebuild (their meshes live on the legendary root, not the
    // streaming feature pool, so only the mining registration needs merging).
    if (this._legendaryRocks && this._legendaryRocks.length) {
      this._lastBoulders = mineableRocks.concat(this._legendaryRocks);
    }
    if (this.mining) this.mining.setBoulderTargets(this._lastBoulders);

    // Lava pools — glowing flat discs at low points
    if (biome.lava > 0) {
      const lavaMat = trackMat(new StandardMaterial('lavaMat', this.scene));
      lavaMat.disableLighting = true;
      lavaMat.fogEnabled = false;
      lavaMat.emissiveColor = new Color3(1, 0.45, 0.1);
      this._scatterTiles(cx, cz, span, 0x1002, biome.lava / tilesInField, 2, (lx, lz, i, tileSeed, tx, tz) => {
        const r = 6 + this._hash2(i, 31, tileSeed) * 14;
        const d = MeshBuilder.CreateDisc(`lava_${tx}_${tz}_${i}`, { radius: r, tessellation: 18 }, this.scene);
        d.rotation.x = Math.PI / 2;
        d.position = new Vector3(lx, this._terrainHeight(lx, lz) + 0.08, lz);
        d.material = lavaMat;
        d.isPickable = false;
        this._featureMeshes.push(d);
      });
    }

    // Ice crystals — pale blue spikes
    if (biome.ice > 0) {
      const iceMat = trackMat(new StandardMaterial('iceMat', this.scene));
      iceMat.diffuseColor = new Color3(0.78, 0.92, 1);
      iceMat.specularColor = new Color3(0.95, 0.98, 1);
      iceMat.emissiveColor = new Color3(0.1, 0.18, 0.25);
      iceMat.alpha = 0.85;
      this._scatterTiles(cx, cz, span, 0x1003, biome.ice / tilesInField, 2, (ix, iz, i, tileSeed, tx, tz) => {
        const height = 2.5 + this._hash2(i, 43, tileSeed) * 6;
        const c = MeshBuilder.CreateCylinder(`ice_${tx}_${tz}_${i}`, {
          diameterTop: 0.05,
          diameterBottom: 0.6 + this._hash2(i, 44, tileSeed) * 1.1,
          height,
          tessellation: 6,
        }, this.scene);
        c.position = new Vector3(ix, this._terrainHeight(ix, iz) + height * 0.45, iz);
        c.rotation = new Vector3(
          (this._hash2(i, 45, tileSeed) - 0.5) * 0.3,
          this._hash2(i, 46, tileSeed) * Math.PI,
          (this._hash2(i, 47, tileSeed) - 0.5) * 0.3,
        );
        c.material = iceMat;
        c.isPickable = false;
        this._featureMeshes.push(c);
      });
    }

    // ---- Trees / large flora — taller, varied, with conifer + broadleaf forms ----
    if (biome.vegetation > 0) {
      const vegHabBoost = (this.body?.habitability || 0) / 100;
      let vegCount = Math.round(biome.vegetation * (0.4 + vegHabBoost * 0.7));
      if (isTerrestrial) vegCount = Math.round(vegCount * 8 * vegScale);
      const baseLeaf = biome.type === 'ocean'
        ? new Color3(0.35, 0.7, 0.55)
        : new Color3(0.22, 0.5, 0.26);
      // A few leaf-colour variants so a forest isn't a single flat green.
      const leafMats = [];
      for (let v = 0; v < 3; v++) {
        const m = new StandardMaterial(`vegMat${cx}_${cz}_${v}`, this.scene);
        m.diffuseColor = new Color3(
          clampC(baseLeaf.r + (this._hash2(v, 81, this._terrainSeed) - 0.5) * 0.18),
          clampC(baseLeaf.g + (this._hash2(v, 82, this._terrainSeed) - 0.5) * 0.22),
          clampC(baseLeaf.b + (this._hash2(v, 83, this._terrainSeed) - 0.5) * 0.18),
        );
        m.specularColor = new Color3(0, 0, 0);
        leafMats.push(trackMat(m));
      }
      const trunkMat = trackMat(new StandardMaterial(`trunkMat${cx}_${cz}`, this.scene));
      trunkMat.diffuseColor = new Color3(0.30, 0.21, 0.13);
      trunkMat.specularColor = new Color3(0, 0, 0);

      this._scatterTiles(cx, cz, span, 0x1004, vegCount / tilesInField, 14, (vx, vz, i, tileSeed, tx, tz) => {
        const gC = this._terrainHeight(vx, vz);
        if (biome.water && gC < ((biome.seaLevel ?? 1.2) + 0.3)) return;
        // Plant the trunk just below the LOWEST nearby surface point so trees
        // never float off the triangulated ground, even on slopes.
        const groundY = Math.min(
          gC,
          this._terrainHeight(vx + 1.0, vz),
          this._terrainHeight(vx - 1.0, vz),
          this._terrainHeight(vx, vz + 1.0),
          this._terrainHeight(vx, vz - 1.0),
        ) - 0.5;
        // Size: most medium, ~12% big trees that tower over the player.
        const big = this._hash2(i, 60, tileSeed) > 0.88;
        const trunkH = big ? (6 + this._hash2(i, 53, tileSeed) * 5) : (2.2 + this._hash2(i, 53, tileSeed) * 3.0);
        const trunkD = (0.22 + this._hash2(i, 54, tileSeed) * 0.3) * (big ? 2.2 : 1);
        const trunk = MeshBuilder.CreateCylinder(`trunk_${tx}_${tz}_${i}`, {
          diameterTop: trunkD * 0.6, diameterBottom: trunkD, height: trunkH, tessellation: 7,
        }, this.scene);
        trunk.position = new Vector3(vx, groundY + trunkH / 2, vz);
        trunk.material = trunkMat;
        trunk.isPickable = false;
        this._featureMeshes.push(trunk);

        const leaf = leafMats[Math.floor(this._hash2(i, 84, tileSeed) * leafMats.length)];
        if (this._hash2(i, 61, tileSeed) > 0.55) {
          // Conifer — stacked cones.
          const canopyBase = (big ? 4.5 : 2.2) + this._hash2(i, 55, tileSeed) * (big ? 4 : 2);
          for (let t = 0; t < 3; t++) {
            const cone = MeshBuilder.CreateCylinder(`conev_${tx}_${tz}_${i}_${t}`, {
              diameterTop: 0, diameterBottom: canopyBase * (1 - t / 3), height: canopyBase * 0.85, tessellation: 8,
            }, this.scene);
            cone.position = new Vector3(vx, groundY + trunkH + t * canopyBase * 0.5, vz);
            cone.material = leaf;
            cone.isPickable = false;
            this._featureMeshes.push(cone);
          }
        } else {
          // Broadleaf — rounded canopy.
          const canopyD = (big ? 5 : 1.8) + this._hash2(i, 55, tileSeed) * (big ? 5 : 2.2);
          const canopy = MeshBuilder.CreateSphere(`canopy_${tx}_${tz}_${i}`, {
            diameter: canopyD, segments: 8,
          }, this.scene);
          canopy.position = new Vector3(vx, groundY + trunkH + canopyD * 0.3, vz);
          canopy.scaling.y = 0.8 + this._hash2(i, 56, tileSeed) * 0.5;
          canopy.material = leaf;
          canopy.isPickable = false;
          this._featureMeshes.push(canopy);
        }
      });

      // ---- Bushes / shrubs — low rounded foliage clusters that carpet the
      //      forest floor (very dense on terrestrial worlds to replace grass). ----
      let bushCount = Math.round((biome.vegetation || 10) * 0.8);
      if (isTerrestrial) bushCount = Math.round(bushCount * 5 * vegScale);
      // Bioluminescent flora — on habitable / ocean / toxic worlds a fraction of
      // bushes glow (emissive), reading brightest at night.
      const bioGlow = biome.type === 'ocean' || biome.type === 'toxic' || ((this.body?.habitability || 0) >= 35);
      const bioMats = [];
      if (bioGlow) {
        const bioColors = [new Color3(0.2, 0.95, 1.0), new Color3(0.6, 0.3, 1.0), new Color3(0.3, 1.0, 0.6)];
        bioColors.forEach((c, k) => {
          const m = new StandardMaterial(`bioMat${cx}_${cz}_${k}`, this.scene);
          m.diffuseColor = c.scale(0.25);
          m.emissiveColor = c;
          m.specularColor = new Color3(0, 0, 0);
          bioMats.push(trackMat(m));
        });
      }
      this._scatterTiles(cx, cz, span, 0x1006, bushCount / tilesInField, 12, (bx, bz, i, tileSeed, tx, tz) => {
        const gC = this._terrainHeight(bx, bz);
        if (biome.water && gC < ((biome.seaLevel ?? 1.2) + 0.3)) return;
        const groundY = Math.min(
          gC,
          this._terrainHeight(bx + 0.8, bz),
          this._terrainHeight(bx - 0.8, bz),
          this._terrainHeight(bx, bz + 0.8),
          this._terrainHeight(bx, bz - 0.8),
        ) - 0.2;
        const isBio = bioGlow && bioMats.length > 0 && this._hash2(i, 87, tileSeed) < 0.3;
        const mat = isBio
          ? bioMats[Math.floor(this._hash2(i, 86, tileSeed) * bioMats.length)]
          : leafMats[Math.floor(this._hash2(i, 88, tileSeed) * leafMats.length)];
        const lobes = 2 + Math.floor(this._hash2(i, 89, tileSeed) * 2); // 2..3 lobes
        const bushR = 0.7 + this._hash2(i, 90, tileSeed) * 1.2;
        for (let b = 0; b < lobes; b++) {
          const lobe = MeshBuilder.CreateSphere(`bush_${tx}_${tz}_${i}_${b}`, {
            diameter: bushR * (0.8 + this._hash2(i + b, 91, tileSeed) * 0.6), segments: 6,
          }, this.scene);
          const off = bushR * 0.55;
          lobe.position = new Vector3(
            bx + (this._hash2(i + b, 92, tileSeed) - 0.5) * off,
            groundY + bushR * 0.4,
            bz + (this._hash2(i + b, 94, tileSeed) - 0.5) * off,
          );
          lobe.scaling.y = 0.7;
          lobe.material = mat;
          lobe.isPickable = false;
          this._featureMeshes.push(lobe);
        }
      });
    }

    // ---- Mushrooms / fungal flora — vivid caps, some giant. Grow on vegetated
    //      worlds and (glowing) on toxic biomes as alien fungus. ----
    if (biome.vegetation > 0 || biome.type === 'toxic') {
      let fungiCount = biome.type === 'toxic' ? 26 : Math.round((biome.vegetation || 10) * 0.5);
      if (isTerrestrial) fungiCount = Math.round(fungiCount * 6 * vegScale);
      const capColors = [
        new Color3(0.85, 0.22, 0.20), // red
        new Color3(0.78, 0.55, 0.25), // tan
        new Color3(0.55, 0.30, 0.65), // purple
        new Color3(0.25, 0.62, 0.70), // teal
        new Color3(0.90, 0.78, 0.35), // yellow
      ];
      const stemMat = trackMat(new StandardMaterial(`shroomStem${cx}_${cz}`, this.scene));
      stemMat.diffuseColor = new Color3(0.86, 0.83, 0.74);
      stemMat.specularColor = new Color3(0, 0, 0);
      const capMats = capColors.map((c, idx) => {
        const m = new StandardMaterial(`shroomCap${cx}_${cz}_${idx}`, this.scene);
        m.diffuseColor = c;
        m.specularColor = new Color3(0.05, 0.05, 0.05);
        m.emissiveColor = biome.type === 'toxic' ? c.scale(0.4) : c.scale(0.08);
        return trackMat(m);
      });
      this._scatterTiles(cx, cz, span, 0x1005, fungiCount / tilesInField, 10, (mx, mz, i, tileSeed, tx, tz) => {
        const gMC = this._terrainHeight(mx, mz);
        if (biome.water && gMC < ((biome.seaLevel ?? 1.2) + 0.3)) return;
        const groundY = Math.min(
          gMC,
          this._terrainHeight(mx + 0.6, mz),
          this._terrainHeight(mx - 0.6, mz),
          this._terrainHeight(mx, mz + 0.6),
          this._terrainHeight(mx, mz - 0.6),
        ) - 0.2;
        // ~10% giant mushrooms that dwarf the player.
        const giant = this._hash2(i, 93, tileSeed) > 0.9;
        const stemH = giant ? (4 + this._hash2(i, 94, tileSeed) * 5) : (0.6 + this._hash2(i, 94, tileSeed) * 2.0);
        const stemD = stemH * (0.16 + this._hash2(i, 95, tileSeed) * 0.12);
        const stem = MeshBuilder.CreateCylinder(`shroomStem_${tx}_${tz}_${i}`, {
          diameterTop: stemD * 1.1, diameterBottom: stemD * 0.8, height: stemH, tessellation: 8,
        }, this.scene);
        stem.position = new Vector3(mx, groundY + stemH / 2, mz);
        stem.material = stemMat;
        stem.isPickable = false;
        this._featureMeshes.push(stem);
        const capD = stemH * (0.9 + this._hash2(i, 96, tileSeed) * 0.9);
        const cap = MeshBuilder.CreateSphere(`shroomCap_${tx}_${tz}_${i}`, {
          diameter: capD, segments: 12,
        }, this.scene);
        cap.scaling.y = 0.55;
        cap.position = new Vector3(mx, groundY + stemH, mz);
        cap.material = capMats[Math.floor(this._hash2(i, 97, tileSeed) * capMats.length)];
        cap.isPickable = false;
        this._featureMeshes.push(cap);
      });
    }

    // Ground-cover grass — procedural crossed-quad blades, dense thin-instanced
    // tufts on biomes that have flora. Single draw call for thousands of
    // blades. Restored after the GLB experiment proved too heavy on perf.
    // Grass removed on terrestrial worlds per design feedback — the denser
    // trees, rocks and fungi carry the surface detail there instead.
    if (biome.vegetation > 0 && !isTerrestrial) {
      this._setupGrassProcedural(biome, cx, cz, span, localSeed);
    }

    // Water surface — translucent plane covering the (sub-zero) seabed / lakes
    if (biome.water) {
      const sea = (biome.seaLevel != null) ? biome.seaLevel : 1.2;
      if (!this.waterPlane) {
        const water = MeshBuilder.CreateGround('waterPlane', { width: 800, height: 800, subdivisions: 1 }, this.scene);
        water.position.y = sea;
        water.position.x = cx;
        water.position.z = cz;
        const waterMat = new StandardMaterial('waterMat', this.scene);
        waterMat.diffuseColor = new Color3(0.06, 0.24, 0.40);
        waterMat.emissiveColor = new Color3(0.03, 0.10, 0.20);
        waterMat.specularColor = new Color3(0.7, 0.85, 1.0);
        waterMat.specularPower = 128;
        waterMat.alpha = 0.8;
        // Animated ripple normal-map → shimmering specular "waves" (cheap; no
        // render-target reflections so it stays light on the GPU). Skipped on
        // 'low' graphics.
        const bump = this.graphicsQuality === 'low' ? null : this._makeWaterNormalTexture();
        if (bump) {
          bump.uScale = 6; bump.vScale = 6;
          bump.level = 0.55;
          waterMat.bumpTexture = bump;
          this.waterBumpTex = bump;
        }
        // Visible from BELOW too (underwater camera looks up at the surface).
        waterMat.backFaceCulling = false;
        water.material = waterMat;
        water.isPickable = false;
        this.waterPlane = water;
        this.waterMat = waterMat;
        this.waterLevel = sea;
        // Scroll the ripple normals over time so the surface feels alive.
        if (!this._waterAnimAdded) {
          this._waterAnimAdded = true;
          this.scene.onBeforeRenderObservable.add(() => {
            if (this.waterBumpTex) {
              const t = performance.now() * 0.00004;
              this.waterBumpTex.uOffset = t;
              this.waterBumpTex.vOffset = t * 0.65;
            }
          });
        }
      }
    } else {
      this.waterLevel = null;
    }

    // Use scan-text keywords for flavor (only if scans exist)
    this._applyScanFlavor(baseColor);
  }

  // Loads the user-supplied grass GLB once and uses it directly as the
  // persistent grass field mesh. The model is merged into a single mesh so a
  // single draw call renders the whole field via thin instances. We bake a Y
  // translation into the vertices so the tuft's base sits on Y=0, then scale
  // the mesh so each tuft is ~0.7m tall regardless of how the artist
  // exported it. The mesh starts disabled — `_setupGrass` enables it and
  // pushes a thin-instance matrix buffer once it has placement data.
  _loadGrassTemplate() {
    if (this._grassTemplatePromise) return this._grassTemplatePromise;
    this._grassTemplatePromise = SceneLoader.ImportMeshAsync(
      null, '/assets/models/', 'grass.glb', this.scene,
    ).then((result) => {
      // Pick only meshes with actual geometry — Babylon's GLB loader adds an
      // empty __root__ transform node we want to ignore.
      const real = result.meshes.filter(
        (m) => m && typeof m.getTotalVertices === 'function' && m.getTotalVertices() > 0,
      );
      if (real.length === 0) throw new Error('grass.glb has no geometry');
      // Babylon's GLB loader wraps imports in a __root__ node with a
      // handedness-correcting scale (z = -1) so Y-up right-handed glTF data
      // renders correctly in Babylon's left-handed scene. We MUST bake that
      // world transform into the vertices before detaching — otherwise the
      // mesh ends up flipped/rotated when we pluck it out of the hierarchy.
      for (const m of real) {
        try { m.bakeCurrentTransformIntoVertices(); } catch (_e) { /* noop */ }
      }
      let merged;
      if (real.length === 1) {
        real[0].setParent(null);
        merged = real[0];
      } else {
        // After baking, each mesh's verts are already in world space —
        // merging combines them without further transform issues.
        merged = Mesh.MergeMeshes(real, true, true, undefined, false, true);
        if (!merged) throw new Error('failed to merge grass meshes');
      }
      // Dispose stray transform nodes left behind by the GLB loader.
      for (const m of result.meshes) {
        if (m === merged) continue;
        if (m.getTotalVertices && m.getTotalVertices() > 0) continue;
        try { m.dispose(); } catch (_e) { /* noop */ }
      }
      merged.name = 'grassFieldGLB';
      merged.isPickable = false;
      merged.alwaysSelectAsActiveMesh = true; // never frustum-cull the field
      merged.receiveShadows = false;
      merged.setEnabled(false); // hidden until _setupGrass pushes instances

      // Reset any transforms so scaling/rotation we apply below isn't
      // multiplied by an unexpected parent matrix.
      merged.position.set(0, 0, 0);
      merged.rotation.set(0, 0, 0);
      merged.scaling.set(1, 1, 1);

      // Bake a Y translation so the base of the tuft sits at local Y=0.
      // This way each thin-instance position (wx, groundY, wz) plants the
      // tuft on the ground without needing per-instance height correction.
      const bbox = merged.getBoundingInfo().boundingBox;
      const minY = bbox.minimum.y;
      const heightNative = Math.max(bbox.maximum.y - bbox.minimum.y, 0.001);
      try {
        merged.bakeTransformIntoVertices(Matrix.Translation(0, -minY, 0));
      } catch (_e) { /* noop */ }

      // Target ~0.6m blade tuft regardless of native model scale. We apply
      // the scale to the mesh itself; per-instance matrices add ±variance.
      const TARGET_HEIGHT = 0.6;
      const fitScale = TARGET_HEIGHT / heightNative;
      merged.scaling.setAll(fitScale);

      // Make sure the material renders both sides — grass blades are
      // commonly modeled as single-sided cards.
      if (merged.material) {
        merged.material.backFaceCulling = false;
      }

      // eslint-disable-next-line no-console
      console.log('[grass] GLB loaded', {
        verts: merged.getTotalVertices?.(),
        heightNative,
        fitScale,
        hasMaterial: !!merged.material,
        materialType: merged.material?.getClassName?.(),
      });

      this._grassTemplate = merged;
      return merged;
    });
    return this._grassTemplatePromise;
  }

  // Ground-cover grass field using thin instances — a single draw call renders
  // thousands of tufts. Density and placement are biome-driven (terrestrial =
  // lush green carpet, ocean = sparse seagrass on the shoreline, default =
  // sparse). Skipped underwater and on the spawn clearing. The field is a
  // tight disc around the player so the carpet always feels dense up close;
  // `_streamGrass` rebuilds the instance buffer as the player walks.
  _setupGrass(biome, cx, cz, _span, localSeed) {
    // First call kicks off the async GLB load. We retry once it lands so the
    // initial frame on the planet doesn't block on the network fetch. If the
    // GLB fails entirely we fall back to the procedural crossed-quad blades.
    if (!this._grassTemplate) {
      if (this._grassLoadFailed) {
        return this._setupGrassProcedural(biome, cx, cz, _span, localSeed);
      }
      this._loadGrassTemplate()
        .then(() => {
          if (this.mode === 'space' || this.mode === 'ship') return;
          this._setupGrass(biome, cx, cz, _span, localSeed);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('Grass GLB unavailable, using procedural blades', err);
          this._grassLoadFailed = true;
          this._setupGrassProcedural(biome, cx, cz, _span, localSeed);
        });
      return;
    }

    // Density (instance count) scales with vegetation rating + habitability.
    // GLB tufts already contain many blades each so the perceived density is
    // far higher than the previous single-blade quads — keep counts modest.
    const habBoost = (this.body?.habitability || 0) / 100;
    // Cell size (m) controls minimum spacing between tufts; smaller = denser.
    // Tuned for performance — a 2.4MB Meshy GLB carries thousands of verts
    // per tuft, so even ~60 instances renders a lush carpet without tanking
    // frame rate.
    const cellSize = biome.type === 'terrestrial'
      ? 4.0
      : (biome.type === 'ocean' ? 6.0 : 7.5);
    // Probability a given cell hosts a tuft — habitability boosts it but
    // never exceeds 1 (so every cell stays a possible empty spot for variety).
    const cellFillProb = Math.min(0.85, 0.45 + habBoost * 0.45);

    // Field is a tight disc around the active center.
    const fieldRadius = 22;
    const center = this._grassCenter || this._activeGrassCenter();
    this._grassCenter = { x: center.x, z: center.z };

    // Grid-based placement (Poisson-disc-style): one jittered tuft per cell,
    // never two stacked. This guarantees minimum spacing of ~cellSize between
    // any pair of tufts regardless of RNG outcomes.
    const gridRange = Math.ceil(fieldRadius / cellSize) + 1;
    const maxCells = (gridRange * 2 + 1) ** 2;
    const matrices = new Float32Array(maxCells * 16);
    const water = biome.water;
    const waterLevel = water ? (biome.seaLevel ?? 1.2) : -Infinity;
    let placed = 0;
    // Carve a tiny clearing right at the spawn point so blades don't poke
    // through the player or parked ship on the very first frame.
    const carveX = 0, carveZ = -20;
    const carveRadius = biome.type === 'ocean' ? 2 : 3;

    for (let gx = -gridRange; gx <= gridRange; gx++) {
      for (let gz = -gridRange; gz <= gridRange; gz++) {
        const cellX = gx * cellSize;
        const cellZ = gz * cellSize;
        // Trim cells outside the field disc.
        if (cellX * cellX + cellZ * cellZ > fieldRadius * fieldRadius) continue;
        // Probabilistic skip — deterministic via localSeed so the same body
        // always renders the same field.
        if (this._hash2(gx + 1024, gz + 1024, localSeed) > cellFillProb) continue;

        // Jitter ±40% of a cell so the grid doesn't look like a checkerboard.
        const jx = (this._hash2(gx, gz, localSeed) - 0.5) * cellSize * 0.8;
        const jz = (this._hash2(gx, gz, localSeed + 1) - 0.5) * cellSize * 0.8;
        const wx = center.x + cellX + jx;
        const wz = center.z + cellZ + jz;

        // Skip the spawn carve-out.
        const dxs = wx - carveX;
        const dzs = wz - carveZ;
        if (dxs * dxs + dzs * dzs < carveRadius * carveRadius) continue;

        const groundY = this._terrainHeight(wx, wz);
        // Don't sprout grass underwater (or below the ocean plane).
        if (water && groundY < waterLevel + 0.2) continue;

        // Per-tuft variance: scale + yaw + slight forward tilt for wind feel.
        const sScale = 0.65 + this._hash2(gx, gz + 7, localSeed) * 0.70;
        const yaw = this._hash2(gx, gz + 11, localSeed) * Math.PI * 2;
        const tilt = (this._hash2(gx, gz + 15, localSeed) - 0.5) * 0.16;

        const rot = Quaternion.RotationYawPitchRoll(yaw, 0, tilt);
        const m = Matrix.Compose(
          new Vector3(sScale, sScale, sScale),
          rot,
          new Vector3(wx, groundY, wz),
        );
        m.copyToArray(matrices, placed * 16);
        placed++;
      }
    }
    if (placed === 0) {
      // Nothing landed in the field this tick — keep mesh disabled.
      this._grassTemplate.setEnabled(false);
      this._grassMesh = null;
      return;
    }

    const trimmedMat = matrices.slice(0, placed * 16);
    const blade = this._grassTemplate;
    blade.thinInstanceSetBuffer('matrix', trimmedMat, 16);
    try { blade.thinInstanceRefreshBoundingInfo(true); } catch (_e) { /* noop */ }
    blade.setEnabled(true);
    blade.isVisible = true;
    this._grassMesh = blade;
    // eslint-disable-next-line no-console
    console.log('[grass] populated thin instances', { placed, center });
  }

  // Procedural crossed-quad fallback grass — used when the GLB model fails
  // to load (network error, corrupt file). Identical placement logic to
  // `_setupGrass`; only the per-blade mesh is built procedurally.
  _setupGrassProcedural(biome, cx, cz, _span, localSeed) {
    const habBoost = (this.body?.habitability || 0) / 100;
    const baseDensity = biome.type === 'terrestrial'
      ? 22000
      : (biome.type === 'ocean' ? 8000 : 6000);
    const count = Math.round(baseDensity * (0.55 + habBoost * 0.75));

    const fieldRadius = 32;
    const center = this._grassCenter || this._activeGrassCenter();
    this._grassCenter = { x: center.x, z: center.z };

    const bladeWidth = 0.20;
    const bladeHeight = 0.62;
    const p1 = MeshBuilder.CreatePlane(`grassPlane1_${cx}_${cz}`, {
      width: bladeWidth, height: bladeHeight, sideOrientation: Mesh.DOUBLESIDE,
    }, this.scene);
    p1.position.y = bladeHeight * 0.5;
    const p2 = MeshBuilder.CreatePlane(`grassPlane2_${cx}_${cz}`, {
      width: bladeWidth, height: bladeHeight, sideOrientation: Mesh.DOUBLESIDE,
    }, this.scene);
    p2.position.y = bladeHeight * 0.5;
    p2.rotation.y = Math.PI / 2;
    const blade = Mesh.MergeMeshes([p1, p2], true, true, undefined, false, true);
    blade.name = `grass_${cx}_${cz}`;
    blade.isPickable = false;
    blade.alwaysSelectAsActiveMesh = true;
    blade.receiveShadows = false;

    const bladeTex = this._buildGrassBladeTexture(biome.type);
    const blMat = new StandardMaterial(`grassMat_${cx}_${cz}`, this.scene);
    blMat.diffuseTexture = bladeTex;
    blMat.opacityTexture = bladeTex;
    blMat.useAlphaFromDiffuseTexture = true;
    blMat.diffuseTexture.hasAlpha = true;
    blMat.transparencyMode = 1;
    blMat.alphaCutOff = 0.45;
    blMat.specularColor = new Color3(0, 0, 0);
    blMat.backFaceCulling = false;
    blMat.diffuseColor = new Color3(1, 1, 1);
    blMat.emissiveColor = new Color3(0.05, 0.10, 0.04);
    blade.material = blMat;

    const matrices = new Float32Array(count * 16);
    const water = biome.water;
    const waterLevel = water ? (biome.seaLevel ?? 1.2) : -Infinity;
    let placed = 0;
    const carveX = 0, carveZ = -20;
    const carveRadius = biome.type === 'ocean' ? 2 : 3;

    for (let i = 0; i < count; i++) {
      const angle = this._hash2(i, 11, localSeed) * Math.PI * 2;
      const r = Math.sqrt(this._hash2(i, 12, localSeed)) * fieldRadius;
      const wx = center.x + Math.cos(angle) * r;
      const wz = center.z + Math.sin(angle) * r;

      const dxs = wx - carveX;
      const dzs = wz - carveZ;
      if (dxs * dxs + dzs * dzs < carveRadius * carveRadius) continue;

      const groundY = this._terrainHeight(wx, wz);
      if (water && groundY < waterLevel + 0.2) continue;

      const sScale = 0.55 + this._hash2(i, 13, localSeed) * 1.10;
      const yaw = this._hash2(i, 14, localSeed) * Math.PI * 2;
      const tilt = (this._hash2(i, 15, localSeed) - 0.5) * 0.20;

      const rot = Quaternion.RotationYawPitchRoll(yaw, 0, tilt);
      const m = Matrix.Compose(
        new Vector3(sScale, sScale, sScale),
        rot,
        new Vector3(wx, groundY, wz),
      );
      m.copyToArray(matrices, placed * 16);
      placed++;
    }
    if (placed === 0) {
      try { blade.dispose(); } catch (_e) { /* noop */ }
      return;
    }

    const trimmedMat = matrices.slice(0, placed * 16);
    blade.thinInstanceSetBuffer('matrix', trimmedMat, 16);
    try { blade.thinInstanceRefreshBoundingInfo(true); } catch (_e) { /* noop */ }
    this._grassMesh = blade;
    this._featureMeshes.push(blade);
  }

  // Procedural grass-blade alpha texture. Two perpendicular planes each
  // sample this — the alpha channel cuts out the blade silhouette and the
  // RGB gradient gives darker base + sunlit tip for realistic blade shading.
  _buildGrassBladeTexture(biomeType) {
    const W = 64, H = 256;
    const tex = new DynamicTexture('grassBladeTex', { width: W, height: H }, this.scene, true);
    const ctx = tex.getContext();
    ctx.clearRect(0, 0, W, H);

    // Per-biome palette
    let topRgb, midRgb, baseRgb;
    if (biomeType === 'ocean') {
      topRgb = 'rgb(170,235,200)';
      midRgb = 'rgb(60,160,120)';
      baseRgb = 'rgb(20,70,55)';
    } else if (biomeType === 'terrestrial') {
      topRgb = 'rgb(195,230,140)';   // sunlit yellow-green tip
      midRgb = 'rgb(95,170,55)';     // bright mid blade
      baseRgb = 'rgb(35,75,25)';     // dark base in self-shadow
    } else {
      // sparse pale tufts (e.g. tundra)
      topRgb = 'rgb(210,210,150)';
      midRgb = 'rgb(150,160,90)';
      baseRgb = 'rgb(80,80,50)';
    }
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0.00, topRgb);
    grad.addColorStop(0.55, midRgb);
    grad.addColorStop(1.00, baseRgb);
    ctx.fillStyle = grad;

    // Blade silhouette — slightly curved, narrower at the tip, wider at base.
    ctx.beginPath();
    ctx.moveTo(W * 0.50, 2);
    ctx.bezierCurveTo(W * 0.42, H * 0.30, W * 0.28, H * 0.65, W * 0.20, H);
    ctx.lineTo(W * 0.80, H);
    ctx.bezierCurveTo(W * 0.72, H * 0.65, W * 0.58, H * 0.30, W * 0.50, 2);
    ctx.closePath();
    ctx.fill();

    // Sharpen the silhouette edges so alpha-test gives a crisp cutout.
    tex.update(true, true);
    tex.hasAlpha = true;
    tex.getAlphaFromRGB = false;
    return tex;
  }

  // Returns the world position the grass field should center on — player on
  // foot, ship while flying. Falls back to the world origin.
  _activeGrassCenter() {
    if (this.state?.inShip && this.shipRoot) {
      return { x: this.shipRoot.position.x, z: this.shipRoot.position.z };
    }
    if (this.playerRoot) {
      return { x: this.playerRoot.position.x, z: this.playerRoot.position.z };
    }
    return { x: 0, z: 0 };
  }

  // Stream the grass field so the dense carpet always surrounds the player.
  // Called from the main tick. Rebuilds when the player walks far enough that
  // they'd start seeing the edge of the field.
  _streamGrass() {
    if (!this._biome || this._biome.vegetation <= 0) return;
    // Terrestrial worlds have no ground-cover grass (denser trees/rocks instead).
    if (this._biome.type === 'terrestrial') return;
    if (this.mode === 'space') return;
    const center = this._activeGrassCenter();
    const prev = this._grassCenter || { x: 0, z: 0 };
    const dx = center.x - prev.x;
    const dz = center.z - prev.z;
    // Rebuild once the player has moved more than ~12 units so the carpet
    // re-fills ahead of them.
    if (dx * dx + dz * dz < 12 * 12) return;
    // Dispose old per-tile grass mesh and rebuild around the new center.
    if (this._grassMesh) {
      const idx = this._featureMeshes.indexOf(this._grassMesh);
      if (idx >= 0) this._featureMeshes.splice(idx, 1);
      try { this._grassMesh.dispose(); } catch (_e) { /* noop */ }
      this._grassMesh = null;
    }
    this._grassCenter = { x: center.x, z: center.z };
    const localSeed = (this._terrainSeed ^ (((center.x | 0) * 73856093) ^ (((center.z | 0) * 19349663)))) >>> 0;
    this._setupGrassProcedural(this._biome, this._groundCenterX | 0, this._groundCenterZ | 0, 0, localSeed);
  }

  _applyScanFlavor(baseColor) {
    const scans = this.body?.scans;
    if (!Array.isArray(scans) || scans.length === 0) return;
    const text = scans.map((s) => (s?.result?.text || s?.text || '')).join(' ').toLowerCase();
    if (!text) return;
    const cx = this._groundCenterX || 0;
    const cz = this._groundCenterZ || 0;
    const span = (this._groundSize || 800) * 0.92;
    const localSeed = (this._terrainSeed ^ (((cx | 0) * 73856093) ^ (((cz | 0) * 19349663)))) >>> 0;
    // Add small "crystal" sparkles if scan mentions crystals/gems
    if (text.includes('crystal') || text.includes('gem')) {
      const sparkleMat = new StandardMaterial('sparkleMat', this.scene);
      sparkleMat.disableLighting = true;
      sparkleMat.fogEnabled = false;
      sparkleMat.emissiveColor = new Color3(0.85, 0.95, 1);
      for (let i = 0; i < 24; i++) {
        const sx = cx + (this._hash2(i, 81, localSeed) - 0.5) * span;
        const sz = cz + (this._hash2(i, 82, localSeed) - 0.5) * span;
        const s = MeshBuilder.CreatePolyhedron(`spark_${cx}_${cz}_${i}`, { type: 1, size: 0.35 + this._hash2(i, 83, localSeed) * 0.3 }, this.scene);
        s.position = new Vector3(sx, this._terrainHeight(sx, sz) + 0.35, sz);
        s.material = sparkleMat;
        s.isPickable = false;
        this._featureMeshes.push(s);
      }
    }
    // Register all newly-built feature meshes as shadow casters so rocks,
    // crystals and tree trunks throw silhouettes onto the terrain. Skip the
    // thin-instanced grass field — thousands of tiny shadow casters would
    // crater the shadow map's performance and the visual gain is negligible.
    if (this.shadowGen) {
      for (const m of this._featureMeshes) {
        if (m && typeof m.name === 'string' && m.name.startsWith('grass_')) continue;
        try { this.shadowGen.addShadowCaster(m); } catch (_e) { /* noop */ }
      }
    }
  }

  _setupAtmosphereProps(_palette) {
    // Phase 4 terrain now provides the relief; keep this hook for future
    // atmospheric props (fog walls, dust particles, etc.) without re-adding the
    // legacy decorative half-spheres that no longer fit a sculpted surface.
  }

  /**
   * June 2026 — Painted sun sprite. Draws a soft radial glow (white core →
   * star colour → transparent) with optional cross lens-flare streaks onto a
   * DynamicTexture. Used as a camera-facing billboard so the sun reads as a
   * blazing light source instead of layered translucent spheres.
   */
  /** Build a tiling-ish ripple NORMAL map for the water surface. Derived from a
   *  small sum-of-sines height field so the specular highlights shimmer like
   *  wavelets once the texture offset is scrolled. Cheap (CPU paint, one-time). */
  /** Build a single deterministic volcano landmark on lava / arid worlds — a
   *  glowing crater that erupts embers, ringed by rich (mineable) ore. */
  _buildVolcano(biome, cx, cz, mineableRocks) {
    try { this._volcanoEmber?.dispose(); } catch (_e) { /* noop */ }
    this._volcanoEmber = null;
    this._volcano = null;
    const t = String(biome.type || '').toLowerCase();
    const lava = t === 'lava';
    const arid = (t === 'terrestrial' || t === 'dwarf') && (this.body?.habitability || 0) < 40;
    const roll = this._hash2(7, 77, this._terrainSeed);
    if (!lava && !(arid && roll > 0.5)) return;

    const ang = this._hash2(8, 78, this._terrainSeed) * Math.PI * 2;
    const dist = 130 + this._hash2(9, 79, this._terrainSeed) * 90;
    const vx = cx + Math.cos(ang) * dist;
    const vz = cz + Math.sin(ang) * dist;
    const baseY = this._terrainHeight(vx, vz);
    const height = 34 + this._hash2(10, 80, this._terrainSeed) * 26;
    const baseR = height * 1.5;

    const cone = MeshBuilder.CreateCylinder('volcano', {
      diameterTop: baseR * 0.32, diameterBottom: baseR, height, tessellation: 24,
    }, this.scene);
    cone.position = new Vector3(vx, baseY + height / 2 - 2, vz);
    const rockMat = new StandardMaterial('volcanoRock', this.scene);
    rockMat.diffuseColor = new Color3(0.14, 0.11, 0.10);
    rockMat.specularColor = new Color3(0.02, 0.02, 0.02);
    cone.material = rockMat;
    cone.isPickable = false;
    this._featureMeshes.push(cone);
    this._featureMats.push(rockMat);

    const craterY = baseY + height - 2;
    const crater = MeshBuilder.CreateDisc('volcanoCrater', { radius: baseR * 0.3, tessellation: 24 }, this.scene);
    crater.rotation.x = Math.PI / 2;
    crater.position = new Vector3(vx, craterY, vz);
    const lavaMat = new StandardMaterial('volcanoLava', this.scene);
    lavaMat.disableLighting = true;
    lavaMat.emissiveColor = new Color3(1.0, 0.4, 0.08);
    crater.material = lavaMat;
    crater.isPickable = false;
    this._featureMeshes.push(crater);
    this._featureMats.push(lavaMat);

    // Ember eruption emitter (skipped on 'low' graphics).
    if (this.graphicsQuality !== 'low') {
      try {
        const et = new DynamicTexture('volcEmberTex', { width: 32, height: 32 }, this.scene, false);
        const ec = et.getContext();
        const g = ec.createRadialGradient(16, 16, 1, 16, 16, 16);
        g.addColorStop(0, 'rgba(255,255,255,1)');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ec.fillStyle = g; ec.fillRect(0, 0, 32, 32);
        et.hasAlpha = true; et.update();
        const ps = new ParticleSystem('volcanoEmber', 220, this.scene);
        ps.particleTexture = et;
        ps.emitter = new Vector3(vx, craterY + 1, vz);
        ps.minEmitBox = new Vector3(-baseR * 0.2, 0, -baseR * 0.2);
        ps.maxEmitBox = new Vector3(baseR * 0.2, 1, baseR * 0.2);
        ps.color1 = new Color4(1.0, 0.6, 0.15, 1);
        ps.color2 = new Color4(1.0, 0.3, 0.05, 1);
        ps.colorDead = new Color4(0.3, 0.05, 0, 0);
        ps.minSize = 0.5; ps.maxSize = 1.8;
        ps.minLifeTime = 1.6; ps.maxLifeTime = 3.6;
        ps.emitRate = 70;
        ps.blendMode = ParticleSystem.BLENDMODE_ADD;
        ps.gravity = new Vector3(0, -6, 0);
        ps.direction1 = new Vector3(-3, 11, -3);
        ps.direction2 = new Vector3(3, 19, 3);
        ps.minEmitPower = 4; ps.maxEmitPower = 11;
        ps.updateSpeed = 0.02;
        ps.start();
        this._volcanoEmber = ps;
      } catch (_e) { /* noop */ }
    }

    // Rich ore boulders ringing the base — tangible mining reward.
    for (let k = 0; k < 6; k++) {
      const ra = (k / 6) * Math.PI * 2 + this._hash2(k, 41, this._terrainSeed);
      const rr = baseR + 6 + this._hash2(k, 42, this._terrainSeed) * 14;
      const ox = vx + Math.cos(ra) * rr;
      const oz = vz + Math.sin(ra) * rr;
      const oy = this._terrainHeight(ox, oz);
      const size = 6 + this._hash2(k, 43, this._terrainSeed) * 5;
      const ore = MeshBuilder.CreatePolyhedron(`volcOre_${k}`, { type: 2, size }, this.scene);
      ore.position = new Vector3(ox, oy + size * 0.3, oz);
      const om = new StandardMaterial(`volcOreMat_${k}`, this.scene);
      om.diffuseColor = new Color3(0.32, 0.17, 0.12);
      om.emissiveColor = new Color3(0.45, 0.13, 0.03);
      om.specularColor = new Color3(0.05, 0.05, 0.05);
      ore.material = om;
      ore.isPickable = false;
      this._featureMeshes.push(ore);
      this._featureMats.push(om);
      mineableRocks.push({
        id: `volc_${Math.round(ox)}_${Math.round(oz)}`,
        pos: ore.position.clone(),
        radius: size * 1.4,
        mesh: ore,
      });
    }

    this._volcano = { crater, lavaMat, t: 0 };
  }

  /** Aurora ribbons high in the sky — fade in at night on bodies with an
   *  atmosphere. Atmospheric only; gated off on 'low' graphics. */
  _setupAurora() {
    this._aurora = null;
    if (this.atmosphere === 'none' || this.graphicsQuality === 'low') return;
    const bands = [];
    const palettes = [
      new Color3(0.2, 1.0, 0.6),
      new Color3(0.3, 0.6, 1.0),
      new Color3(0.6, 0.3, 1.0),
    ];
    for (let i = 0; i < 3; i++) {
      const tex = new DynamicTexture(`auroraTex${i}`, { width: 256, height: 128 }, this.scene, false);
      const ctx = tex.getContext();
      const grad = ctx.createLinearGradient(0, 0, 0, 128);
      grad.addColorStop(0, 'rgba(255,255,255,0)');
      grad.addColorStop(0.5, 'rgba(255,255,255,0.95)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      for (let x = 0; x < 256; x += 4) {
        const w = 2 + Math.abs(Math.sin(x * 0.08 + i)) * 3;
        ctx.fillRect(x, 0, w, 128);
      }
      tex.hasAlpha = true;
      tex.wrapU = Texture.WRAP_ADDRESSMODE;
      tex.update();
      const plane = MeshBuilder.CreatePlane(`aurora${i}`, { width: 2600, height: 360 }, this.scene);
      plane.position = new Vector3((i - 1) * 240, 760 + i * 90, -300 + i * 160);
      plane.rotation.x = -0.5 - i * 0.12;
      plane.rotation.y = (i - 1) * 0.3;
      if (this.skyAnchor) plane.parent = this.skyAnchor;
      plane.isPickable = false;
      plane.renderingGroupId = 1;
      const mat = new StandardMaterial(`auroraMat${i}`, this.scene);
      mat.disableLighting = true;
      mat.fogEnabled = false;
      mat.backFaceCulling = false;
      mat.opacityTexture = tex;
      mat.emissiveColor = palettes[i].clone();
      mat.alpha = 0;
      plane.material = mat;
      bands.push({ plane, mat, tex, base: palettes[i] });
    }
    this._aurora = { bands, t: 0 };
  }

  _makeWaterNormalTexture() {
    try {
      const size = 256;
      const dt = new DynamicTexture('waterNormal', { width: size, height: size }, this.scene, false);
      const ctx = dt.getContext();
      const img = ctx.createImageData(size, size);
      const hAt = (x, y) =>
        Math.sin(x * 0.10) * 0.5 +
        Math.sin(y * 0.13 + x * 0.05) * 0.5 +
        Math.sin((x + y) * 0.07) * 0.4;
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          let nx = hAt(x - 1, y) - hAt(x + 1, y);
          let ny = hAt(x, y - 1) - hAt(x, y + 1);
          const nz = 1.0;
          const len = Math.hypot(nx, ny, nz) || 1;
          nx /= len; ny /= len;
          const idx = (y * size + x) * 4;
          img.data[idx] = Math.round((nx * 0.5 + 0.5) * 255);
          img.data[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);
          img.data[idx + 2] = Math.round((nz / len * 0.5 + 0.5) * 255);
          img.data[idx + 3] = 255;
        }
      }
      ctx.putImageData(img, 0, 0);
      dt.update();
      dt.wrapU = Texture.WRAP_ADDRESSMODE;
      dt.wrapV = Texture.WRAP_ADDRESSMODE;
      return dt;
    } catch (_e) {
      return null;
    }
  }

  _makeSunTexture(starColor, { flare = true, name = 'sunTex' } = {}) {
    const size = 512;
    const tex = new DynamicTexture(name, { width: size, height: size }, this.scene, false);
    tex.hasAlpha = true;
    const ctx = tex.getContext();
    const c = size / 2;
    ctx.clearRect(0, 0, size, size);
    const ch = (v) => Math.round(Math.max(0, Math.min(1, v)) * 255);
    const col = (sc, a) => `rgba(${ch(sc.r)},${ch(sc.g)},${ch(sc.b)},${a})`;
    const warm = new Color3(
      Math.min(1, starColor.r * 0.4 + 0.6),
      Math.min(1, starColor.g * 0.4 + 0.6),
      Math.min(1, starColor.b * 0.4 + 0.6),
    );
    // Soft radial glow — blinding white core easing into the star's colour.
    const g = ctx.createRadialGradient(c, c, 0, c, c, c);
    g.addColorStop(0.00, 'rgba(255,255,255,1)');
    g.addColorStop(0.07, col(warm, 0.98));
    g.addColorStop(0.16, col(starColor, 0.70));
    g.addColorStop(0.32, col(starColor, 0.30));
    g.addColorStop(0.60, col(starColor, 0.10));
    g.addColorStop(1.00, col(starColor, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    if (flare) {
      // Cross lens-flare: two slim streaks fading out from the core.
      ctx.save();
      ctx.translate(c, c);
      for (const ang of [0, Math.PI / 2]) {
        ctx.save();
        ctx.rotate(ang);
        const lg = ctx.createLinearGradient(-c, 0, c, 0);
        lg.addColorStop(0.0, col(starColor, 0));
        lg.addColorStop(0.42, col(warm, 0.30));
        lg.addColorStop(0.5, col(warm, 0.55));
        lg.addColorStop(0.58, col(warm, 0.30));
        lg.addColorStop(1.0, col(starColor, 0));
        ctx.fillStyle = lg;
        ctx.fillRect(-c, -size * 0.011, size, size * 0.022);
        ctx.restore();
      }
      ctx.restore();
    }
    tex.update();
    return tex;
  }

  /** Build a camera-facing glow billboard from a sun sprite texture. */
  _makeSunBillboard(name, sizeUnits, tex, parent) {
    const plane = MeshBuilder.CreatePlane(name, { size: sizeUnits }, this.scene);
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    plane.renderingGroupId = 1;
    plane.isPickable = false;
    if (parent) plane.parent = parent;
    const mat = new StandardMaterial(`${name}Mat`, this.scene);
    mat.disableLighting = true;
    mat.fogEnabled = false;
    mat.emissiveTexture = tex;
    mat.opacityTexture = tex;
    mat.opacityTexture.getAlphaFromRGB = true; // luminance → opacity (soft edges)
    mat.diffuseColor = new Color3(0, 0, 0);
    mat.specularColor = new Color3(0, 0, 0);
    mat.backFaceCulling = false;
    plane.material = mat;
    if (this.glow) {
      try { this.glow.addExcludedMesh(plane); } catch (_e) { /* noop */ }
    }
    return plane;
  }

  _setupCelestials() {
    // Celestial bodies (sun, sky-moons, parent planet) render in rendering
    // group 1 so they composite cleanly OVER the skybox + starfield. However,
    // Babylon clears the depth buffer between rendering groups by default,
    // which made these distant bodies draw ON TOP of nearby surface terrain &
    // props. Disabling the depth auto-clear for group 1 lets it share group 0's
    // depth buffer, so the planet/moon you're standing on (and its rocks,
    // trees, hills) correctly occlude anything in the sky behind them.
    try { this.scene.setRenderingAutoClearDepthStencil(1, false, true, true); } catch (_e) { /* noop */ }

    // Sun (the system's star). A small bright core surrounded by layered
    // corona shells gives the impression of a glowing photosphere. The core
    // is excluded from the glow layer so it stays sharp, while the corona
    // layers are picked up by bloom for a softer halo.
    const starColorHex = this.body?.star?.color || '#FFE4A0';
    const starColor = hexToColor3(starColorHex);
    this._sunDist = 1200;
    // Initial placement = "noon" so the scene reads correctly on the first
    // frame before _tickDayNight runs.
    const initialSunUp = new Vector3(0, 1, -0.22).normalize();

    // Group all sun layers under one transform so we move them as a unit.
    const sunGroup = new TransformNode('sunGroup', this.scene);
    sunGroup.position = initialSunUp.scale(this._sunDist);
    if (this.skyAnchor) sunGroup.parent = this.skyAnchor;
    this.sunMesh = sunGroup; // legacy reference — _tickDayNight only reads .position
    this._sunGroup = sunGroup;

    // 1) Bright disc — the visible "photosphere".
    const core = MeshBuilder.CreateSphere('sunCore', { diameter: 60, segments: 32 }, this.scene);
    core.parent = sunGroup;
    core.renderingGroupId = 1;
    core.isPickable = false;
    const coreMat = new StandardMaterial('sunCoreMat', this.scene);
    coreMat.disableLighting = true;
    coreMat.fogEnabled = false;
    // Push toward white at the center so bloom blows it out like a real star.
    coreMat.emissiveColor = starColor.scale(1.0).add(new Color3(0.55, 0.55, 0.55));
    coreMat.specularColor = new Color3(0, 0, 0);
    core.material = coreMat;
    this._sunCore = core;

    // Outer corona/halo "ring" billboards were removed per design feedback —
    // the bright core sphere plus the bloom pipeline supply a clean glowing
    // disc with no concentric rings around it.
    this.sunHalo = null;
    this._sunCorona = null;

    // Keep the core crisp; bloom from the rendering pipeline radiates the glow.
    if (this.glow) {
      try { this.glow.addExcludedMesh(core); } catch (_e) { /* noop */ }
    }

    // Aim the directional sun light from the actual sun position toward origin.
    // (Will be overridden every frame by _tickDayNight; this is just the first frame.)
    if (this.sun) {
      this.sun.direction = initialSunUp.scale(-1);
      // Tint sunlight with star color (preserve some white for definition)
      const tint = starColor.scale(0.65).add(new Color3(0.35, 0.35, 0.35));
      this.sun.diffuse = tint;
      if (this._dayNight) this._dayNight.sunBaseColor = this.sun.diffuse.clone();
    }

    // Moons orbiting overhead (only when standing on a planet that has moons)
    this.skyMoons = [];
    if (this.body?.kind === 'planet' && Array.isArray(this.body.moons) && this.body.moons.length > 0) {
      const list = this.body.moons.slice(0, 6); // cap for performance
      list.forEach((moon, i) => {
        const dia = 26 + Math.min(70, (moon.radius_km || 1500) / 60);
        const m = MeshBuilder.CreateSphere(`skyMoon${i}`, { diameter: dia, segments: 28 }, this.scene);
        m.renderingGroupId = 1;
        if (this.skyAnchor) m.parent = this.skyAnchor;
        // Procedural planetary texture (matches the system-space view).
        const mat = createPlanetMaterial(this.scene, {
          name: moon.name,
          type: moon.type || 'Moon',
          kind: 'moon',
          color: moon.color || '#a8a39d',
          planetIdx: this.body?.currentPlanetIdx ?? 0,
          moonIdx: i,
        });
        mat.fogEnabled = false;
        m.material = mat;
        m.rotation.y = Math.random() * Math.PI * 2;
        m.isPickable = false;
        const radius = 700 + i * 110;
        const initialAngle = (i / list.length) * Math.PI * 2 + Math.random() * 0.5;
        const inclination = 0.25 + (i % 3) * 0.18;
        const periodSec = 80 + i * 35;
        this.skyMoons.push({
          mesh: m,
          radius,
          angle: initialAngle,
          inclination,
          omega: (2 * Math.PI) / periodSec,
        });
      });
    }

    // Parent planet — visible when standing on a moon
    this.parentPlanetMesh = null;
    if (this.body?.kind === 'moon' && this.body?.parentColor) {
      const dia = 480;
      const pp = MeshBuilder.CreateSphere('parentPlanet', { diameter: dia, segments: 48 }, this.scene);
      // Place opposite/perpendicular from the sun so it dominates the sky.
      // Keep distance < skybox half (2000) to avoid clipping.
      pp.position = new Vector3(-720, 380, -880); // closer to horizon, dominates the sky
      pp.renderingGroupId = 1;
      if (this.skyAnchor) pp.parent = this.skyAnchor;
      // Procedural planetary texture for the parent world (matches space view).
      const ppm = createPlanetMaterial(this.scene, {
        name: this.body.parentName || 'Parent',
        type: this.body.parentType || 'Terrestrial',
        kind: 'planet',
        color: this.body.parentColor,
        planetIdx: this.body?.currentPlanetIdx ?? 0,
        moonIdx: undefined,
      });
      ppm.fogEnabled = false;
      pp.material = ppm;
      pp.rotation.y = Math.random() * Math.PI * 2;
      pp.isPickable = false;
      this.parentPlanetMesh = pp;
    }
  }

  _setupClouds() {
    // June 2026 — volumetric-style puffy clouds. Each cloud is a cluster of
    // flattened, overlapping spheres under one TransformNode (the classic
    // low-poly "cumulus" look that matches the art style), with a slow wind
    // drift handled in _tick. The old flat translucent discs read as dim
    // plates; these read as actual clouds.
    this.cloudMeshes = [];
    this._cloudDrift = [];
    if (this.atmosphere === 'none') return;
    let cloudCount = 0;
    let alpha = 0.5;
    const altitudeMin = 150;
    const altitudeMax = 250;
    if (this.atmosphere === 'thin') { cloudCount = 5; alpha = 0.42; }
    else if (this.atmosphere === 'dense') { cloudCount = 10; alpha = 0.55; }
    else if (this.atmosphere === 'toxic') { cloudCount = 7; alpha = 0.46; }
    if (cloudCount === 0) return;

    // Two shared materials: bright tops + slightly shaded undersides give the
    // clusters depth without any lighting cost.
    const tint = this.atmosphere === 'toxic'
      ? new Color3(0.64, 0.72, 0.44)
      : new Color3(0.93, 0.95, 0.99);
    const topMat = new StandardMaterial('cloudTopMat', this.scene);
    topMat.disableLighting = true;
    topMat.fogEnabled = false;
    topMat.emissiveColor = tint;
    topMat.specularColor = new Color3(0, 0, 0);
    topMat.alpha = alpha;
    topMat.backFaceCulling = false;
    const baseMat = new StandardMaterial('cloudBaseMat', this.scene);
    baseMat.disableLighting = true;
    baseMat.fogEnabled = false;
    baseMat.emissiveColor = tint.scale(0.72);
    baseMat.specularColor = new Color3(0, 0, 0);
    baseMat.alpha = alpha * 0.9;
    baseMat.backFaceCulling = false;

    for (let i = 0; i < cloudCount; i++) {
      const root = new TransformNode(`cloud${i}`, this.scene);
      root.position = new Vector3(
        (Math.random() - 0.5) * 900,
        altitudeMin + Math.random() * (altitudeMax - altitudeMin),
        (Math.random() - 0.5) * 900,
      );
      if (this.skyAnchor) root.parent = this.skyAnchor;

      const width = 30 + Math.random() * 45;           // cluster half-width
      const nPuffs = 4 + Math.floor(Math.random() * 4); // 4-7 puffs
      for (let p = 0; p < nPuffs; p++) {
        const t = nPuffs <= 1 ? 0.5 : p / (nPuffs - 1);          // 0..1 across the cluster
        const envelope = 0.45 + 0.55 * Math.sin(Math.PI * t);     // big in the middle
        const dia = width * (0.55 + Math.random() * 0.5) * envelope;
        const puff = MeshBuilder.CreateSphere(`cloud${i}_p${p}`, { diameter: dia, segments: 10 }, this.scene);
        puff.scaling = new Vector3(1.25, 0.42 + Math.random() * 0.14, 1.0);
        puff.position = new Vector3(
          (t - 0.5) * width * 2 + (Math.random() - 0.5) * width * 0.25,
          (Math.random() - 0.35) * width * 0.16,
          (Math.random() - 0.5) * width * 0.7,
        );
        puff.material = (puff.position.y < 0 ? baseMat : topMat);
        puff.isPickable = false;
        puff.parent = root;
        if (this.glow) {
          try { this.glow.addExcludedMesh(puff); } catch (_e) { /* noop */ }
        }
      }
      // Slow wind drift — wraps around the sky so coverage stays constant.
      this._cloudDrift.push({ node: root, vx: 1.0 + Math.random() * 1.8 });
      this.cloudMeshes.push(root);
    }
  }

  _setupPlayer() {
    // Root that holds the player's world position + facing
    this.playerRoot = new TransformNode('playerRoot', this.scene);
    const spawnX = 0, spawnZ = -20;
    const spawnY = this._terrainHeight ? this._terrainHeight(spawnX, spawnZ) : 0;
    this.playerRoot.position = new Vector3(spawnX, spawnY, spawnZ);
    // Spawn yaw faces toward the ship (which sits forward-right) and the sun,
    // giving a great hero shot of both on arrival.
    this.yaw = Math.atan2(12, 10);
    this.pitch = 0.2; // start with a small upward tilt so the sky is visible
    // Vertical state for player jumping/falling
    this.playerY = spawnY;
    this.playerVY = 0;
    this.grounded = true;

    // Astronaut body (shown in TPS only)
    const body = MeshBuilder.CreateCapsule('astroBody', { radius: 0.35, height: 1.4 }, this.scene);
    body.position = new Vector3(0, 0.85, 0);
    const bodyMat = new StandardMaterial('astroBodyMat', this.scene);
    bodyMat.diffuseColor = new Color3(0.9, 0.92, 0.96);
    bodyMat.specularColor = new Color3(0.2, 0.2, 0.25);
    body.material = bodyMat;

    const helmet = MeshBuilder.CreateSphere('astroHelmet', { diameter: 0.5 }, this.scene);
    helmet.position = new Vector3(0, 1.7, 0);
    const helmetMat = new StandardMaterial('astroHelmetMat', this.scene);
    helmetMat.diffuseColor = new Color3(0.1, 0.15, 0.22);
    helmetMat.specularColor = new Color3(0.7, 0.8, 0.95);
    helmetMat.emissiveColor = new Color3(0.05, 0.08, 0.12);
    helmet.material = helmetMat;

    const visor = MeshBuilder.CreateBox('astroVisor', { width: 0.32, height: 0.14, depth: 0.05 }, this.scene);
    visor.position = new Vector3(0, 1.75, 0.22);
    const visorMat = new StandardMaterial('astroVisorMat', this.scene);
    visorMat.diffuseColor = new Color3(0.05, 0.05, 0.08);
    visorMat.emissiveColor = new Color3(0.2, 0.5, 0.8);
    visor.material = visorMat;

    const pack = MeshBuilder.CreateBox('astroPack', { width: 0.45, height: 0.5, depth: 0.25 }, this.scene);
    pack.position = new Vector3(0, 1.2, -0.28);
    const packMat = new StandardMaterial('astroPackMat', this.scene);
    packMat.diffuseColor = new Color3(0.7, 0.72, 0.78);
    pack.material = packMat;

    this.astronautMeshes = [body, helmet, visor, pack];
    this.astronautMeshes.forEach((m) => { m.parent = this.playerRoot; m.isPickable = false; });
    // Cast shadows from the player so the directional sun feels real.
    if (this.shadowGen) {
      for (const m of this.astronautMeshes) {
        try { this.shadowGen.addShadowCaster(m); } catch (_e) { /* noop */ }
      }
    }

    // Cameras
    this.fpsCamera = new FreeCamera('fpsCam', new Vector3(0, 1.7, 0), this.scene);
    this.fpsCamera.minZ = 0.05;
    this.fpsCamera.parent = this.playerRoot;
    this.fpsCamera.inputs.clear();   // we drive rotation manually

    this.tpsCamera = new ArcRotateCamera('tpsCam', -Math.PI / 2, Math.PI / 2.4, 6, new Vector3(0, 1.3, 0), this.scene);
    this.tpsCamera.lowerRadiusLimit = 3;
    this.tpsCamera.upperRadiusLimit = 10;
    this.tpsCamera.upperBetaLimit = Math.PI / 1.85;
    this.tpsCamera.lowerBetaLimit = 0.2;
    this.tpsCamera.inputs.clear();   // we drive rotation manually too
  }

  _setupSpaceship() {
    const shipRoot = new TransformNode('shipRoot', this.scene);
    const shipX = 12, shipZ = -10;
    const shipY = this._terrainHeight ? this._terrainHeight(shipX, shipZ) : 0;
    shipRoot.position = new Vector3(shipX, shipY, shipZ);
    shipRoot.rotation = new Vector3(0, -Math.PI / 4, 0);
    this.shipRoot = shipRoot;

    // June 2026 — Hangar: build the active ship's hull via the procedural
    // ShipBuilder. Every builder returns the same gameplay rig hooks so the
    // flight code (thruster/ring emissive pulse, nav lights, gear, cockpit
    // occluders, exhaust anchors) works for all 6 ships.
    const rig = buildShip(this.scene, shipRoot, this.shipDef?.visual || 'scout', {
      shadowGen: this.shadowGen || null,
    });
    this.thrusterMat = rig.thrusterMat;
    this._thrusterRingMat = rig.ringMat;
    this._shipNavLights = rig.navLights;
    this.gearMeshes = rig.gearMeshes || [];
    this._shipExhaustAnchors = rig.exhaustAnchors || [
      new Vector3(-1.2, 1.4, -3.85), new Vector3(0, 1.4, -3.85), new Vector3(1.2, 1.4, -3.85),
    ];
    this._shipCockpitOccluders = rig.cockpitOccluders || [];
    // Underglow light — kept so the parked ship still casts the warm pool
    // of light onto the terrain at dusk/night that the original design had.
    const underglow = new PointLight('shipUnderglow', new Vector3(0, 0.6, 0), this.scene);
    underglow.diffuse = new Color3(0.30, 0.55, 1.0);
    underglow.intensity = 0.55;
    underglow.range = 9;
    underglow.parent = shipRoot;
    this._shipUnderglow = underglow;

    // Flight physics state
    this.shipPos = shipRoot.position.clone();
    this.shipVel = new Vector3(0, 0, 0);
    this.shipYaw = shipRoot.rotation.y;
    this.shipPitch = 0;
    this.shipRoll = 0;
    this.shipThrottle = 0;
    this.shipLanded = true;
    this._shipSpawnPos = shipRoot.position.clone();

    // Flight chase camera (used only when piloting)
    this.flightCamera = new FreeCamera('flightCam', new Vector3(0, 6, -25), this.scene);
    this.flightCamera.minZ = 0.05;
    this.flightCamera.maxZ = 60000; // long view for space travel
    this.flightCamera.inputs.clear();

    // Cockpit first-person camera (parented to the ship so it inherits its
    // pose). Local position sits just inside the cockpit glass, looking
    // forward over the nose. We intentionally leave its rotation at zero —
    // the ship's own yaw/pitch/roll carries it.
    this.shipFpsCamera = new FreeCamera('shipFpsCam', new Vector3(0, 1.95, 1.6), this.scene);
    this.shipFpsCamera.minZ = 0.05;
    this.shipFpsCamera.maxZ = 60000;
    this.shipFpsCamera.parent = shipRoot;
    this.shipFpsCamera.inputs.clear();
    // Track the meshes we hide while in cockpit view so we don't have the
    // hull / canopy blocking the forward view. (Provided by the ShipBuilder.)
    this._cockpitOccluders = this._shipCockpitOccluders || [];
  }

  // ---------- Phase 5 — System Travel (space scene) ----------

  _setupSpace() {
    // Build the system-space scene: star at the origin of `spaceRoot` + every
    // planet/moon placed at scaled orbital positions. Disabled initially.
    const layout = this.body?.systemLayout;
    this.spaceRoot = new TransformNode('spaceRoot', this.scene);
    this.spaceRoot.setEnabled(false);
    this.spaceBodies = []; // [{ planetIdx, moonIdx, kind, name, type, mesh, scaledRadius, worldPos, target }]
    this._auToUnits = 600;
    if (!layout || !Array.isArray(layout.bodies) || layout.bodies.length === 0) return;

    // Central star — crisp core sphere + painted billboard corona.
    const starColor = hexToColor3(layout.star?.color || '#FFE4A0');
    const starMesh = MeshBuilder.CreateSphere('spaceStar', { diameter: 280, segments: 36 }, this.scene);
    const starMat = new StandardMaterial('spaceStarMat', this.scene);
    starMat.disableLighting = true;
    starMat.fogEnabled = false;
    // Push the core toward white so it reads as blinding photosphere.
    starMat.emissiveColor = starColor.scale(0.6).add(new Color3(0.5, 0.5, 0.5));
    starMesh.material = starMat;
    starMesh.position = new Vector3(0, 0, 0);
    starMesh.parent = this.spaceRoot;
    starMesh.isPickable = false;
    this.spaceStar = starMesh;

    // Outer corona/halo "ring" billboards removed per design feedback — the
    // bright star sphere plus bloom give a clean glowing disc with no rings.

    // Point light at the star
    const spaceLight = new PointLight('spaceStarLight', new Vector3(0, 0, 0), this.scene);
    spaceLight.diffuse = starColor.scale(0.7).add(new Color3(0.3, 0.3, 0.3));
    spaceLight.intensity = 1.0;
    spaceLight.range = 50000;
    spaceLight.parent = this.spaceRoot;
    spaceLight.setEnabled(false);
    this.spaceLight = spaceLight;

    // Build each body — placed so spheres never intersect. Planets are spaced
    // along increasing shells (accounting for each planet's moon cluster), and
    // moons orbit clear of their parent's surface and of each other.
    const AU = this._auToUnits;
    const scaledRadiusFor = (b) => {
      const km = b.radius_km || (b.kind === 'moon' ? 1500 : 6000);
      return b.kind === 'moon'
        ? Math.max(12, Math.min(30, km / 200))
        : Math.max(28, Math.min(90, km / 200));
    };

    // Group moons by their parent planet.
    const moonsByPlanet = {};
    for (const b of layout.bodies) {
      if (b.kind === 'moon') (moonsByPlanet[b.planetIdx] = moonsByPlanet[b.planetIdx] || []).push(b);
    }

    // Planets ordered by orbit so we can lay them out on non-overlapping shells.
    const planets = layout.bodies.filter((b) => b.kind === 'planet')
      .sort((a, b) => ((a.orbit_au || 0) - (b.orbit_au || 0)));

    const placements = []; // { b, scaledR, x, z, orbitR (planets only) }
    let shell = 400; // keep clear of the central star (radius 140 + halo)
    for (let pi = 0; pi < planets.length; pi++) {
      const p = planets[pi];
      const pr = scaledRadiusFor(p);
      const moons = moonsByPlanet[p.planetIdx] || [];

      // Pre-compute moon ring radii (from the planet's surface outward).
      let ring = pr + 34;
      const moonRings = [];
      for (let mi = 0; mi < moons.length; mi++) {
        const mr = scaledRadiusFor(moons[mi]);
        ring += mr;                       // centre of this moon's orbit
        const ang = (typeof moons[mi].moonAngle === 'number')
          ? moons[mi].moonAngle
          : (mi * 2.399963 + (p.initial_angle || 0));
        moonRings.push({ b: moons[mi], scaledR: mr, ring, ang });
        ring += mr + 18;                  // gap before the next moon ring
      }
      const clusterR = moonRings.length
        ? (moonRings[moonRings.length - 1].ring + moonRings[moonRings.length - 1].scaledR)
        : pr;
      const need = Math.max(pr, clusterR);

      // Place the planet no closer than the running shell, honouring its orbit.
      const orbitR = Math.max((p.orbit_au || 0.5) * AU, shell + need);
      const angle = p.initial_angle || 0;
      const px = Math.cos(angle) * orbitR;
      const pz = Math.sin(angle) * orbitR;
      placements.push({ b: p, scaledR: pr, x: px, z: pz, orbitR });

      // Place this planet's moons around it.
      for (const m of moonRings) {
        placements.push({
          b: m.b,
          scaledR: m.scaledR,
          x: px + Math.cos(m.ang) * m.ring,
          z: pz + Math.sin(m.ang) * m.ring,
        });
      }

      shell = orbitR + need + 150; // reserve room before the next planet shell
    }

    for (const pl of placements) {
      const b = pl.b;
      const scaledR = pl.scaledR;
      const bx = pl.x;
      const bz = pl.z;

      const mesh = MeshBuilder.CreateSphere(
        `space_${b.kind}_${b.planetIdx}${b.moonIdx !== undefined ? '_' + b.moonIdx : ''}`,
        { diameter: scaledR * 2, segments: b.kind === 'moon' ? 24 : 40 },
        this.scene,
      );
      mesh.position = new Vector3(bx, 0, bz);
      mesh.parent = this.spaceRoot;
      mesh.isPickable = false;
      mesh.rotation.y = Math.random() * Math.PI * 2;

      // Procedural planetary texture based on the body type.
      mesh.material = createPlanetMaterial(this.scene, {
        name: b.name,
        type: b.type,
        kind: b.kind,
        color: b.color,
        planetIdx: b.planetIdx,
        moonIdx: b.moonIdx,
      });

      // Orbit ring guide (planets only — keeps the system readable)
      if (b.kind === 'planet') {
        const ring = MeshBuilder.CreateTorus(`orbitRing_${b.planetIdx}`, {
          diameter: pl.orbitR * 2,
          thickness: 0.5,
          tessellation: 96,
        }, this.scene);
        const rmat = new StandardMaterial(`orbitMat_${b.planetIdx}`, this.scene);
        rmat.disableLighting = true;
        rmat.fogEnabled = false;
        rmat.emissiveColor = new Color3(0.25, 0.45, 0.6);
        rmat.alpha = 0.18;
        ring.material = rmat;
        ring.parent = this.spaceRoot;
        ring.isPickable = false;
      }

      this.spaceBodies.push({
        planetIdx: b.planetIdx,
        moonIdx: b.moonIdx,
        kind: b.kind,
        name: b.name,
        type: b.type,
        mesh,
        scaledRadius: scaledR,
        worldPos: new Vector3(bx, 0, bz),
        isCurrent: !!b.isCurrent,
      });
    }

    // --- Space starfield (separate from surface skybox stars) ---
    // Built as a large inverted sphere of emissive dots so the void doesn't
    // read as flat black. Parented to spaceRoot so it toggles with space mode.
    const spaceStarParent = new TransformNode('spaceStarParent', this.scene);
    spaceStarParent.parent = this.spaceRoot;
    const SPACE_STAR_COUNT = 380;
    const SPACE_STAR_RADIUS = 18000;
    for (let i = 0; i < SPACE_STAR_COUNT; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = SPACE_STAR_RADIUS;
      const sx = r * Math.sin(phi) * Math.cos(theta);
      const sy = r * Math.cos(phi);
      const sz = r * Math.sin(phi) * Math.sin(theta);
      const star = MeshBuilder.CreateSphere(`spaceStarBg_${i}`, {
        diameter: 18 + Math.random() * 26,
        segments: 4,
      }, this.scene);
      star.position = new Vector3(sx, sy, sz);
      const sm = new StandardMaterial(`spaceStarBgMat_${i}`, this.scene);
      sm.disableLighting = true;
      sm.fogEnabled = false;
      // Slight tint variety
      const tintR = 0.85 + Math.random() * 0.15;
      const tintG = 0.85 + Math.random() * 0.15;
      const tintB = 0.95 + Math.random() * 0.05;
      sm.emissiveColor = new Color3(tintR, tintG, tintB);
      star.material = sm;
      star.isPickable = false;
      star.parent = spaceStarParent;
    }

    // --- Orbital trading stations ---
    // 1-2 deterministic stations per system. Players can dock to sell from
    // BOTH their personal backpack and the ship's cargo hold.
    this._buildSpaceStations();
  }

  _buildSpaceStations() {
    this.spaceStations = [];
    const coords = this.body?.coords || '0:0:0:0:0';
    // Seed station count + positions from the system coords so the same
    // system always has the same stations.
    const seed = this._hashStringToInt(`station-${coords}`);
    const rand = this._mulberry32(seed);
    const count = 1 + (rand() < 0.45 ? 1 : 0); // 1 or 2 stations
    const namesPool = ['Helios', 'Vanguard', 'Argo', 'Pioneer', 'Tartarus', 'Atlas', 'Nyx', 'Lyra'];
    // Choose a baseline orbit radius — slightly outside the inner planet ring.
    const orbitBase = 520;
    for (let i = 0; i < count; i++) {
      const angle = rand() * Math.PI * 2;
      const radius = orbitBase + i * 280 + (rand() - 0.5) * 80;
      const yOff = (rand() - 0.5) * 60;
      const px = Math.cos(angle) * radius;
      const pz = Math.sin(angle) * radius;
      const stationId = `${coords}:${i}`;
      const name = `${namesPool[Math.floor(rand() * namesPool.length)]} Station`;
      const station = this._buildStationMesh(stationId, name, new Vector3(px, yOff, pz));
      this.spaceStations.push(station);
    }
  }

  _buildStationMesh(id, name, position) {
    const root = new TransformNode(`station_${id}`, this.scene);
    root.position = position;
    root.parent = this.spaceRoot;

    // Outer rotating habitation ring
    const ring = MeshBuilder.CreateTorus(`stationRing_${id}`, {
      diameter: 56, thickness: 6, tessellation: 36,
    }, this.scene);
    ring.parent = root;
    ring.isPickable = false;
    const rmat = new StandardMaterial(`stationRingMat_${id}`, this.scene);
    rmat.diffuseColor = new Color3(0.62, 0.64, 0.70);
    rmat.specularColor = new Color3(0.4, 0.42, 0.50);
    rmat.specularPower = 80;
    rmat.emissiveColor = new Color3(0.08, 0.09, 0.12);
    ring.material = rmat;

    // Central spindle/hub
    const hub = MeshBuilder.CreateCylinder(`stationHub_${id}`, {
      diameterTop: 8, diameterBottom: 8, height: 38, tessellation: 18,
    }, this.scene);
    hub.parent = root;
    hub.isPickable = false;
    const hubMat = new StandardMaterial(`stationHubMat_${id}`, this.scene);
    hubMat.diffuseColor = new Color3(0.50, 0.52, 0.58);
    hubMat.specularColor = new Color3(0.35, 0.35, 0.40);
    hubMat.emissiveColor = new Color3(0.06, 0.07, 0.10);
    hub.material = hubMat;

    // Docking pods (4 little boxes around the hub for visual interest)
    for (let i = 0; i < 4; i++) {
      const pod = MeshBuilder.CreateBox(`stationPod_${id}_${i}`, {
        width: 12, height: 6, depth: 6,
      }, this.scene);
      const a = (i / 4) * Math.PI * 2;
      pod.position = new Vector3(Math.cos(a) * 28, 0, Math.sin(a) * 28);
      pod.rotation.y = -a;
      pod.parent = root;
      pod.isPickable = false;
      const pm = new StandardMaterial(`stationPodMat_${id}_${i}`, this.scene);
      pm.diffuseColor = new Color3(0.45, 0.48, 0.55);
      pm.emissiveColor = new Color3(0.05, 0.06, 0.08);
      pod.material = pm;
    }

    // Beacon lights — emissive red/blue spheres that pulse via tickSpace
    const beaconA = MeshBuilder.CreateSphere(`stationBeaconA_${id}`, { diameter: 2.2, segments: 8 }, this.scene);
    beaconA.parent = root;
    beaconA.position = new Vector3(0, 22, 0);
    const baMat = new StandardMaterial(`stationBeaconAMat_${id}`, this.scene);
    baMat.disableLighting = true;
    baMat.emissiveColor = new Color3(1, 0.25, 0.25);
    beaconA.material = baMat;
    if (this.glow) {
      try { this.glow.addIncludedOnlyMesh && this.glow.addIncludedOnlyMesh(beaconA); } catch (_e) { /* noop */ }
    }
    const beaconB = MeshBuilder.CreateSphere(`stationBeaconB_${id}`, { diameter: 2.2, segments: 8 }, this.scene);
    beaconB.parent = root;
    beaconB.position = new Vector3(0, -22, 0);
    const bbMat = new StandardMaterial(`stationBeaconBMat_${id}`, this.scene);
    bbMat.disableLighting = true;
    bbMat.emissiveColor = new Color3(0.3, 0.6, 1);
    beaconB.material = bbMat;

    // Tilt + initial orientation: random Z roll so stations look distinct
    root.rotation.z = (this._hashStringToInt(id) % 360) * (Math.PI / 180);

    return {
      id,
      name,
      root,
      ring,
      worldPos: position.clone(),
      beaconA,
      beaconB,
      beaconPhase: Math.random() * Math.PI * 2,
      dockRadius: 60,  // ship "near" range
    };
  }

  _spaceBodyHudList() {
    const layout = this.body?.systemLayout;
    if (!layout?.bodies) return [];
    return layout.bodies.map((b) => ({
      kind: b.kind,
      name: b.name,
      type: b.type,
      planetIdx: b.planetIdx,
      moonIdx: b.moonIdx,
      isCurrent: !!b.isCurrent,
    }));
  }

  _currentBodySpacePosition() {
    // Find the current body's space-position so we can teleport the ship there
    // when launching to orbit.
    if (!this.spaceBodies) return new Vector3(this._auToUnits || 600, 0, 0);
    const cur = this.spaceBodies.find((b) => b.isCurrent);
    if (cur) return cur.worldPos.clone();
    // Fallback: first planet
    const first = this.spaceBodies.find((b) => b.kind === 'planet');
    return first ? first.worldPos.clone() : new Vector3(this._auToUnits || 600, 0, 0);
  }

  enterSpaceMode() {
    if (this._disposed) return;
    if (this.mode === 'space') return;
    if (!this.state.inShip) return;
    if (!this.spaceRoot || !this.spaceBodies || this.spaceBodies.length === 0) return;
    this.mode = 'space';
    this.state.mode = 'space';

    // Show space scene, hide surface scene
    this.spaceRoot.setEnabled(true);
    if (this.spaceLight) this.spaceLight.setEnabled(true);
    if (this.ground) this.ground.setEnabled(false);
    if (this.waterPlane) this.waterPlane.setEnabled(false);
    if (this._featureMeshes) {
      for (const m of this._featureMeshes) try { m.setEnabled(false); } catch (_e) { /* noop */ }
    }
    if (this.cloudMeshes) {
      for (const m of this.cloudMeshes) try { m.setEnabled(false); } catch (_e) { /* noop */ }
    }
    if (this.sunMesh) this.sunMesh.setEnabled(false);
    if (this.skyMoons) for (const m of this.skyMoons) try { m.mesh.setEnabled(false); } catch (_e) { /* noop */ }
    if (this.parentPlanetMesh) this.parentPlanetMesh.setEnabled(false);
    // Hide the planet's atmospheric skybox + its starfield/sun (parented to skyAnchor).
    if (this.skybox) this.skybox.setEnabled(false);
    if (this.skyAnchor) this.skyAnchor.setEnabled(false);
    // Phase 12a — surface biome haze off in vacuum; asteroid field on.
    if (this.effects) {
      this._spaceBiomeKindCache = this.effects._biomeHaze?.kind || null;
      this.effects.setBiomeHaze(null);
    }
    if (this._asteroidField) {
      try { this._asteroidField.parent.setEnabled(true); } catch (_e) { /* noop */ }
    }
    // Phase 12b — ambient NPC ships visible while in space
    try { this.npcs?.setAmbientShipsEnabled(true); } catch (_e) { /* noop */ }
    // Switch clear color to deep space and kill fog (vacuum)
    this._surfaceClearColor = this.scene.clearColor?.clone?.() || null;
    this._surfaceFogDensity = this.scene.fogDensity;
    this._surfaceFogMode = this.scene.fogMode;
    this.scene.clearColor = new Color4(0.008, 0.012, 0.035, 1); // near-black with faint blue
    this.scene.fogMode = Scene.FOGMODE_NONE;
    this.scene.fogDensity = 0;
    // Dim hemisphere/sun lighting (point light from star will take over)
    if (this.sun) this.sun.intensity = 0.0;
    const hemi = this.scene.getLightByName('hemi');
    if (hemi) {
      this._surfaceHemiIntensity = hemi.intensity;
      hemi.intensity = 0.08;
    }

    // Teleport the ship to a launch position just outside the current body
    const bodyPos = this._currentBodySpacePosition();
    const cur = this.spaceBodies.find((b) => b.isCurrent);
    const offset = (cur?.scaledRadius || 50) + 60;
    // Position slightly outward (away from star), with the ship aimed back at the star
    const outwardDir = bodyPos.clone().normalize();
    // Guard against zero-length normalization (current body at origin)
    if (!isFinite(outwardDir.x) || outwardDir.lengthSquared() < 0.0001) {
      outwardDir.set(1, 0, 0);
    }
    const launchPos = bodyPos.add(outwardDir.scale(offset));
    this.shipPos = new Vector3(launchPos.x, launchPos.y + offset * 0.4, launchPos.z);
    this.shipVel = new Vector3(0, 0, 0);
    // Aim ship toward the star (negative outward direction)
    this.shipYaw = Math.atan2(-outwardDir.x, -outwardDir.z);
    this.shipPitch = 0;
    this.shipRoll = 0;
    this.shipThrottle = 0;
    this.shipRoot.position = this.shipPos.clone();
    this.shipRoot.rotation = new Vector3(this.shipPitch, this.shipYaw, this.shipRoll);

    // Snap chase camera to new pose
    const cy = Math.cos(this.shipYaw), sy = Math.sin(this.shipYaw);
    this.flightCamera.position = new Vector3(this.shipPos.x - sy * 18, this.shipPos.y + 7, this.shipPos.z - cy * 18);
    this.flightCamera.setTarget(new Vector3(this.shipPos.x + sy * 6, this.shipPos.y + 1.4, this.shipPos.z + cy * 6));
    // Honour the player's current ship camera preference (cockpit vs chase)
    this._applyShipCameraMode();
    this.state.canLaunch = false;
    this.state.approachTarget = null;
    // Space-combat encounters are now RARE — only a 1-in-50 chance of an
    // ambush each time you launch into space. Most launches are peaceful.
    try {
      const ambush = Math.random() < (1 / 50);
      this.combat?.setShipCombatEnabled(ambush, ambush ? {
        center: this.shipPos.clone(),
        seed: (this._terrainSeed | 0) ^ 0x517a8c,
        count: 3,
        radiusMin: 600,
        radiusMax: 1100,
      } : {});
    } catch (_e) { /* noop */ }
    this._emit();
  }

  landOnTarget() {
    // Triggered by HUD / E key when an approach target is locked.
    if (this._disposed || this.mode !== 'space' || !this.state.approachTarget) return;
    const target = this.state.approachTarget;
    try { this.onLandRequest(target); } catch (_e) { /* noop */ }
  }

  _setupInput() {
    this.keys = { w: false, a: false, s: false, d: false, shift: false, space: false, ctrl: false, x: false };
    this._pointerLocked = false;
    this._pointerLockUnavailable = false; // becomes true after a SecurityError (sandboxed iframe)
    this._dragLook = false;                // active while user holds mouse button to look (fallback)
    this._inputPaused = false;             // when true, ignore keyboard input (e.g. while inventory overlay open)

    this._onKeyDown = (e) => {
      if (this._disposed) return;
      if (this._inputPaused) return;       // overlay (inventory etc.) has the focus
      const k = e.key.toLowerCase();
      if (k === 'w' || k === 'arrowup') this.keys.w = true;
      if (k === 's' || k === 'arrowdown') this.keys.s = true;
      if (k === 'a' || k === 'arrowleft') this.keys.a = true;
      if (k === 'd' || k === 'arrowright') this.keys.d = true;
      if (k === 'shift') this.keys.shift = true;
      if (k === ' ') { this.keys.space = true; e.preventDefault?.(); }
      if (k === 'control') this.keys.ctrl = true;
      if (k === 'x') this.keys.x = true;
      if (k === 'v') this.toggleCameraMode();
      if (k === 'l') this.toggleFlashlight();
      if (k === 'e') {
        // Station docking + in-station interactions are owned by the React HUD
        // layer (enterStation / trade terminal / airlock). Bail here so we
        // never run toggleShip()/exitShip() on the same keypress and fight it.
        if (this._inStation) {
          // no-op: React handles trade terminal + airlock
        } else if (this._inDerelict) {
          // no-op: React handles loot / log / exit-hatch
        } else if (this.state?.nearDerelict) {
          // no-op: React handles boarding the wreck (surface on foot OR space)
        } else if (this.mode === 'space' && this.state?.nearStation && this.state.inShip) {
          // no-op: React handles docking into the station interior
        } else if (this.mode === 'space' && this.state?.approachTarget) {
          this.landOnTarget();
        } else {
          this.toggleShip();
        }
      }
      if (k === 'escape' && this.state.inShip) this.exitShip();
      // Phase 7 — weapon controls (only on foot, surface, alive)
      if (this.combat && !this.state.inShip && (this.mode !== 'space' || this._inDerelict)) {
        if (k === 'f') {
          this.combat.fire();
        }
        if (k === 'r') {
          this.combat.reload();
        }
      }
      // Ship weapon controls (only while piloting in space)
      if (this.combat && this.state.inShip && this.mode === 'space') {
        if (k === 'f' || k === ' ') {
          // Tap-fire on F (or fallback Space — though Space is ascend, only
          // F is reliable; Space is intentionally consumed by the ship
          // ascend control via this.keys.space).
          if (k === 'f') this.combat.fireShipLaser();
        }
      }
    };
    this._onKeyUp = (e) => {
      if (this._disposed) return;
      // Allow keyups even while paused so we don't leave keys "stuck" on resume.
      const k = e.key.toLowerCase();
      if (k === 'w' || k === 'arrowup') this.keys.w = false;
      if (k === 's' || k === 'arrowdown') this.keys.s = false;
      if (k === 'a' || k === 'arrowleft') this.keys.a = false;
      if (k === 'd' || k === 'arrowright') this.keys.d = false;
      if (k === 'shift') this.keys.shift = false;
      if (k === ' ') this.keys.space = false;
      if (k === 'control') this.keys.ctrl = false;
      if (k === 'x') this.keys.x = false;
    };

    // Mouse-look:
    //   * Preferred: Pointer Lock (continuous, full-screen mouse capture)
    //   * Fallback:  Click-and-drag (works in sandboxed iframes where Pointer Lock is blocked)
    this._lastDragX = 0;
    this._lastDragY = 0;

    this._onMouseMove = (e) => {
      if (this._disposed) return;
      // Phase 10 — user-tunable mouse sensitivity + invert-Y (set via setMouseConfig).
      const sensMult = this._mouseSensitivity ?? 1.0;
      const invertY = !!this._invertY;
      const sensWalk = 0.0025 * sensMult;
      const sensFly = 0.0018 * sensMult; // a touch slower for the ship to feel weighty
      let dx = 0, dy = 0;
      if (this._pointerLocked) {
        dx = e.movementX || 0;
        dy = e.movementY || 0;
      } else if (this._dragLook) {
        dx = e.clientX - this._lastDragX;
        dy = e.clientY - this._lastDragY;
        if (Math.abs(dx) > 1 || Math.abs(dy) > 1) this._dragMoved = true;
        this._lastDragX = e.clientX;
        this._lastDragY = e.clientY;
      } else {
        return;
      }
      if (this.state.inShip) {
        this.shipYaw += dx * sensFly;
        this.shipPitch += dy * sensFly * (invertY ? -1 : 1);
        // Limit pitch so the ship doesn't flip
        const plim = Math.PI / 3;
        if (this.shipPitch > plim) this.shipPitch = plim;
        if (this.shipPitch < -plim) this.shipPitch = -plim;
      } else {
        this.yaw += dx * sensWalk;
        this.pitch -= dy * sensWalk * (invertY ? -1 : 1);
        const lim = Math.PI / 2 - 0.05;
        if (this.pitch > lim) this.pitch = lim;
        if (this.pitch < -lim) this.pitch = -lim;
      }
    };

    this._onPointerLockChange = () => {
      this._pointerLocked = document.pointerLockElement === this.canvas;
      this._emit();
    };

    this._onPointerLockError = () => {
      // Most likely sandboxed iframe without allow-pointer-lock
      this._pointerLockUnavailable = true;
      this._pointerLocked = false;
      this._emit();
    };

    this._onMouseDown = (e) => {
      if (this._disposed) return;
      // Right-button — mining intent (hold to drill)
      if (e.button === 2) {
        e.preventDefault?.();
        if (!this.state.inShip && this.mode !== 'space') {
          this.setMiningIntent(true);
        }
        return;
      }
      // Only left button starts drag-look / fire-tap
      if (e.button !== 0) return;
      // While pointer-locked: left click is always "fire"
      if (this._pointerLocked) {
        if (this.combat) {
          if (this.state.inShip && this.mode === 'space') {
            this.combat.fireShipLaser();
          } else if (!this.state.inShip && (this.mode !== 'space' || this._inDerelict)) {
            this.combat.fire();
          }
        }
        return;
      }
      // Otherwise begin a drag-look but remember the start for a possible "tap to fire".
      this._dragLook = true;
      this._dragStartX = e.clientX;
      this._dragStartY = e.clientY;
      this._dragStartT = performance.now();
      this._dragMoved = false;
      this._lastDragX = e.clientX;
      this._lastDragY = e.clientY;
      this.canvas.style.cursor = 'grabbing';
    };

    this._onMouseUp = (e) => {
      if (e && e.button === 2) {
        // Release mining intent
        this.setMiningIntent(false);
        return;
      }
      if (this._dragLook) {
        this._dragLook = false;
        this.canvas.style.cursor = this._pointerLockUnavailable ? 'grab' : 'crosshair';
        // If it was effectively a tap (short + no movement), fire.
        const dt = performance.now() - (this._dragStartT || 0);
        if (!this._dragMoved && dt < 220 && this.combat) {
          if (this.state.inShip && this.mode === 'space') {
            this.combat.fireShipLaser();
          } else if (!this.state.inShip && (this.mode !== 'space' || this._inDerelict)) {
            this.combat.fire();
          }
        }
      }
    };

    // Suppress the browser context menu so RMB can hold for mining
    this._onContextMenu = (e) => { e.preventDefault?.(); return false; };

    this._onCanvasClick = () => {
      if (this._pointerLocked) return;
      if (this._pointerLockUnavailable) return; // already in drag-look mode
      // Try pointer lock; if blocked (sandboxed iframe), drop to drag-look mode
      try {
        const req = this.canvas.requestPointerLock?.();
        if (req && typeof req.catch === 'function') {
          req.catch(() => {
            this._pointerLockUnavailable = true;
            this._emit();
          });
        }
      } catch (err) {
        this._pointerLockUnavailable = true;
        this._emit();
      }
    };

    // Initial cursor hint
    this.canvas.style.cursor = 'crosshair';

    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mouseup', this._onMouseUp);
    document.addEventListener('pointerlockchange', this._onPointerLockChange);
    document.addEventListener('pointerlockerror', this._onPointerLockError);
    this.canvas.addEventListener('click', this._onCanvasClick);
    this.canvas.addEventListener('mousedown', this._onMouseDown);
    this.canvas.addEventListener('contextmenu', this._onContextMenu);
  }

  // ---------- public API ----------

  toggleCameraMode() {
    if (this.state && this.state.inShip) {
      this.shipCameraMode = this.shipCameraMode === 'fps' ? 'tps' : 'fps';
      this._applyShipCameraMode();
      this._emit();
      return;
    }
    this.cameraMode = this.cameraMode === 'fps' ? 'tps' : 'fps';
    this._applyCameraMode();
    this._emit();
  }

  setCameraMode(mode) {
    if (mode !== 'fps' && mode !== 'tps') return;
    if (this.state && this.state.inShip) {
      this.shipCameraMode = mode;
      this._applyShipCameraMode();
    } else {
      this.cameraMode = mode;
      this._applyCameraMode();
    }
    this._emit();
  }

  /** Toggle the player flashlight on/off (L key or HUD button). */
  toggleFlashlight() {
    this._flashlightOn = !this._flashlightOn;
    if (this.flashlight) this.flashlight.setEnabled(this._flashlightOn);
    if (this._flashlightPool) this._flashlightPool.setEnabled(this._flashlightOn);
    if (this._flashlightOn) {
      // Guarantee EVERY surface currently in the scene (terrain, rocks, trees,
      // the ship, player-built structures, cities) can be lit by the flashlight
      // — Babylon caps materials at 4 lights by default and drops the rest.
      this._raiseAllMaterialLightCaps();
      this._updateFlashlight();
    }
    if (this.state) this.state.flashlightOn = this._flashlightOn;
    this._emit();
  }

  /** Raise maxSimultaneousLights on every existing scene material so added
   *  lights (flashlight, floodlights, habitat lamps) actually illuminate them. */
  _raiseAllMaterialLightCaps(min = 8) {
    const mats = this.scene.materials || [];
    for (const mat of mats) {
      if (mat && typeof mat.maxSimultaneousLights === 'number' && mat.maxSimultaneousLights < min) {
        mat.maxSimultaneousLights = min;
      }
      // Multi-material (e.g. some imported meshes) — bump children too.
      if (mat && Array.isArray(mat.subMaterials)) {
        for (const sub of mat.subMaterials) {
          if (sub && typeof sub.maxSimultaneousLights === 'number' && sub.maxSimultaneousLights < min) {
            sub.maxSimultaneousLights = min;
          }
        }
      }
    }
  }

  /** Keep the flashlight glued to the active camera's position + view dir. */
  _updateFlashlight() {
    if (!this.flashlight || !this._flashlightOn) return;
    const cam = this.scene.activeCamera;
    if (!cam) return;
    const pos = cam.globalPosition || cam.position;
    this.flashlight.position.copyFrom(pos);
    let fwd = null;
    const ray = cam.getForwardRay ? cam.getForwardRay() : null;
    if (ray && ray.direction) {
      fwd = ray.direction;
      this.flashlight.direction.copyFrom(fwd);
    }
    // Project a light pool onto the ground a few metres ahead of the aim.
    if (this._flashlightPool && fwd) {
      const reach = 7;
      const px = pos.x + fwd.x * reach;
      const pz = pos.z + fwd.z * reach;
      const gy = this._walkGroundHeight(px, pz);
      this._flashlightPool.position.set(px, gy + 0.12, pz);
    }
  }

  toggleShip() {
    if (this.state.inShip) {
      this.exitShip();
    } else if (this.state.nearShip) {
      this.enterShip();
    }
  }

  enterShip() {
    if (this.state.inShip) return;
    this.state.inShip = true;
    // Take off from current ship position; preserve heading.
    this.shipPos = this.shipRoot.position.clone();
    const sgY = this._terrainHeight ? this._terrainHeight(this.shipPos.x, this.shipPos.z) : 0;
    this.shipPos.y = Math.max(this.shipPos.y, sgY);
    this.shipVel = new Vector3(0, 0, 0);
    this.shipYaw = this.shipRoot.rotation.y || 0;
    this.shipPitch = 0;
    this.shipRoll = 0;
    this.shipThrottle = 0;
    this.shipLanded = this.shipPos.y - sgY < 0.5;
    // Hide astronaut while piloting
    this.astronautMeshes.forEach((m) => { m.isVisible = false; });
    // Initial chase camera position
    const cy = Math.cos(this.shipYaw), sy = Math.sin(this.shipYaw);
    this.flightCamera.position = new Vector3(
      this.shipPos.x - sy * 10,
      this.shipPos.y + 4,
      this.shipPos.z - cy * 10
    );
    this.flightCamera.setTarget(new Vector3(this.shipPos.x + sy * 2, this.shipPos.y + 1.2, this.shipPos.z + cy * 2));
    // Apply selected ship camera mode (TPS chase or FPS cockpit)
    this._applyShipCameraMode();
    this.state.shipSpeed = 0;
    this.state.shipAltitude = this.shipPos.y;
    this.state.shipThrottle = 0;
    this.state.shipBoost = false;
    this.state.canExit = true;
    this._emit();
  }

  exitShip() {
    if (!this.state.inShip) return;
    if (this.mode === 'space') {
      // Cannot disembark in space — must land on a body first
      this.state.exitDenied = true;
      this._emit();
      setTimeout(() => {
        if (this.state) { this.state.exitDenied = false; this._emit(); }
      }, 1800);
      return;
    }
    // Allow exit only when landed (or very close to ground) and nearly still.
    const speed = this.shipVel ? this.shipVel.length() : 0;
    const shipGroundY = this._terrainHeight ? this._terrainHeight(this.shipPos.x, this.shipPos.z) : 0;
    const alt = this.shipPos ? this.shipPos.y - shipGroundY : 0;
    const canExit = (alt < 2.5 && speed < 2.5) || this.shipLanded;
    if (!canExit) {
      this.state.exitDenied = true;
      this._emit();
      // Clear flag shortly so HUD can flash a message
      setTimeout(() => {
        if (this.state) { this.state.exitDenied = false; this._emit(); }
      }, 1800);
      return;
    }
    // Snap ship back to ground for clean disembark visuals
    this.shipPos.y = shipGroundY;
    this.shipPitch = 0;
    this.shipRoll = 0;
    this.shipVel = new Vector3(0, 0, 0);
    this.shipRoot.position = this.shipPos.clone();
    this.shipRoot.rotation = new Vector3(0, this.shipYaw, 0);

    this.state.inShip = false;
    // Reparent FPS camera back to player root at head height
    this.fpsCamera.parent = this.playerRoot;
    this.fpsCamera.position = new Vector3(0, 1.7, 0);
    // Restore any cockpit occluder meshes that were hidden in ship-FPS view
    if (this._cockpitOccluders) {
      for (const m of this._cockpitOccluders) {
        try { if (m) m.isVisible = true; } catch (_e) { /* noop */ }
      }
    }
    // Place astronaut a few steps from ship's right side, accounting for ship heading
    const cyaw = Math.cos(this.shipYaw), syaw = Math.sin(this.shipYaw);
    const sideOffset = 3.5;
    const backOffset = -2.0;
    const ox = cyaw * sideOffset + syaw * backOffset;
    const oz = -syaw * sideOffset + cyaw * backOffset;
    const exitX = this.shipPos.x + ox;
    const exitZ = this.shipPos.z + oz;
    const exitY = this._terrainHeight ? this._terrainHeight(exitX, exitZ) : 0;
    this.playerRoot.position = new Vector3(exitX, exitY, exitZ);
    this.playerY = exitY;
    this.playerVY = 0;
    this.grounded = true;
    // Face the ship
    this.yaw = Math.atan2(this.shipPos.x - this.playerRoot.position.x, this.shipPos.z - this.playerRoot.position.z);
    this.pitch = 0;
    this._applyCameraMode();
    this._emit();
  }

  getState() {
    return { ...this.state, body: this.body };
  }

  setInputPaused(paused) {
    // Called by React when an overlay (e.g. inventory) takes the focus.
    // Clears any held movement keys so the player doesn't drift on resume.
    this._inputPaused = !!paused;
    if (paused && this.keys) {
      this.keys.w = false;
      this.keys.a = false;
      this.keys.s = false;
      this.keys.d = false;
      this.keys.shift = false;
      this.keys.space = false;
      this.keys.ctrl = false;
      this.keys.x = false;
    }
  }

  // ---------- Phase 7 — Combat ----------

  _setupCombat() {
    this.combat = new CombatSystem(this.scene, {
      getPlayerPos: () => this.playerRoot.position,
      getPlayerForward: () => this._getPlayerForward(),
      getCameraPos: () => {
        const cam = this.scene.activeCamera;
        return (cam?.globalPosition || cam?.position || this.playerRoot.position).clone();
      },
      getTerrainHeight: (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0),
      getBodyType: () => (this.body?.kind === 'moon' ? 'Moon' : (this.body?.type || 'Default')),
      getBodyHabitability: () => Number(this.body?.habitability || 0),
      getBodySeed: () => this._terrainSeed | 0,
      isCombatActive: () => (
        !this._disposed
        && !this.state.inShip
        && (this.mode !== 'space' || this._inDerelict)
        && !this._inputPaused
      ),
      // ---- Ship combat (space) hooks ----
      getShipPos: () => (this.shipRoot?.position || this.playerRoot.position).clone(),
      getShipForward: () => this._getShipForward(),
      getShipMuzzles: () => this._getShipMuzzles(),
      isShipCombatActive: () => (
        !this._disposed
        && !!this.state?.inShip
        && this.mode === 'space'
        && !this._inputPaused
      ),
      onWeaponState: (w) => {
        if (!this.state) return;
        this.state.weapon = w;
        this._emit();
      },
      onEnemiesChanged: (pois) => {
        if (!this.state) return;
        this.state.enemyPois = pois;
        this._emit();
      },
      onDamageTaken: (info) => {
        // Mirror to public state and bubble up to React for the vignette + autosave
        if (!this.state) return;
        this.state.lastDamageAt = Date.now();
        this.state.hp = info.hp;
        this.state.shield = info.shield;
        this._emit();
        try { this.onDamageTaken(info); } catch (_e) { /* noop */ }
      },
      onVitalsRegen: (info) => {
        // Throttled push while shield/HP are regenerating so the HUD bars
        // animate back up smoothly. Also forwarded to React for autosave.
        if (!this.state) return;
        this.state.hp = info.hp;
        this.state.shield = info.shield;
        this._emit();
        try { this.onVitalsRegen?.(info); } catch (_e) { /* noop */ }
      },
      onKill: (info) => {
        if (this.effects && info?.pos) {
          const p = new Vector3(info.pos.x, info.pos.y, info.pos.z);
          if (this.graphicsQuality === 'low') {
            try { this.effects.spawnImpact(p, '#ffa64a', 22); } catch (_e) { /* noop */ }
          } else {
            const sc = info.elite ? 1.8 : 1.0;
            try { this.effects.spawnExplosion(p, '#ffa64a', sc); } catch (_e) { /* noop */ }
          }
        }
        try { this.onKill(info); } catch (_e) { /* noop */ }
      },
      onFire: (info) => {
        try { this.onFire?.(info); } catch (_e) { /* noop */ }
        if (this.effects && info?.pos) {
          try { this.effects.spawnMuzzleFlash(info.pos, info.dir || null, '#ff5a3a'); } catch (_e) { /* noop */ }
        }
      },
      onProjectileImpact: (info) => {
        if (this.effects && info?.pos) {
          if (info.kind === 'ship_explode' && this.graphicsQuality !== 'low') {
            try { this.effects.spawnExplosion(info.pos, '#ff7a3a', 1.6); } catch (_e) { /* noop */ }
          } else {
            // Enemy shots = orange; player shots = red (matches the red lasers).
            const color = info.owner === 'enemy' ? '#ff8a55' : '#ff5a3a';
            const cnt = info.kind === 'ship_explode' ? 30 : (info.kind === 'unit' ? 28 : 18);
            try { this.effects.spawnImpact(info.pos, color, cnt); } catch (_e) { /* noop */ }
          }
        }
      },
      onDeath: (info) => {
        if (!this.state) return;
        this.state.dead = true;
        this._emit();
        try { this.onDeath(info); } catch (_e) { /* noop */ }
      },
    });
  }

  /** Pushed from React when persisted vitals load / change. */
  setPlayerVitals(v) {
    if (this.combat) this.combat.setVitals(v);
    if (typeof v.hp === 'number') this.state.hp = v.hp;
    if (typeof v.shield === 'number') this.state.shield = v.shield;
    this._emit();
  }

  /** Pushed from React on respawn. Teleports the player next to the ship. */
  respawnPlayer({ hp = 70, shield = 250 } = {}) {
    if (this.combat) this.combat.respawn({ hp, shield });
    // Place player a few meters from the ship and face it
    if (this.shipRoot && this.playerRoot) {
      const sp = this.shipRoot.position;
      const exitX = sp.x + 3.0;
      const exitZ = sp.z - 1.5;
      const gy = this._terrainHeight ? this._terrainHeight(exitX, exitZ) : 0;
      this.playerRoot.position = new Vector3(exitX, gy, exitZ);
      this.playerY = gy;
      this.playerVY = 0;
      this.grounded = true;
      this.yaw = Math.atan2(sp.x - exitX, sp.z - exitZ);
      this.pitch = 0;
    }
    this.state.dead = false;
    this.state.hp = hp;
    this.state.shield = shield;
    this._emit();
  }

  // ---------- Phase 8 — Mining ----------

  _setupMining() {
    this._miningElements = null; // populated when React supplies the catalog
    this.mining = new MiningSystem(this.scene, {
      getPlayerPos: () => this.playerRoot.position,
      getPlayerForward: () => {
        const cy = Math.cos(this.yaw), sy = Math.sin(this.yaw);
        const cp = Math.cos(this.pitch), sp = Math.sin(this.pitch);
        return new Vector3(sy * cp, sp, cy * cp).normalize();
      },
      getCameraPos: () => {
        const cam = this.scene.activeCamera;
        return (cam?.globalPosition || cam?.position || this.playerRoot.position).clone();
      },
      getTerrainHeight: (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0),
      getBodyType: () => (this.body?.kind === 'moon' ? 'Moon' : (this.body?.type || 'Default')),
      getBodySeed: () => (this._terrainSeed | 0) ^ 0xd3aded,
      isMiningAllowed: () => (
        !this._disposed
        && !this.state.inShip
        && this.mode !== 'space'
        && !this._inputPaused
        && !this.state.dead
      ),
      getElements: () => this._miningElements,
      getScannedElementNames: () => (this.body?.scannedElements || []),
      onMiningState: (snap) => {
        if (!this.state) return;
        this.state.mining = snap;
        this._emit();
      },
      onExtract: (info) => {
        try { this.onMiningExtract?.(info); } catch (_e) { /* noop */ }
      },
    });
    // Register any big boulders that were scattered before mining existed.
    if (this._lastBoulders) this.mining.setBoulderTargets(this._lastBoulders);
  }

  /** Called by React after fetching /api/mining/elements. */
  setMiningElements(elements) {
    this._miningElements = Array.isArray(elements) ? elements : null;
    // Invalidate any tiles that were cached as empty before the catalog
    // arrived so they re-stream with real element data.
    try { this.mining?.resetCache?.(); } catch (_e) { /* noop */ }
    // Re-register the already-scattered boulders: the initial registration
    // (during scene build) silently skipped every rock because the element
    // catalog hadn't arrived yet (getElements() returned null), which left the
    // surface with no mineable objects / radar deposit POIs until the next
    // terrain re-scatter or an engine reload.
    try {
      if (this.mining && this._lastBoulders?.length) {
        this.mining.setBoulderTargets(this._lastBoulders);
      }
    } catch (_e) { /* noop */ }
  }

  /** RMB pressed/released — drives the mining intent. */
  setMiningIntent(active) {
    if (this.mining) this.mining.setMining(!!active);
  }

  // ---------- Phase 9 — Cities & Settlements ----------

  _setupCity() {
    this.city = new CitySystem(this.scene, {
      getPlayerPos: () => this.playerRoot.position,
      getTerrainHeight: (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0),
      onCityProximity: (snap) => {
        if (!this.state) return;
        this.state.city = {
          ...(this.state.city || {}),
          ...snap,
          hasCity: true,
        };
        this._emit();
        // Bubble up so React can show a toast / focus the prompt
        try { this.onCityProximity?.(snap); } catch (_e) { /* noop */ }
      },
    });
  }

  /** React pushes the /api/city payload here when the body qualifies. */
  setCity(data) {
    if (!this.city) return;
    this.city.setCity(data);
    const s = this.city.getState();
    if (this.state) {
      this.state.city = s;
      this._emit();
    }
    // Phase 12b — spawn pedestrians around the plaza
    if (this.npcs && data) {
      const plaza = this.city.getPlazaWorld();
      const radius = this.city.getPlazaRadius();
      const seed = ((data.coords_key || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) + Number(data.planet_idx || 0) * 31) | 0;
      this.npcs.setupPedestrians(plaza, radius, seed);
    }
  }

  /** Clear the city when navigating to a body without one. */
  clearCity() {
    if (!this.city) return;
    this.city.clear();
    if (this.state) {
      this.state.city = { hasCity: false, nearCity: false, distance: 0, name: null };
      this._emit();
    }
    // Phase 12b — remove pedestrians along with the city
    try { this.npcs?.clearPedestrians(); } catch (_e) { /* noop */ }
  }

  // ---------- Phase 11 — Structures & Build Mode ----------

  _setupStructures() {
    this.structures = new StructuresSystem(this.scene, {
      getPlayerPos: () => this.playerRoot.position,
      getPlayerForward: () => this._getPlayerForward(),
      getCameraPos: () => this.camera.position,
      getTerrainHeight: (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0),
    });
  }

  /** Camera forward (yaw + pitch) on foot; ship forward when piloting. */
  _getPlayerForward() {
    if (this.state?.inShip) {
      const cy = Math.cos(this.shipYaw), sy = Math.sin(this.shipYaw);
      const cp = Math.cos(this.shipPitch), sp = Math.sin(this.shipPitch);
      return new Vector3(sy * cp, -sp, cy * cp).normalize();
    }
    const cy = Math.cos(this.yaw), sy = Math.sin(this.yaw);
    const cp = Math.cos(this.pitch), sp = Math.sin(this.pitch);
    return new Vector3(sy * cp, sp, cy * cp).normalize();
  }

  /** Pure ship forward vector from current shipYaw/Pitch (no foot fallback). */
  _getShipForward() {
    const cy = Math.cos(this.shipYaw || 0), sy = Math.sin(this.shipYaw || 0);
    const cp = Math.cos(this.shipPitch || 0), sp = Math.sin(this.shipPitch || 0);
    return new Vector3(sy * cp, -sp, cy * cp).normalize();
  }

  /**
   * Compute world-space positions of the twin wing-tip muzzles. Returns
   * [leftMuzzle, rightMuzzle] in world coordinates so the CombatSystem can
   * spawn projectiles at the correct origin regardless of ship pose.
   */
  _getShipMuzzles() {
    const pos = this.shipRoot?.position;
    if (!pos) return null;
    // Local-space wing-tip forward edges. Wings are at y≈1.4, the forward
    // edge sits near z≈0.3 (wing depth 1.6 centered at z=-0.5 → +0.3).
    // Wing-tip x = ±2.4 from the spaceship build.
    const local = [
      new Vector3(-2.4, 1.4, 0.6),
      new Vector3(2.4, 1.4, 0.6),
    ];
    const cy = Math.cos(this.shipYaw || 0), sy = Math.sin(this.shipYaw || 0);
    const cp = Math.cos(this.shipPitch || 0), sp = Math.sin(this.shipPitch || 0);
    // Apply pitch then yaw (matches ship root rotation order)
    return local.map((v) => {
      // pitch (x rotation)
      const y1 = v.y * cp - v.z * sp;
      const z1 = v.y * sp + v.z * cp;
      // yaw (y rotation)
      const x2 = v.x * cy + z1 * sy;
      const z2 = -v.x * sy + z1 * cy;
      return new Vector3(pos.x + x2, pos.y + y1, pos.z + z2);
    });
  }

  /** Push the persisted structures list for the current body. */
  setStructures(list) {
    if (!this.structures) return;
    this.structures.setStructures(list || []);
  }

  /** Add or update a single structure (after build). */
  upsertStructure(doc) {
    if (!this.structures) return;
    this.structures.upsertStructure(doc);
  }

  /** Remove a structure by id (after recycle). */
  removeStructure(id) {
    if (!this.structures) return;
    this.structures.removeStructure(id);
  }

  /** Enter build preview mode. */
  startBuildPreview(typeId) {
    if (!this.structures) return;
    this.structures.startBuildPreview(typeId);
    if (this.state) {
      this.state.build = { active: true, typeId, valid: false };
      this._emit();
    }
  }

  /** Exit build preview mode. */
  stopBuildPreview() {
    if (!this.structures) return;
    this.structures.stopBuildPreview();
    if (this.state) {
      this.state.build = { active: false, typeId: null, valid: false };
      this._emit();
    }
  }

  /** Commit the build at the current preview position. Returns payload
      to POST to /api/structures/build, or null if no valid spot. */
  commitBuild() {
    if (!this.structures) return null;
    const payload = this.structures.commitBuild();
    if (payload && this.effects) {
      try {
        this.effects.spawnBuildPoof(new Vector3(payload.x, payload.y + 0.6, payload.z));
      } catch (_e) { /* noop */ }
    }
    return payload;
  }

  // ---------- Phase 12a — Effects (particles + asteroids + biome haze) ----------

  _setupEffects() {
    this.effects = new EffectsSystem(this.scene, {
      getPlayerPos: () => this.playerRoot?.position || new Vector3(0, 0, 0),
    });
    // Ship exhaust — nozzle anchors come from the active ship's builder.
    if (this.shipRoot) {
      this.effects.attachShipExhaust(this.shipRoot, this._shipExhaustAnchors || [
        new Vector3(-1.2, 1.4, -3.85),
        new Vector3(0, 1.4, -3.85),
        new Vector3(1.2, 1.4, -3.85),
      ]);
    }
    // Biome haze keyed off the body type / atmosphere.
    this.effects.setBiomeHaze(this._biomeHazeKind());

    // Asteroid field for the system space view.
    if (this.spaceRoot && this.spaceBodies && this.spaceBodies.length > 0) {
      this._asteroidField = buildAsteroidField(this.scene, this.spaceRoot, {
        seed: this._terrainSeed,
        count: 80,
      });
      // Disabled until space mode is entered.
      try { this._asteroidField.parent.setEnabled(false); } catch (_e) { /* noop */ }
    }
  }

  /** Map body type / atmosphere onto a haze kind for EffectsSystem. */
  _biomeHazeKind() {
    if (this.mode === 'space') return null;
    const type = this.body?.kind === 'moon' ? 'Moon' : (this.body?.type || '');
    if (type === 'Lava') return 'lava';
    if (type === 'Ice Giant') return 'ice';
    if (this.atmosphere === 'toxic') return 'toxic';
    // Desert-ish dust for arid Terrestrial / Dwarf bodies with very low habitability
    if ((type === 'Dwarf' || type === 'Terrestrial')
        && (this.body?.habitability ?? 0) < 25
        && this.atmosphere !== 'none') {
      return 'desert';
    }
    return null;
  }

  // ---------- Phase 12b — NPC life ----------

  _setupNpcs() {
    this.npcs = new NpcSystem(this.scene, {
      getPlayerPos: () => this.playerRoot?.position || new Vector3(0, 0, 0),
      getTerrainHeight: (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0),
    });
    // Wildlife auto-spawns based on body type/habitability
    this.npcs.setWildlife(this.body);
    // Ambient ships for system-space (parented to spaceRoot, hidden until launch)
    if (this.spaceRoot && this.spaceBodies?.length > 0) {
      this.npcs.setupAmbientShips(this.spaceRoot, this._terrainSeed | 0);
      // Disabled at start; we enable it inside enterSpaceMode()
      this.npcs.setAmbientShipsEnabled(false);
    }
  }

  // ---------- Phase 13 (P3) — Survey anomalies ----------

  _setupAnomalies() {
    this.anomalies = new AnomalySystem(this.scene, {
      getPlayerPos: () => this.playerRoot?.position || new Vector3(0, 0, 0),
      getTerrainHeight: (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0),
    });
    // Only spawn anomalies on planets/moons with a surface (skip Gas Giant)
    this.anomalies.setBody(this.body);
  }

  /** React pushes the list of already-scanned anomaly ids for this body. */
  setScannedAnomalies(ids) {
    this.anomalies?.setScanned(ids || []);
  }

  /** Returns the anomaly currently within scan range, or null. */
  getNearAnomaly() {
    return this.anomalies?.getNearAnomaly?.() || null;
  }

  /** Locally mark an anomaly scanned (after the backend POST succeeds). */
  markAnomalyScanned(id) {
    this.anomalies?.markScanned?.(id);
  }

  // ---------- Batch B — Surface Discoverables (pods / debris / fossils) ----------

  _setupDiscoveries() {
    this.discoveries = new DiscoverySystem(this.scene, {
      getPlayerPos: () => this.playerRoot?.position || new Vector3(0, 0, 0),
      getTerrainHeight: (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0),
    });
    this.discoveries.setBody(this.body);
  }

  /** React pushes already-claimed discovery ids for this body. */
  setClaimedDiscoveries(ids) {
    this.discoveries?.setClaimed(ids || []);
  }

  /** Returns the discovery currently within claim range, or null. */
  getNearDiscovery() {
    return this.discoveries?.getNearDiscovery?.() || null;
  }

  /** Locally mark a discovery claimed (after the backend POST succeeds). */
  markDiscoveryClaimed(id) {
    this.discoveries?.markClaimed?.(id);
  }

  /** Phase 13 — returns full pedestrian bark for the nearest NPC, or null. */
  getNearPedestrianFull() {
    return this.npcs?.getNearPedestrian?.() || null;
  }

  // ---------- June 2026 — Underground Caves ----------

  _setupCaves() {
    this._inCave = false;
    this.caves = new CaveSystem(this.scene, {
      getPlayerPos: () => this.playerRoot?.position || new Vector3(0, 0, 0),
      getTerrainHeight: (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0),
      glow: this.glow || null,
    });
    // Scatter entrances on rocky bodies (skips Gas Giants internally).
    this.caves.setBody(this.body);
  }

  /** Ground-height source for the walking player — cave floor while inside. */
  /** Ground-height source for the walking player — interior floor while inside. */
  _walkGroundHeight(x, z) {
    if (this._inCave && this.caves) return this.caves.floorHeight(x, z);
    if (this._inDerelict && this.derelicts) return this.derelicts.floorHeight(x, z);
    if (this._inStation && this.stationInterior) return this.stationInterior.floorHeight(x, z);
    if (this._inShipInterior && this.shipInterior) return this.shipInterior.floorHeight(x, z);
    return this._terrainHeight ? this._terrainHeight(x, z) : 0;
  }

  /** Toggle every surface visual (ground, water, features, sky) on/off. */
  _setSurfaceVisible(v) {
    if (this.ground) this.ground.setEnabled(v);
    if (this.waterPlane) this.waterPlane.setEnabled(v);
    if (this._featureMeshes) {
      for (const m of this._featureMeshes) try { m.setEnabled(v); } catch (_e) { /* noop */ }
    }
    if (this.cloudMeshes) {
      for (const m of this.cloudMeshes) try { m.setEnabled(v); } catch (_e) { /* noop */ }
    }
    if (this.sunMesh) this.sunMesh.setEnabled(v);
    if (this.skyMoons) for (const m of this.skyMoons) try { m.mesh.setEnabled(v); } catch (_e) { /* noop */ }
    if (this.parentPlanetMesh) this.parentPlanetMesh.setEnabled(v);
    if (this.skybox) this.skybox.setEnabled(v);
    if (this.skyAnchor) this.skyAnchor.setEnabled(v);
    // Player-built structures, NPCs, anomalies, ship — only relevant on surface.
    try { this.shipRoot?.setEnabled(v); } catch (_e) { /* noop */ }
    try { this.structures?.setRootEnabled?.(v); } catch (_e) { /* noop */ }
    try { this.npcs?.setSurfaceEnabled?.(v); } catch (_e) { /* noop */ }
    try { this.anomalies?._root?.setEnabled?.(v); } catch (_e) { /* noop */ }
    try { this.caves?._surfaceRoot?.setEnabled?.(v); } catch (_e) { /* noop */ }
    try { this.derelicts?._markersRoot?.setEnabled?.(v); } catch (_e) { /* noop */ }
  }

  /** Descend into the cavern at the nearest surface entrance. */
  enterCave() {
    if (this._disposed || this._inCave) return;
    if (this.state.inShip || this.mode === 'space') return;
    if (!this.caves) return;
    const near = this.caves.getNearEntrance(6.5);
    if (!near) return;

    // Remember where to drop the player back on the surface.
    this._caveReturnPos = this.playerRoot.position.clone();
    this._caveReturnYaw = this.yaw;

    // Build the cavern keyed off this specific entrance.
    this.caves.buildInterior(this.caves.seedForEntrance(near.id));

    // Hide the surface, reveal the cavern.
    this._setSurfaceVisible(false);
    this.caves.showInterior(true);

    // Save + darken the environment (caves are dark — flashlight matters).
    this._caveSaved = {
      clearColor: this.scene.clearColor?.clone?.() || null,
      fogMode: this.scene.fogMode,
      fogDensity: this.scene.fogDensity,
      fogColor: this.scene.fogColor?.clone?.() || null,
    };
    this.scene.clearColor = new Color4(0.06, 0.06, 0.09, 1);
    this.scene.fogMode = Scene.FOGMODE_NONE;
    this.scene.fogDensity = 0;
    const hemi = this.scene.getLightByName('hemi');
    if (hemi) {
      this._caveSaved.hemiIntensity = hemi.intensity;
      hemi.intensity = 0.6;   // moderately lit cavern
    }
    if (this.sun) {
      this._caveSaved.sunIntensity = this.sun.intensity;
      this.sun.intensity = 0.0;
    }

    // Teleport the player onto the cavern floor, facing inward.
    const ep = this.caves.getEntryPoint();
    this.playerRoot.position = ep.clone();
    this.playerY = ep.y;
    this.playerVY = 0;
    this.grounded = true;
    this.yaw = Math.PI; // entry pad sits on the +Z rim → face the cavern centre
    this.pitch = 0;

    this._inCave = true;
    this.state.inCave = true;
    this.state.nearCaveEntrance = null;

    // Combat: explicit cave creatures only.
    if (this.combat) {
      this._caveOrigTerrainHeight = this.combat.getTerrainHeight;
      this.combat.getTerrainHeight = (x, z) => this.caves.floorHeight(x, z);
      this.combat.setCaveMode(true);
      this.combat.clearAllEnemies();
      // Creatures are rare — only a 1-in-50 chance any given cave is infested.
      if (Math.random() < (1 / 50)) {
        for (const s of this.caves.getCreatureSpawns()) {
          this.combat.spawnCaveEnemy(s.x, s.z);
        }
      }
    }

    // Mining: swap surface boulders for rich cave veins.
    if (this.mining) this.mining.setBoulderTargets(this.caves.getVeins());

    this._raiseAllMaterialLightCaps();
    this._emit();
  }

  /** Climb back out of the cavern to the surface entrance. */
  exitCave() {
    if (this._disposed || !this._inCave || !this.caves) return;

    this.caves.showInterior(false);
    this.caves.clearInterior();
    this._setSurfaceVisible(true);

    // Restore the saved surface environment.
    const s = this._caveSaved || {};
    if (s.clearColor) this.scene.clearColor = s.clearColor;
    if (typeof s.fogMode === 'number') this.scene.fogMode = s.fogMode;
    if (typeof s.fogDensity === 'number') this.scene.fogDensity = s.fogDensity;
    if (s.fogColor) this.scene.fogColor = s.fogColor;
    const hemi = this.scene.getLightByName('hemi');
    if (hemi && typeof s.hemiIntensity === 'number') hemi.intensity = s.hemiIntensity;
    if (this.sun && typeof s.sunIntensity === 'number') this.sun.intensity = s.sunIntensity;
    this._caveSaved = null;

    // Drop the player back at the entrance.
    const rp = this._caveReturnPos || new Vector3(0, 0, 0);
    const gy = this._terrainHeight ? this._terrainHeight(rp.x, rp.z) : 0;
    this.playerRoot.position = new Vector3(rp.x, gy, rp.z);
    this.playerY = gy;
    this.playerVY = 0;
    this.grounded = true;
    if (typeof this._caveReturnYaw === 'number') this.yaw = this._caveReturnYaw;

    this._inCave = false;
    this.state.inCave = false;
    this.state.nearCaveExit = null;

    // Combat: clear cave creatures, restore surface streaming + height source.
    if (this.combat) {
      this.combat.clearAllEnemies();
      this.combat.setCaveMode(false);
      if (this._caveOrigTerrainHeight) this.combat.getTerrainHeight = this._caveOrigTerrainHeight;
    }
    // Mining: restore the surface boulders for this body.
    if (this.mining) this.mining.setBoulderTargets(this._lastBoulders || []);

    this._emit();
  }

  /** True while the player is inside a cavern. */
  isInCave() { return !!this._inCave; }

  // ---------- Phase 3 — Derelict Ships & Shipwrecks ----------

  _setupDerelicts() {
    this._inDerelict = false;
    this._derelictId = null;
    this._derelictGuarded = false;
    this._derelictCleared = false;
    this.derelicts = new DerelictSystem(this.scene, {
      getPlayerPos: () => this.playerRoot?.position || new Vector3(0, 0, 0),
      glow: this.glow || null,
    });
  }

  /**
   * Push the backend-reported derelicts for the current context.
   *   location='surface' -> markers placed on the terrain
   *   location='space'   -> markers placed in the system view
   */
  setDerelicts(list, location = 'surface') {
    if (!this.derelicts || this._inDerelict) return;
    this.derelicts.setDerelicts(list || [], location,
      (x, z) => (this._terrainHeight ? this._terrainHeight(x, z) : 0));
    this.state.derelictPois = this.derelicts.getDerelictPois();
    this._emit();
  }

  /** Board the nearest wreck (on foot for surface, while piloting for space). */
  enterDerelict() {
    if (this._disposed || this._inDerelict || !this.derelicts) return;
    const near = this.derelicts.getNearDerelict();
    if (!near) return;
    const marker = this.derelicts.getMarker(near.id);
    if (!marker) return;
    const info = marker.info;

    this._derelictReturn = {
      mode: this.mode,
      inShip: this.state.inShip,
      pos: this.playerRoot.position.clone(),
      yaw: this.yaw,
    };
    this._derelictId = near.id;
    this._derelictGuarded = !!info.guarded;
    this._derelictCleared = !info.guarded || !!info.cleared;

    this.derelicts.buildInterior(info);

    // Hide the outside world (surface or space) and reveal the hull.
    this._setSurfaceVisible(false);
    if (this.spaceRoot) this.spaceRoot.setEnabled(false);
    try { this.shipRoot?.setEnabled(false); } catch (_e) { /* noop */ }
    if (this._asteroidField) { try { this._asteroidField.parent.setEnabled(false); } catch (_e) { /* noop */ } }
    this.derelicts.showInterior(true);

    // Save + set a dim, eerie interior environment.
    this._derelictSaved = {
      clearColor: this.scene.clearColor?.clone?.() || null,
      fogMode: this.scene.fogMode,
      fogDensity: this.scene.fogDensity,
      fogColor: this.scene.fogColor?.clone?.() || null,
    };
    this.scene.clearColor = new Color4(0.04, 0.05, 0.07, 1);
    this.scene.fogMode = Scene.FOGMODE_NONE;
    this.scene.fogDensity = 0;
    const hemi = this.scene.getLightByName('hemi');
    if (hemi) { this._derelictSaved.hemiIntensity = hemi.intensity; hemi.intensity = 0.45; }
    if (this.sun) { this._derelictSaved.sunIntensity = this.sun.intensity; this.sun.intensity = 0.0; }

    // Disembark on foot inside the hull.
    this.state.inShip = false;
    this.fpsCamera.parent = this.playerRoot;
    this.fpsCamera.position = new Vector3(0, 1.7, 0);
    if (this._cockpitOccluders) {
      for (const m of this._cockpitOccluders) { try { if (m) m.isVisible = true; } catch (_e) { /* noop */ } }
    }
    const ep = this.derelicts.getEntryPoint();
    this.playerRoot.position = ep.clone();
    this.playerY = ep.y;
    this.playerVY = 0;
    this.grounded = true;
    this.yaw = 0; // entry sits on the -Z (hatch) end → face the bridge (+Z)
    this.pitch = 0;
    this._applyCameraMode();

    this._inDerelict = true;
    this.state.inDerelict = true;
    this.state.derelictCleared = this._derelictCleared;
    this.state.nearDerelict = null;

    // Combat: explicit guard drones only (mirrors cave creature handling).
    if (this.combat) {
      this._derelictOrigTerrainHeight = this.combat.getTerrainHeight;
      this.combat.getTerrainHeight = (x, z) => this.derelicts.floorHeight(x, z);
      this.combat.setCaveMode(true);
      this.combat.clearAllEnemies();
      if (this._derelictGuarded && !this._derelictCleared) {
        const spawns = this.derelicts.getGuardSpawns();
        spawns.forEach((s, i) => this.combat.spawnCaveEnemy(s.x, s.z, { archetypeKey: 'drone', id: `guard_${i}` }));
      }
    }
    // No mining inside the wreck.
    if (this.mining) this.mining.setBoulderTargets([]);

    this.derelicts.setMarkerBoarded(near.id, true);
    this._raiseAllMaterialLightCaps?.();
    this._emit();
  }

  /** Leave the wreck and return to the surface / cockpit. */
  exitDerelict() {
    if (this._disposed || !this._inDerelict || !this.derelicts) return;

    this.derelicts.showInterior(false);
    this.derelicts.clearInterior();

    // Restore the saved environment.
    const s = this._derelictSaved || {};
    if (s.clearColor) this.scene.clearColor = s.clearColor;
    if (typeof s.fogMode === 'number') this.scene.fogMode = s.fogMode;
    if (typeof s.fogDensity === 'number') this.scene.fogDensity = s.fogDensity;
    if (s.fogColor) this.scene.fogColor = s.fogColor;
    const hemi = this.scene.getLightByName('hemi');
    if (hemi && typeof s.hemiIntensity === 'number') hemi.intensity = s.hemiIntensity;
    if (this.sun && typeof s.sunIntensity === 'number') this.sun.intensity = s.sunIntensity;
    this._derelictSaved = null;

    const ret = this._derelictReturn || { mode: 'surface' };

    // Combat: clear guards, restore surface streaming + height source.
    if (this.combat) {
      this.combat.clearAllEnemies();
      this.combat.setCaveMode(false);
      if (this._derelictOrigTerrainHeight) this.combat.getTerrainHeight = this._derelictOrigTerrainHeight;
    }
    if (this.mining) this.mining.setBoulderTargets(this._lastBoulders || []);

    this._inDerelict = false;
    this.state.inDerelict = false;
    this.state.nearDerelictExit = null;
    this.state.nearDerelictContainer = null;
    this.state.nearDerelictLog = null;

    if (ret.mode === 'space') {
      // Re-show the space scene + parked ship, re-board the ship.
      if (this.spaceRoot) this.spaceRoot.setEnabled(true);
      try { this.shipRoot?.setEnabled(true); } catch (_e) { /* noop */ }
      if (this._asteroidField) { try { this._asteroidField.parent.setEnabled(true); } catch (_e) { /* noop */ } }
      this.state.inShip = true;
      const cy = Math.cos(this.shipYaw || 0), sy = Math.sin(this.shipYaw || 0);
      if (this.flightCamera && this.shipPos) {
        this.flightCamera.position = new Vector3(this.shipPos.x - sy * 18, this.shipPos.y + 7, this.shipPos.z - cy * 18);
        this.flightCamera.setTarget(new Vector3(this.shipPos.x + sy * 6, this.shipPos.y + 1.4, this.shipPos.z + cy * 6));
      }
      this._applyShipCameraMode?.();
    } else {
      // Drop the player back on the surface where they boarded.
      this._setSurfaceVisible(true);
      const rp = ret.pos || new Vector3(0, 0, 0);
      const gy = this._terrainHeight ? this._terrainHeight(rp.x, rp.z) : 0;
      this.playerRoot.position = new Vector3(rp.x, gy, rp.z);
      this.playerY = gy;
      this.playerVY = 0;
      this.grounded = true;
      if (typeof ret.yaw === 'number') this.yaw = ret.yaw;
      this._applyCameraMode();
    }

    // Allow the wreck to be boarded again.
    this.derelicts.setMarkerBoarded(this._derelictId, false);
    this._emit();
  }

  /** Reflect a freshly-looted container in the 3D (flatten/darken the crate). */
  markDerelictContainerLooted(id) {
    try { this.derelicts?.markContainerLooted?.(id); } catch (_e) { /* noop */ }
  }

  /** Mark the boarded wreck's guards cleared (e.g. after a server confirm). */
  setDerelictCleared() {
    this._derelictCleared = true;
    this.state.derelictCleared = true;
    this._emit();
  }

  /** True while the player is inside a boarded wreck. */
  isInDerelict() { return !!this._inDerelict; }


  // ---------- June 2026 — Walkable Station Interiors ----------

  _setupStationInterior() {
    this._inStation = false;
    this.stationInterior = new StationInteriorSystem(this.scene, {
      getPlayerPos: () => this.playerRoot?.position || new Vector3(0, 0, 0),
      glow: this.glow || null,
    });
  }

  /** Dock the ship and disembark into the station interior on foot. */
  enterStation(station) {
    if (this._disposed || this._inStation) return;
    // Only valid while piloting near a station in space.
    if (!this.state.inShip || this.mode !== 'space') return;
    const near = this.state.nearStation;
    const info = station || near;
    if (!info || !info.id) return;
    if (!this.stationInterior) return;

    // Build the hall keyed off the station id.
    const seed = this._hashStringToInt(`stationint-${info.id}`);
    this.stationInterior.buildInterior(seed, info.name);

    // Hide the space scene (incl. the parked ship + asteroid field), reveal hall.
    if (this.spaceRoot) this.spaceRoot.setEnabled(false);
    try { this.shipRoot?.setEnabled(false); } catch (_e) { /* noop */ }
    if (this._asteroidField) { try { this._asteroidField.parent.setEnabled(false); } catch (_e) { /* noop */ } }
    this.stationInterior.showInterior(true);

    // Save + brighten the environment for the lit interior.
    this._stationSaved = {
      clearColor: this.scene.clearColor?.clone?.() || null,
    };
    this.scene.clearColor = new Color4(0.03, 0.04, 0.06, 1);
    const hemi = this.scene.getLightByName('hemi');
    if (hemi) {
      this._stationSaved.hemiIntensity = hemi.intensity;
      hemi.intensity = 0.7;
    }

    // Disembark: switch to on-foot. mode stays 'space' so all surface systems
    // (terrain, combat, mining, day/night, anomalies) remain suspended.
    this.state.inShip = false;
    this.fpsCamera.parent = this.playerRoot;
    this.fpsCamera.position = new Vector3(0, 1.7, 0);
    if (this._cockpitOccluders) {
      for (const m of this._cockpitOccluders) { try { if (m) m.isVisible = true; } catch (_e) { /* noop */ } }
    }
    const ep = this.stationInterior.getEntryPoint();
    this.playerRoot.position = ep.clone();
    this.playerY = ep.y;
    this.playerVY = 0;
    this.grounded = true;
    this.yaw = Math.PI; // entry sits on the +Z (airlock) side → face the hall
    this.pitch = 0;
    this._applyCameraMode();

    this._inStation = true;
    this.state.inStation = true;
    this.state.stationInfo = { id: info.id, name: info.name };
    this.state.nearStation = null;
    this.state.nearStationTerminal = null;
    this.state.nearStationExit = null;

    this._raiseAllMaterialLightCaps();
    this._emit();
  }

  /** Board the ship again and undock back into space flight. */
  exitStation() {
    if (this._disposed || !this._inStation || !this.stationInterior) return;

    this.stationInterior.showInterior(false);
    this.stationInterior.clearInterior();

    // Restore the space environment.
    const s = this._stationSaved || {};
    if (s.clearColor) this.scene.clearColor = s.clearColor;
    const hemi = this.scene.getLightByName('hemi');
    if (hemi && typeof s.hemiIntensity === 'number') hemi.intensity = s.hemiIntensity;
    this._stationSaved = null;

    // Re-show the space scene + parked ship.
    if (this.spaceRoot) this.spaceRoot.setEnabled(true);
    try { this.shipRoot?.setEnabled(true); } catch (_e) { /* noop */ }
    if (this._asteroidField) { try { this._asteroidField.parent.setEnabled(true); } catch (_e) { /* noop */ } }

    // Board the ship — resume flying from the docked position.
    this._inStation = false;
    this.state.inStation = false;
    this.state.nearStationTerminal = null;
    this.state.nearStationExit = null;
    this.state.inShip = true;
    // We undock right next to the station, so keep nearStation set — this also
    // makes the GameScene 'e' handler defer to React on the same keypress.
    if (this.state.stationInfo) {
      this.state.nearStation = { id: this.state.stationInfo.id, name: this.state.stationInfo.name, distance: 0 };
    }
    this.state.stationInfo = null;

    // Snap the chase camera back to the ship.
    const cy = Math.cos(this.shipYaw || 0), sy = Math.sin(this.shipYaw || 0);
    if (this.flightCamera && this.shipPos) {
      this.flightCamera.position = new Vector3(this.shipPos.x - sy * 18, this.shipPos.y + 7, this.shipPos.z - cy * 18);
      this.flightCamera.setTarget(new Vector3(this.shipPos.x + sy * 6, this.shipPos.y + 1.4, this.shipPos.z + cy * 6));
    }
    this._applyShipCameraMode();
    this._emit();
  }

  /** True while the player is walking inside a station. */
  isInStation() { return !!this._inStation; }

  // ---------- June 2026 — Walkable ship interiors (Hangar) ----------

  _setupShipInterior() {
    this._inShipInterior = false;
    this.shipInterior = new ShipInteriorSystem(this.scene, {
      getPlayerPos: () => this.playerRoot?.position || new Vector3(0, 0, 0),
      glow: this.glow || null,
    });
  }

  // ---------- June 2026 — Dynamic weather ----------

  _setupWeather() {
    this.weather = new WeatherSystem(this.scene, {
      sun: this.sun || null,
      hemi: this.hemi || null,
      baseFogDensity: this.fogDensity,
      baseFogColor: this.fogColor,
      // Follow the active camera so weather works on foot AND in low flight.
      getPlayerPos: () => {
        const cam = this.scene.activeCamera;
        const gp = cam && (cam.globalPosition || cam.position);
        return gp ? new Vector3(gp.x, gp.y, gp.z) : (this.playerRoot?.position || new Vector3(0, 0, 0));
      },
      onLightning: (strength) => {
        try { getAudioSystem().playThunder(strength); } catch (_e) { /* noop */ }
        this.state.lightningAt = Date.now();
        this._emit();
      },
      onState: (w) => {
        this.state.weather = w;
        this._emit();
      },
    });
    const seed = this._hashStringToInt(`weather-${this.body?.name || ''}-${this.body?.type || ''}`);
    this.weather.setBody(this.body, seed);
  }

  /** Whether the active ship offers a walkable interior. */
  shipHasInterior() { return !!this.shipDef?.has_interior; }

  /** Board the ship's cabin on foot (only while landed & in the ship). */
  enterShipInterior() {
    if (this._disposed || this._inShipInterior) return;
    if (!this.shipHasInterior() || !this.shipInterior) return;
    // Must be piloting and landed (surface or space-docked is fine).
    if (!this.state.inShip || !this.shipLanded) return;

    const seed = this._hashStringToInt(`shipint-${this.shipId}`);
    this.shipInterior.buildInterior(seed, this.shipDef);

    // Hide the outside world while inside the cabin.
    this._shipIntPrevMode = this.mode;
    // Switch to 'space' mode so all surface ticks (terrain/grass stream, sky,
    // day-night, anomalies) suspend exactly as they do inside a station.
    this.mode = 'space';
    this._setSurfaceVisible?.(false);
    if (this.spaceRoot) this.spaceRoot.setEnabled(false);
    try { this.shipRoot?.setEnabled(false); } catch (_e) { /* noop */ }
    if (this._asteroidField) { try { this._asteroidField.parent.setEnabled(false); } catch (_e) { /* noop */ } }
    this.shipInterior.showInterior(true);

    // Brighten for the lit cabin; save what we change.
    this._shipIntSaved = {
      clearColor: this.scene.clearColor?.clone?.() || null,
      fogMode: this.scene.fogMode,
    };
    this.scene.fogMode = Scene.FOGMODE_NONE;
    this.scene.clearColor = new Color4(0.03, 0.04, 0.06, 1);
    const hemi = this.scene.getLightByName('hemi');
    if (hemi) { this._shipIntSaved.hemiIntensity = hemi.intensity; hemi.intensity = 0.7; }

    // Disembark on foot inside the cabin.
    this.state.inShip = false;
    this.fpsCamera.parent = this.playerRoot;
    this.fpsCamera.position = new Vector3(0, 1.7, 0);
    if (this._cockpitOccluders) {
      for (const m of this._cockpitOccluders) { try { if (m) m.isVisible = true; } catch (_e) { /* noop */ } }
    }
    const ep = this.shipInterior.getEntryPoint();
    this.playerRoot.position = ep.clone();
    this.playerY = ep.y;
    this.playerVY = 0;
    this.grounded = true;
    this.yaw = 0; // face the bridge (+Z front)
    this.pitch = 0;
    this._applyCameraMode();

    this._inShipInterior = true;
    this.state.inShipInterior = true;
    this.state.canBoardInterior = false;
    this.state.nearShipInteriorExit = null;
    this.state.nearShipFabricator = null;

    this._raiseAllMaterialLightCaps?.();
    this._emit();
  }

  /** Step back into the cockpit and resume piloting. */
  exitShipInterior() {
    if (this._disposed || !this._inShipInterior || !this.shipInterior) return;

    this.shipInterior.showInterior(false);
    this.shipInterior.clearInterior();

    // Restore environment.
    const s = this._shipIntSaved || {};
    if (s.clearColor) this.scene.clearColor = s.clearColor;
    if (typeof s.fogMode === 'number') this.scene.fogMode = s.fogMode;
    const hemi = this.scene.getLightByName('hemi');
    if (hemi && typeof s.hemiIntensity === 'number') hemi.intensity = s.hemiIntensity;
    this._shipIntSaved = null;

    // Re-show the outside world + parked ship.
    this.mode = this._shipIntPrevMode || 'surface';
    if (this._shipIntPrevMode === 'space') {
      if (this.spaceRoot) this.spaceRoot.setEnabled(true);
      if (this._asteroidField) { try { this._asteroidField.parent.setEnabled(true); } catch (_e) { /* noop */ } }
    } else {
      this._setSurfaceVisible?.(true);
    }
    try { this.shipRoot?.setEnabled(true); } catch (_e) { /* noop */ }

    // Board the ship — resume from the parked position.
    this._inShipInterior = false;
    this.state.inShipInterior = false;
    this.state.nearShipInteriorExit = null;
    this.state.nearShipFabricator = null;
    this.state.inShip = true;

    const cy = Math.cos(this.shipYaw || 0), sy = Math.sin(this.shipYaw || 0);
    if (this.flightCamera && this.shipPos) {
      this.flightCamera.position = new Vector3(this.shipPos.x - sy * 18, this.shipPos.y + 7, this.shipPos.z - cy * 18);
      this.flightCamera.setTarget(new Vector3(this.shipPos.x + sy * 6, this.shipPos.y + 1.4, this.shipPos.z + cy * 6));
    }
    this._applyShipCameraMode();
    this._emit();
  }

  /** True while the player is walking inside their ship. */
  isInShipInterior() { return !!this._inShipInterior; }

  /** Update skill-tree progression multipliers live (no scene rebuild). */
  setProgression(p = {}) {
    this._progression = {
      walkMult: Number(p.walkMult || 1) || 1,
      shipSpeedMult: Number(p.shipSpeedMult || 1) || 1,
      jumpMult: Number(p.jumpMult || 1) || 1,
    };
    // Phase 1 — module-driven multipliers fan out to their owning systems.
    const miningMult = Number(p.miningMult || 1) || 1;
    const scanRangeMult = Number(p.scanRangeMult || 1) || 1;
    const weaponDamageMult = Number(p.weaponDamageMult || 1) || 1;
    const weaponFireRateMult = Number(p.weaponFireRateMult || 1) || 1;
    try { this.mining?.setYieldMult?.(miningMult); } catch (e) { /* noop */ }
    try { this.anomalies?.setScanRangeMult?.(scanRangeMult); } catch (e) { /* noop */ }
    try { this.combat?.setWeaponMods?.({ damageMult: weaponDamageMult, fireRateMult: weaponFireRateMult }); } catch (e) { /* noop */ }
  }


  // ---------- Phase 10 — Mouse config (settings sync) ----------

  setMouseConfig({ sensitivity, invertY } = {}) {
    if (typeof sensitivity === 'number' && Number.isFinite(sensitivity)) {
      this._mouseSensitivity = Math.max(0.1, Math.min(5.0, sensitivity));
    }
    if (typeof invertY === 'boolean') {
      this._invertY = invertY;
    }
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    try { window.removeEventListener('resize', this._onResize); } catch (e) { /* noop */ }
    try { window.removeEventListener('keydown', this._onKeyDown); } catch (e) { /* noop */ }
    try { window.removeEventListener('keyup', this._onKeyUp); } catch (e) { /* noop */ }
    try { window.removeEventListener('mousemove', this._onMouseMove); } catch (e) { /* noop */ }
    try { window.removeEventListener('mouseup', this._onMouseUp); } catch (e) { /* noop */ }
    try { document.removeEventListener('pointerlockchange', this._onPointerLockChange); } catch (e) { /* noop */ }
    try { document.removeEventListener('pointerlockerror', this._onPointerLockError); } catch (e) { /* noop */ }
    try { this.canvas.removeEventListener('click', this._onCanvasClick); } catch (e) { /* noop */ }
    try { this.canvas.removeEventListener('mousedown', this._onMouseDown); } catch (e) { /* noop */ }
    try { this.canvas.removeEventListener('contextmenu', this._onContextMenu); } catch (e) { /* noop */ }
    if (document.pointerLockElement === this.canvas) document.exitPointerLock?.();
    try { this.combat?.dispose(); } catch (e) { /* noop */ }
    try { this.mining?.dispose(); } catch (e) { /* noop */ }
    try { this.city?.dispose(); } catch (e) { /* noop */ }
    try { this.structures?.dispose(); } catch (e) { /* noop */ }
    try { this.effects?.dispose(); } catch (e) { /* noop */ }
    try { this._asteroidField?.dispose(); } catch (e) { /* noop */ }
    try { this.npcs?.dispose(); } catch (e) { /* noop */ }
    try { this.anomalies?.dispose(); } catch (e) { /* noop */ }
    try { this.discoveries?.dispose(); } catch (e) { /* noop */ }
    try { this.legendary?.dispose(); } catch (e) { /* noop */ }
    try { this.derelicts?.dispose(); } catch (e) { /* noop */ }
    try { this.caves?.dispose(); } catch (e) { /* noop */ }
    try { this.stationInterior?.dispose(); } catch (e) { /* noop */ }
    try { this.shipInterior?.dispose(); } catch (e) { /* noop */ }
    try { this.weather?.dispose(); } catch (e) { /* noop */ }
    try { this._volcanoEmber?.dispose(); } catch (e) { /* noop */ }
    try { this.scene.dispose(); } catch (e) { /* noop */ }
    try { this.engine.dispose(); } catch (e) { /* noop */ }
  }

  // ---------- internals ----------

  _applyCameraMode() {
    if (this.state && this.state.inShip) return;
    if (this.cameraMode === 'fps') {
      this.astronautMeshes.forEach((m) => { m.isVisible = false; });
      this.scene.activeCamera = this.fpsCamera;
    } else {
      this.astronautMeshes.forEach((m) => { m.isVisible = true; });
      this.scene.activeCamera = this.tpsCamera;
    }
    if (this.state) this.state.cameraMode = this.cameraMode;
  }

  /**
   * Switch the active camera while piloting between cockpit (FPS) and chase
   * (TPS). Hides the cockpit canopy / hull in FPS so the forward view isn't
   * blocked. Safe to call any time — no-ops when not in the ship.
   */
  _applyShipCameraMode() {
    if (!this.state || !this.state.inShip) return;
    const inFps = this.shipCameraMode === 'fps';
    if (this._cockpitOccluders) {
      for (const m of this._cockpitOccluders) {
        try { if (m) m.isVisible = !inFps; } catch (_e) { /* noop */ }
      }
    }
    this.scene.activeCamera = inFps ? this.shipFpsCamera : this.flightCamera;
    if (this.state) this.state.shipCameraMode = this.shipCameraMode;
  }

  _emit() {
    if (this.state) {
      this.state.pointerLocked = this._pointerLocked || false;
      this.state.pointerLockUnavailable = this._pointerLockUnavailable || false;
    }
    try { this.onStateChange({ ...this.state }); } catch (e) { /* noop */ }
  }

  _tick() {
    if (this._disposed) return;
    const dt = Math.min(0.05, this.engine.getDeltaTime() / 1000); // clamp for stability

    // Anchor the sky (sun, halo, stars, moons, parent planet) to the active
    // camera so they appear at consistent positions regardless of how far the
    // player roams.
    if (this.skyAnchor && this.scene.activeCamera) {
      const c = this.scene.activeCamera.globalPosition || this.scene.activeCamera.position;
      this.skyAnchor.position.x = c.x;
      this.skyAnchor.position.z = c.z;
    }

    // Stream the terrain so the world feels boundless (surface mode only).
    if (this.mode !== 'space' && !this._inCave && !this._inDerelict && this._streamTerrain) this._streamTerrain();
    // Stream the grass field independently so it stays dense around the
    // player even between coarse terrain recenters.
    if (this.mode !== 'space' && !this._inCave && !this._inDerelict && this._streamGrass) this._streamGrass();

    // Animate sky moons + cull any celestial body that drops below the
    // horizon. These render in a separate group ON TOP of the terrain, so a
    // moon that sinks below the horizon would otherwise show "through" the
    // ground — hide it explicitly once its centre passes the horizon line.
    const camForSky = this.scene.activeCamera
      ? (this.scene.activeCamera.globalPosition || this.scene.activeCamera.position)
      : null;
    // Keep the flashlight tracking the camera every frame while it's on.
    if (this._flashlightOn) this._updateFlashlight();
    const horizonY = camForSky ? camForSky.y : 0;
    const skyAnchorY = this.skyAnchor ? this.skyAnchor.position.y : 0;
    const skyVisible = this.mode !== 'space' && !this._inCave && !this._inDerelict;
    if (this.skyMoons && this.skyMoons.length > 0) {
      for (const m of this.skyMoons) {
        m.angle += m.omega * dt;
        const r = m.radius;
        const incl = m.inclination;
        const a = m.angle;
        const localY = 400 + Math.sin(a) * r * incl;
        m.mesh.position = new Vector3(
          Math.cos(a) * r,
          localY,
          Math.sin(a) * r
        );
        // World Y (skyAnchor tracks only x/z). Below the horizon ⇒ hide.
        if (skyVisible) {
          m.mesh.setEnabled((skyAnchorY + localY) > horizonY);
        }
      }
    }
    // Parent planet (visible when standing on a moon) — cull below the horizon
    // too, for the rare case the player flies above it in surface flight.
    if (skyVisible && this.parentPlanetMesh) {
      const ppY = skyAnchorY + this.parentPlanetMesh.position.y;
      this.parentPlanetMesh.setEnabled(ppY > horizonY);
    }

    if (this.state.inShip) {
      this._tickFlight(dt);
    } else {
      this._tickWalking(dt);
      // Ship proximity (only relevant when on foot)
      const distToShip = Vector3.Distance(this.playerRoot.position, this.shipRoot.position);
      const near = distToShip < 8.5;
      if (near !== this.state.nearShip) {
        this.state.nearShip = near;
        this._emit();
      }
    }

    // Phase 6 — HUD telemetry update (heading, coords, oxygen, POIs)
    this._tickHud(dt);

    // Phase 7 — combat tick (weapon timers, enemy AI, projectiles)
    if (this.combat) this.combat.tick(dt);
    // Phase 8 — mining tick (deposit streaming, reticle target, drill progress)
    if (this.mining) {
      this.mining.tick(dt);
      // Keep depositPois state fresh for the radar
      const pois = this.mining.getDepositPois();
      const sigOld = this.state.depositPois?.length || 0;
      if (pois.length !== sigOld) {
        this.state.depositPois = pois;
        this._emit();
      } else {
        this.state.depositPois = pois;
      }
      // Phase 12a — continuous mining sparks while drilling.
      if (this.effects) {
        const dpos = this.mining.getActiveDepositPos?.();
        if (dpos) {
          this.effects.spawnMiningSparks(dpos, '#ffb070');
        }
      }
    }
    // Phase 9 — city tick (proximity prompt + radar POI)
    if (this.city && !this._inCave && !this._inDerelict) {
      this.city.tick(dt);
    }
    // Phase 11 — structures tick (build ghost + proximity prompt for E)
    if (this.structures && !this._inCave && !this._inDerelict) {
      this.structures.tick(dt);
      const near = this.structures.getNearStructure(4.5);
      const prev = this.state.nearStructure;
      const snap = near ? { id: near.id, type: near.type, distance: Math.round(near.distance * 10) / 10, doc: near.doc } : null;
      const changed = !!prev !== !!snap
        || (prev && snap && (prev.id !== snap.id || Math.abs(prev.distance - snap.distance) > 0.2));
      if (changed) {
        this.state.nearStructure = snap;
        this._emit();
      }
      // Update build snapshot
      const bs = this.structures.getBuildSnapshot();
      if (bs.active !== this.state.build.active
        || bs.typeId !== this.state.build.typeId
        || !!bs.validPoint !== this.state.build.valid) {
        this.state.build = { active: bs.active, typeId: bs.typeId, valid: !!bs.validPoint };
        this._emit();
      }
    }
    // Phase 10 — day/night cycle (visual only, surface mode only).
    if (this._dayNight && this.mode !== 'space' && !this._inCave && !this._inDerelict) {
      this._tickDayNight(dt);
    }

    // June 2026 — dynamic weather. Ticks AFTER day/night so storm darkening +
    // lightning flashes layer on top of the day/night lighting. Surface only
    // (mode 'space' covers station + ship interiors; caves excluded too).
    if (this.weather) {
      const onSurface = this.mode !== 'space' && !this._inCave && !this._inDerelict;
      if (onSurface) {
        if (!this._weatherEnabled) { this.weather.setEnabled(true); this._weatherEnabled = true; }
        try { this.weather.tick(dt); } catch (_e) { /* noop */ }
      } else if (this._weatherEnabled) {
        this.weather.setEnabled(false);
        this._weatherEnabled = false;
      }
    }

    // June 2026 — cloud drift: slow wind push with wrap-around so the sky
    // never empties out on one side.
    if (this._cloudDrift?.length && this.mode !== 'space') {
      for (const c of this._cloudDrift) {
        c.node.position.x += c.vx * dt;
        if (c.node.position.x > 470) c.node.position.x = -470;
      }
    }

    // June 2026 — underwater look. Applied AFTER day/night + weather so the
    // override wins the per-frame fog writes while the camera is submerged.
    this._tickUnderwater();

    // Phase 10 — nav-light blink (alternating red/green, ~0.8s period).
    if (this._shipNavLights) {
      this._navBlinkT = (this._navBlinkT || 0) + dt;
      const phase = (this._navBlinkT % 0.8) / 0.8;
      const onRight = phase > 0.5;
      const dim = 0.25;
      const left = this._shipNavLights.left;
      const right = this._shipNavLights.right;
      if (left) {
        const k = onRight ? dim : 1.0;
        left.emissiveColor.set(1.0 * k, 0.15 * k, 0.15 * k);
      }
      if (right) {
        const k = onRight ? 1.0 : dim;
        right.emissiveColor.set(0.15 * k, 1.0 * k, 0.25 * k);
      }
    }

    // Phase 12a — Effects tick (biome haze follows player; mining sparks decay)
    if (this.effects) this.effects.tick();
    if (this._asteroidField && this.mode === 'space') {
      try { this._asteroidField.tick(dt); } catch (_e) { /* noop */ }
    }
    // Phase 12b — NPC tick
    if (this.npcs && !this._inCave && !this._inDerelict) {
      try { this.npcs.tick(dt, this.mode); } catch (_e) { /* noop */ }
      // Phase 13 — pedestrian proximity for "Talk" prompt
      if (this.mode !== 'space') {
        const ped = this.npcs.getNearPedestrian?.();
        const prev = this.state.nearPed;
        const snap = ped ? { name: ped.name, distance: Math.round(ped.distance * 10) / 10 } : null;
        const changed = !!prev !== !!snap
          || (prev && snap && (prev.name !== snap.name));
        if (changed) {
          this.state.nearPed = snap;
          this._emit();
        }
      }
    }
    // Phase 13 — Anomaly tick + proximity state for HUD prompt
    // Phase 4 — animate the legendary wonder's lights/lava on the surface.
    if (this.legendary && this.mode !== 'space' && !this._inCave && !this._inDerelict) {
      try { this.legendary.tick(dt); } catch (_e) { /* noop */ }
    }

    if (this.anomalies && this.mode !== 'space' && !this._inCave && !this._inDerelict) {
      try { this.anomalies.tick(dt); } catch (_e) { /* noop */ }
      const near = this.anomalies.getNearAnomaly();
      const prev = this.state.nearAnomaly;
      const snap = near ? { id: near.id, distance: Math.round(near.distance * 10) / 10 } : null;
      const changed = !!prev !== !!snap
        || (prev && snap && (prev.id !== snap.id || Math.abs(prev.distance - snap.distance) > 0.2));
      if (changed) {
        this.state.nearAnomaly = snap;
        this._emit();
      }
      // Refresh anomaly POIs for the radar (cheap; small list).
      const pois = this.anomalies.getRadarPois();
      const same = (this.state.anomalyPois?.length === pois.length);
      if (!same) {
        this.state.anomalyPois = pois;
        this._emit();
      } else {
        this.state.anomalyPois = pois;
      }
    }
    // Batch B — Surface discoverables: proximity prompt + radar pings.
    if (this.discoveries && this.mode !== 'space' && !this._inCave && !this._inDerelict) {
      try { this.discoveries.tick(dt); } catch (_e) { /* noop */ }
      const near = this.discoveries.getNearDiscovery();
      const prev = this.state.nearDiscovery;
      const snap = near
        ? { id: near.id, kind: near.kind, label: near.label, verb: near.verb, distance: Math.round(near.distance * 10) / 10 }
        : null;
      const changed = !!prev !== !!snap
        || (prev && snap && (prev.id !== snap.id || Math.abs(prev.distance - snap.distance) > 0.2));
      if (changed) {
        this.state.nearDiscovery = snap;
        this._emit();
      }
      const dpois = this.discoveries.getRadarPois();
      if ((this.state.discoveryPois?.length || 0) !== dpois.length) {
        this.state.discoveryPois = dpois;
        this._emit();
      } else {
        this.state.discoveryPois = dpois;
      }
    }
    // June 2026 — Caves: surface entrance proximity / cavern exit proximity.
    if (this.caves && this.mode !== 'space' && !this.state.inShip) {
      if (this._inCave) {
        try { this.caves.tick(dt); } catch (_e) { /* noop */ }
        const nearExit = this.caves.getNearExit();
        const prevX = this.state.nearCaveExit;
        const snapX = nearExit ? { distance: Math.round(nearExit.distance * 10) / 10 } : null;
        if (!!prevX !== !!snapX || (prevX && snapX && Math.abs(prevX.distance - snapX.distance) > 0.2)) {
          this.state.nearCaveExit = snapX;
          this._emit();
        }
      } else {
        const nearEnt = this.caves.getNearEntrance();
        const prevE = this.state.nearCaveEntrance;
        const snapE = nearEnt ? { id: nearEnt.id, distance: Math.round(nearEnt.distance * 10) / 10 } : null;
        if (!!prevE !== !!snapE || (prevE && snapE && (prevE.id !== snapE.id || Math.abs(prevE.distance - snapE.distance) > 0.2))) {
          this.state.nearCaveEntrance = snapE;
          this._emit();
        }
        // Surface entrance POIs for the radar (cheap; small list).
        const cpois = this.caves.getEntrancePois();
        if ((this.state.cavePois?.length || 0) !== cpois.length) {
          this.state.cavePois = cpois;
          this._emit();
        } else {
          this.state.cavePois = cpois;
        }
      }
    }
    // June 2026 — Station interior: terminal / airlock proximity prompts.
    if (this.stationInterior && this._inStation && !this.state.inShip) {
      try { this.stationInterior.tick(dt); } catch (_e) { /* noop */ }
      const nearTerm = this.stationInterior.getNearTerminal();
      const prevT = this.state.nearStationTerminal;
      const snapT = nearTerm ? { distance: Math.round(nearTerm.distance * 10) / 10 } : null;
      if (!!prevT !== !!snapT || (prevT && snapT && Math.abs(prevT.distance - snapT.distance) > 0.2)) {
        this.state.nearStationTerminal = snapT;
        this._emit();
      }
      const nearEx = this.stationInterior.getNearExit();
      const prevX = this.state.nearStationExit;
      const snapX = nearEx ? { distance: Math.round(nearEx.distance * 10) / 10 } : null;
      if (!!prevX !== !!snapX || (prevX && snapX && Math.abs(prevX.distance - snapX.distance) > 0.2)) {
        this.state.nearStationExit = snapX;
        this._emit();
      }
      const nearYard = this.stationInterior.getNearShipyard();
      const prevY = this.state.nearStationShipyard;
      const snapY = nearYard ? { distance: Math.round(nearYard.distance * 10) / 10 } : null;
      if (!!prevY !== !!snapY || (prevY && snapY && Math.abs(prevY.distance - snapY.distance) > 0.2)) {
        this.state.nearStationShipyard = snapY;
        this._emit();
      }
      const nearSkills = this.stationInterior.getNearSkills();
      const prevS = this.state.nearStationSkills;
      const snapS = nearSkills ? { distance: Math.round(nearSkills.distance * 10) / 10 } : null;
      if (!!prevS !== !!snapS || (prevS && snapS && Math.abs(prevS.distance - snapS.distance) > 0.2)) {
        this.state.nearStationSkills = snapS;
        this._emit();
      }
    }
    // June 2026 — Ship interior: reactor animation + exit-hatch proximity.
    if (this.shipInterior && this._inShipInterior && !this.state.inShip) {
      try { this.shipInterior.tick(dt); } catch (_e) { /* noop */ }
      const nearEx = this.shipInterior.getNearExit();
      const prevX = this.state.nearShipInteriorExit;
      const snapX = nearEx ? { distance: Math.round(nearEx.distance * 10) / 10 } : null;
      if (!!prevX !== !!snapX || (prevX && snapX && Math.abs(prevX.distance - snapX.distance) > 0.2)) {
        this.state.nearShipInteriorExit = snapX;
        this._emit();
      }
      // Phase 2 — Fabricator terminal proximity.
      const nearFab = this.shipInterior.getNearFabricator?.();
      const prevF = this.state.nearShipFabricator;
      const snapF = nearFab ? { distance: Math.round(nearFab.distance * 10) / 10 } : null;
      if (!!prevF !== !!snapF) {
        this.state.nearShipFabricator = snapF;
        this._emit();
      }
    }
    // Phase 3 — Derelicts: exterior board proximity / interior interactions.
    if (this.derelicts) {
      if (this._inDerelict) {
        try { this.derelicts.tick(dt); } catch (_e) { /* noop */ }

        // Exit hatch
        const nearExit = this.derelicts.getNearExit();
        const prevX = this.state.nearDerelictExit;
        const snapX = nearExit ? { distance: Math.round(nearExit.distance * 10) / 10 } : null;
        if (!!prevX !== !!snapX || (prevX && snapX && Math.abs(prevX.distance - snapX.distance) > 0.2)) {
          this.state.nearDerelictExit = snapX;
          this._emit();
        }

        // Lootable container (gated until guards cleared)
        const canInteract = this._derelictCleared;
        const nearC = canInteract ? this.derelicts.getNearContainer() : null;
        const prevC = this.state.nearDerelictContainer;
        const snapC = nearC ? { id: nearC.id, label: nearC.label, is_module: nearC.is_module, distance: Math.round(nearC.distance * 10) / 10 } : null;
        if ((prevC?.id || null) !== (snapC?.id || null) || (prevC && snapC && Math.abs(prevC.distance - snapC.distance) > 0.2)) {
          this.state.nearDerelictContainer = snapC;
          this._emit();
        }

        // Ship's-log terminal
        const nearL = this.derelicts.getNearLog();
        const prevL = this.state.nearDerelictLog;
        const snapL = nearL ? { distance: Math.round(nearL.distance * 10) / 10 } : null;
        if (!!prevL !== !!snapL || (prevL && snapL && Math.abs(prevL.distance - snapL.distance) > 0.2)) {
          this.state.nearDerelictLog = snapL;
          this._emit();
        }

        // Guard-clear detection — once every guard drone is gone.
        if (this._derelictGuarded && !this._derelictCleared && this.combat) {
          const alive = this.combat.getEnemyPois().filter((p) => String(p.id).startsWith('enemy_guard_')).length;
          if (alive === 0) {
            this._derelictCleared = true;
            this.state.derelictCleared = true;
            try { this.onDerelictCleared?.(this._derelictId); } catch (_e) { /* noop */ }
            this._emit();
          }
        }
      } else {
        // Exterior board proximity: on foot (surface) or piloting (space).
        const onFoot = !this.state.inShip && this.mode !== 'space'
          && !this._inCave && !this._inStation && !this._inShipInterior;
        const piloting = this.state.inShip && this.mode === 'space' && !this._inStation;
        if (onFoot || piloting) {
          const near = this.derelicts.getNearDerelict();
          const prevD = this.state.nearDerelict;
          const snapD = near ? { id: near.id, name: near.name, distance: Math.round(near.distance * 10) / 10 } : null;
          if ((prevD?.id || null) !== (snapD?.id || null) || (prevD && snapD && Math.abs(prevD.distance - snapD.distance) > 0.3)) {
            this.state.nearDerelict = snapD;
            this._emit();
          }
        } else if (this.state.nearDerelict) {
          this.state.nearDerelict = null;
          this._emit();
        }
        // Radar POIs for exterior wrecks.
        const dpois = this.derelicts.getDerelictPois();
        if ((this.state.derelictPois?.length || 0) !== dpois.length) {
          this.state.derelictPois = dpois;
          this._emit();
        } else {
          this.state.derelictPois = dpois;
        }
      }
    }

    // June 2026 — "Board cabin" availability: piloting + landed + has interior.
    if (!this._inShipInterior) {
      const canBoard = !!this.state.inShip && !!this.shipLanded && this.shipHasInterior();
      if (canBoard !== this.state.canBoardInterior) {
        this.state.canBoardInterior = canBoard;
        this._emit();
      }
    }
    // Ship exhaust intensity scales with throttle when piloting, off otherwise.
    if (this.effects) {
      if (this.state.inShip) {
        this.effects.setShipExhaustIntensity(this.shipThrottle || 0, !!this.state.shipBoost);
      } else {
        this.effects.setShipExhaustIntensity(0, false);
      }
    }

    this.scene.render();
  }

  // ---------- Phase 10 — Day/Night cycle ----------

  /**
   * June 2026 — Underwater rendering. While the active camera sits below the
   * water plane, the world gets murky: dense teal exponential fog, matching
   * clear colour, dimmed sunlight and a HUD flag (`state.underwater`) that
   * React uses for a blue vignette overlay. Runs AFTER day/night + weather in
   * the tick so it overrides their per-frame fog writes; on surfacing those
   * systems naturally restore the look on the next frame.
   */
  _tickUnderwater() {
    const onSurface = this.mode !== 'space' && !this._inCave && !this._inDerelict
      && !this._inStation && !this._inShipInterior;
    const cam = this.scene.activeCamera;
    const camY = cam ? (cam.globalPosition || cam.position).y : Infinity;
    const under = !!(onSurface && this.waterLevel != null && this.waterPlane
      && this.waterPlane.isEnabled() && camY < this.waterLevel - 0.05);

    if (under) {
      if (!this._underwater) {
        this._underwater = true;
        this._preUnderwaterClear = this.scene.clearColor?.clone?.() || null;
        if (this.state) { this.state.underwater = true; this._emit(); }
      }
      // Murk thickens with depth.
      const depth = Math.max(0, this.waterLevel - camY);
      const murk = new Color3(0.035, 0.16, 0.22);
      this.scene.fogMode = Scene.FOGMODE_EXP2;
      this.scene.fogDensity = Math.min(0.10, 0.040 + depth * 0.004);
      this.scene.fogColor.set(murk.r, murk.g, murk.b);
      this.scene.clearColor = Color4.FromColor3(murk, 1);
      // Sunlight attenuates fast through water (day/night re-sets these each
      // frame before us, so scaling here is stable).
      if (this.sun) this.sun.intensity *= 0.35;
      if (this.hemi) {
        this.hemi.intensity *= 0.65;
        this.hemi.diffuse.set(0.25, 0.55, 0.65);
      }
    } else if (this._underwater) {
      this._underwater = false;
      if (this._preUnderwaterClear) this.scene.clearColor = this._preUnderwaterClear;
      if (this.state) { this.state.underwater = false; this._emit(); }
      // fogMode/density/colour are rewritten by day/night + weather next tick.
    }
  }

  _tickDayNight(dt) {
    const dn = this._dayNight;
    if (!dn) return;
    dn.t = (dn.t + dt) % dn.period;

    // Phase in [0, 1) over the full cycle. phi=0 → midnight, 0.25 → dawn,
    // 0.5 → noon, 0.75 → dusk. The sun travels a full circle around the Z
    // axis so the light direction and sun mesh are guaranteed to be aligned.
    const phi = dn.t / dn.period;
    // alpha = 0 at noon (sun overhead), -π/2 at dawn (east horizon),
    //       +π/2 at dusk (west horizon), ±π at midnight (below horizon).
    const alpha = (phi - 0.5) * Math.PI * 2;
    const cosA = Math.cos(alpha);
    const sinA = Math.sin(alpha);
    // Slight Z tilt so light doesn't run perfectly parallel to any cardinal
    // axis — gives shadows a more natural orientation.
    const tilt = -0.22;
    // sunUp = unit vector FROM ground TO sun. y > 0 → above horizon.
    const sunUp = new Vector3(sinA, cosA, tilt).normalize();
    // Day/dusk envelopes derived from the sun's altitude.
    const day = Math.max(0, sunUp.y);          // 0..~1 ; 1 = noon
    const dusk = Math.max(0, 1 - Math.abs(sunUp.y)); // peaks near horizon
    // Floor everything so night isn't pitch-black (still readable).
    const nightFloor = 0.18;

    // 1) Sun direction + sun mesh placement. Light points FROM sun TO ground.
    if (this.sun) {
      this.sun.direction = sunUp.scale(-1);
      // Anchor the directional light's position above the active camera so
      // the shadow map's orthographic frustum stays centered on the player.
      const cam = this.scene.activeCamera;
      const cp = (cam && (cam.globalPosition || cam.position)) || Vector3.Zero();
      // Place the light "source" a fixed distance up-sun from the player.
      this.sun.position = cp.add(sunUp.scale(120));

      // Intensity: day → base, dusk → warm dim, night → near zero.
      this.sun.intensity = dn.sunBase * Math.max(nightFloor * 0.15, day);
      // Color: shift toward warm orange at dusk/dawn, neutral at noon.
      const warm = dusk;          // 0..1
      const r = dn.sunBaseColor.r + warm * 0.10;
      const g = dn.sunBaseColor.g - warm * 0.05;
      const b = dn.sunBaseColor.b - warm * 0.20;
      this.sun.diffuse.set(
        Math.max(0, Math.min(1, r)),
        Math.max(0, Math.min(1, g)),
        Math.max(0, Math.min(1, b)),
      );
    }
    // Keep the visible sun mesh group locked to the light's direction so the
    // shadows always look like they're being cast by the visible sun.
    if (this._sunGroup) {
      this._sunGroup.position = sunUp.scale(this._sunDist || 1200);
    } else if (this.sunMesh) {
      this.sunMesh.position = sunUp.scale(this._sunDist || 1200);
    }
    // Aurora ribbons fade in once the sun drops below the horizon.
    if (this._aurora) {
      const nightF = Math.max(0, Math.min(1, (0.12 - sunUp.y) / 0.45));
      this._aurora.t += dt;
      const T = this._aurora.t;
      for (let i = 0; i < this._aurora.bands.length; i++) {
        const b = this._aurora.bands[i];
        b.mat.alpha = nightF * (0.4 + 0.2 * Math.sin(T * 0.4 + i));
        if (b.tex) b.tex.uOffset = (b.tex.uOffset + dt * (0.012 + i * 0.005)) % 1;
        const hue = (Math.sin(T * 0.12 + i) + 1) * 0.5;
        b.mat.emissiveColor.set(
          b.base.r * (0.7 + hue * 0.5),
          b.base.g * (0.7 + (1 - hue) * 0.5),
          b.base.b * (0.8 + hue * 0.3),
        );
      }
    }
    // Rotate the entire sky dome (skybox + starfield) around the Z axis so
    // the celestial backdrop travels with the sun. The rotation matches the
    // sun's arc, giving the illusion that the whole heavens are moving.
    // Use a quaternion so we bypass any Euler-cache issue and guarantee the
    // world matrix is rebuilt every frame.
    if (this._skyDome) {
      this._skyDome.rotationQuaternion = Quaternion.RotationAxis(
        new Vector3(0, 0, 1),
        alpha
      );
    }

    // 2) Hemispheric ambient — dim at night, full at noon.
    if (this.hemi) {
      this.hemi.intensity = dn.hemiBase * (nightFloor + (1 - nightFloor) * day);
      // Subtly tint ambient toward blue at night
      const bbase = dn.hemiBaseColor;
      const bnight = new Color3(0.18, 0.22, 0.40);
      const t = 1 - day;  // 0 at noon, 1 at night
      this.hemi.diffuse.set(
        bbase.r * (1 - t) + bnight.r * t,
        bbase.g * (1 - t) + bnight.g * t,
        bbase.b * (1 - t) + bnight.b * t,
      );
    }

    // 3) Skybox emissive — darker at night, warmer at dusk, base at noon.
    if (this.skybox && this.skybox.material && dn.skyBase) {
      const sb = dn.skyBase;
      // Dim factor: night = 0.20, noon = 1.0
      const dim = nightFloor + (1 - nightFloor) * day;
      // Warm dusk overlay
      const warm = dusk * 0.25;
      const r = sb.r * dim + warm;
      const g = sb.g * dim + warm * 0.55;
      const b = sb.b * dim + warm * 0.10;
      this.skybox.material.emissiveColor.set(
        Math.max(0, Math.min(1, r)),
        Math.max(0, Math.min(1, g)),
        Math.max(0, Math.min(1, b)),
      );
    }

    // Publish a small HUD-visible value occasionally so React can render an
    // optional clock badge if it wants (does not require any React change).
    if (this.state) {
      // 0..1 mapped to a 24h-like phase. 0.25 = dawn, 0.5 = noon, 0.75 = dusk, 0 = midnight.
      this.state.timeOfDay = (phi + 0.5) % 1;  // shift so 0 = midnight
    }
  }

  // ---------- Phase 6 — HUD helpers ----------

  _atmosphereBreathable() {
    // Toxic and 'none' atmospheres deplete oxygen; Thin is restrictive; Dense is fine.
    const a = String(this.body?.atmosphere || 'None').toLowerCase();
    if (a.includes('toxic')) return false;
    if (a === '' || a === 'none' || a === 'unknown') return false;
    return true; // Thin / Dense / habitable variants all considered breathable enough
  }

  _oxygenDrainRate() {
    // Per-second drain when not breathable (and not in ship/near ship).
    const a = String(this.body?.atmosphere || 'None').toLowerCase();
    if (a.includes('toxic')) return 1.6;   // toxic depletes fast (suit damage)
    if (a === '' || a === 'none') return 1.0;
    if (a.includes('thin')) return 0.35;   // thin still mildly depletes
    return 0;
  }

  _biomeLabel() {
    // Human-friendly label combining body type + atmosphere flavor.
    if (!this.body) return 'Unknown';
    if (this.body.kind === 'moon') return `Moon · ${this.body.parentType || 'Body'}`;
    const t = this.body.type || 'Body';
    const a = String(this.body.atmosphere || 'None');
    return `${t} · ${a} atmosphere`;
  }

  _normalizeAngle360(rad) {
    // Convert yaw (radians) to compass-style 0..360 degrees with 0 = north (-Z).
    // Our world: forward when yaw=0 is +Z (cos), so north (-Z) is yaw=π.
    // We map yaw → bearing so 0° = north, 90° = east (+X), 180° = south, 270° = west.
    // bearing = (180° - yaw)
    let deg = 180 - (rad * 180) / Math.PI;
    deg = ((deg % 360) + 360) % 360;
    return deg;
  }

  _bearingTo(targetX, targetZ, fromX, fromZ) {
    // Compass bearing from (fromX, fromZ) toward (targetX, targetZ).
    // 0° = north (-Z), 90° = east (+X).
    const dx = targetX - fromX;
    const dz = targetZ - fromZ;
    // atan2 with our convention: north is -Z, east is +X.
    let deg = (Math.atan2(dx, -dz) * 180) / Math.PI;
    deg = ((deg % 360) + 360) % 360;
    return deg;
  }

  _tickHud(dt) {
    if (!this.state) return;

    // Active position: use ship in flight, player on foot.
    const inShip = !!this.state.inShip;
    const inSpace = this.mode === 'space';
    const pos = inShip
      ? (this.shipRoot?.position || this.playerRoot?.position)
      : (this.playerRoot?.position || this.shipRoot?.position);
    if (!pos) return;

    // Heading
    const yaw = inShip ? (this.shipYaw || 0) : (this.yaw || 0);
    const heading = this._normalizeAngle360(yaw);

    // Ship bearing/distance (only meaningful on foot or in surface flight)
    let shipBearing = 0;
    let shipDistance = 0;
    if (this.shipRoot && !inSpace) {
      const sx = this.shipRoot.position.x;
      const sz = this.shipRoot.position.z;
      shipBearing = this._bearingTo(sx, sz, pos.x, pos.z);
      shipDistance = Math.hypot(sx - pos.x, sz - pos.z);
    }

    // Oxygen — only depletes on foot in unbreathable atmospheres, on surface.
    // Replenishes when near ship (≤ 12u) or in ship.
    const replenishNear = (this.state.nearShip || inShip || inSpace
      || (this.structures && this.structures.isNearOxygenSource()));
    let oxy = this.state.oxygen ?? 100;
    const max = this.state.maxOxygen ?? 100;
    if (replenishNear) {
      oxy = Math.min(max, oxy + 14 * dt); // fast recharge
    } else if (!this._atmosphereBreathable() && !inSpace) {
      oxy = Math.max(0, oxy - this._oxygenDrainRate() * dt);
    }

    // POIs for radar — ship is always one. (Deposits/enemies/cities added in P7-P9.)
    const pois = [];
    if (this.shipRoot && !inSpace) {
      pois.push({
        id: 'ship',
        kind: 'ship',
        x: this.shipRoot.position.x,
        z: this.shipRoot.position.z,
        label: 'Ship',
      });
    }

    // Phase 9 — city POI (single per body; uses plaza center)
    if (this.city) {
      const cstate = this.city.getState();
      if (cstate?.hasCity && cstate.plaza) {
        pois.push({
          id: 'city',
          kind: 'city',
          x: cstate.plaza.x,
          z: cstate.plaza.z,
          label: cstate.name || 'Colony',
        });
      }
    }
    // Phase 11 — structure POIs (beacons + crates/miners/pads/turrets)
    if (this.structures) {
      for (const p of this.structures.getPois()) pois.push(p);
    }

    // Throttle emissions: only update once per ~150ms or on meaningful deltas.
    this._hudAccum = (this._hudAccum || 0) + dt;
    const headingDelta = Math.abs((this.state.heading || 0) - heading);
    const oxyDelta = Math.abs((this.state.oxygen ?? 100) - oxy);
    const shouldEmit =
      this._hudAccum > 0.15 ||
      headingDelta > 1.5 ||
      oxyDelta > 0.5;

    this.state.heading = heading;
    this.state.coords = { x: pos.x, y: pos.y, z: pos.z };
    this.state.oxygen = oxy;
    this.state.oxygenDepleting = !replenishNear && !this._atmosphereBreathable() && !inSpace;
    this.state.breathable = this._atmosphereBreathable();
    this.state.biomeLabel = this.state.biomeLabel || this._biomeLabel();
    this.state.shipBearing = shipBearing;
    this.state.shipDistance = shipDistance;
    this.state.pois = pois;

    if (shouldEmit) {
      this._hudAccum = 0;
      this._emit();
    }
  }

  _tickWalking(dt) {
    const speed = (this.keys.shift ? 9 : 4.5) * (this._progression?.walkMult || 1) * dt;
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    let mx = 0, mz = 0;
    if (this.keys.w) { mx += sin; mz += cos; }
    if (this.keys.s) { mx -= sin; mz -= cos; }
    if (this.keys.d) { mx += cos; mz -= sin; }
    if (this.keys.a) { mx -= cos; mz += sin; }
    const len = Math.hypot(mx, mz);
    if (len > 0) {
      // Less directional control while airborne
      const airFactor = this.grounded ? 1 : 0.35;
      mx = (mx / len) * speed * airFactor;
      mz = (mz / len) * speed * airFactor;
      const p = this.playerRoot.position;
      p.x += mx;
      p.z += mz;
      // No world bounds — terrain streams to follow the player (see _streamTerrain)
    }

    // Keep the player inside the cavern walls while underground.
    if (this._inCave && this.caves) {
      const c = this.caves.clamp(this.playerRoot.position.x, this.playerRoot.position.z);
      this.playerRoot.position.x = c.x;
      this.playerRoot.position.z = c.z;
    } else if (this._inStation && this.stationInterior) {
      const c = this.stationInterior.clamp(this.playerRoot.position.x, this.playerRoot.position.z);
      this.playerRoot.position.x = c.x;
      this.playerRoot.position.z = c.z;
    } else if (this._inShipInterior && this.shipInterior) {
      const c = this.shipInterior.clamp(this.playerRoot.position.x, this.playerRoot.position.z);
      this.playerRoot.position.x = c.x;
      this.playerRoot.position.z = c.z;
    } else if (this._inDerelict && this.derelicts) {
      const c = this.derelicts.clamp(this.playerRoot.position.x, this.playerRoot.position.z);
      this.playerRoot.position.x = c.x;
      this.playerRoot.position.z = c.z;
    }

    // Jump + gravity (per-planet gravity)
    const g = this.gravity || 9.8;
    // Ground height under the player (cave floor while underground)
    const gx = this.playerRoot.position.x;
    const gz = this.playerRoot.position.z;
    const groundY = this._walkGroundHeight(gx, gz);
    if (this.grounded && this.keys.space) {
      // Constant ~1.6m jump apex regardless of gravity so it always feels playable
      this.playerVY = Math.sqrt(2 * g * 1.6 * (this._progression?.jumpMult || 1));
      this.grounded = false;
    }
    if (!this.grounded) {
      this.playerVY -= g * dt;
    } else {
      // Snap to terrain when grounded so walking up/down hills follows the surface
      this.playerY = groundY;
    }
    this.playerY += this.playerVY * dt;
    if (this.playerY <= groundY) {
      this.playerY = groundY;
      this.playerVY = 0;
      if (!this.grounded) {
        this.grounded = true;
        if (this.state) { this.state.isGrounded = true; this._emit(); }
      }
    } else if (this.grounded === true) {
      this.grounded = false;
      if (this.state) { this.state.isGrounded = false; this._emit(); }
    }
    this.playerRoot.position.y = this.playerY;

    // Rotate astronaut body to face yaw
    this.playerRoot.rotation = new Vector3(0, this.yaw, 0);

    // Apply pitch/yaw to active camera (recoilPitch from CombatSystem kicks the view up)
    const recoil = this.combat?.recoilPitch || 0;
    if (this.cameraMode === 'fps') {
      this.fpsCamera.rotation = new Vector3(-this.pitch - recoil, 0, 0);
    } else {
      this.tpsCamera.target = this.playerRoot.position.add(new Vector3(0, 1.4, 0));
      this.tpsCamera.alpha = -this.yaw - Math.PI / 2;
      this.tpsCamera.beta = Math.PI / 2 - (this.pitch + recoil);
    }
  }

  _tickFlight(dt) {
    if (this.mode === 'space') {
      this._tickSpace(dt);
      return;
    }
    // --- Inputs -----------------------------------------------------------
    // Throttle smoothing toward target (W full forward, S half reverse)
    const throttleTarget = (this.keys.w ? 1 : 0) - (this.keys.s ? 0.5 : 0);
    const lerp = (a, b, t) => a + (b - a) * Math.min(1, t);
    this.shipThrottle = lerp(this.shipThrottle, throttleTarget, dt * 3);

    // Yaw via A/D (in addition to mouse-drag steering on shipYaw)
    const yawInput = (this.keys.a ? -1 : 0) + (this.keys.d ? 1 : 0);
    this.shipYaw += yawInput * 1.4 * dt;

    // Visual banking into turns
    const desiredRoll = -yawInput * 0.4;
    this.shipRoll = lerp(this.shipRoll, desiredRoll, dt * 4);

    const boost = this.keys.shift;

    // --- Compute orientation vectors -------------------------------------
    const cy = Math.cos(this.shipYaw), sy = Math.sin(this.shipYaw);
    const cp = Math.cos(this.shipPitch), sp = Math.sin(this.shipPitch);
    const forward = new Vector3(sy * cp, -sp, cy * cp);

    // --- Forces ----------------------------------------------------------
    const spMult = (this._shipPerf?.speedMult || 1.0) * (this._progression?.shipSpeedMult || 1.0);
    const thrustAccel = (boost ? 70 : 32) * spMult * this.shipThrottle; // m/s^2
    if (Math.abs(thrustAccel) > 0.001) {
      this.shipVel.addInPlace(forward.scale(thrustAccel * dt));
    }
    // Vertical thrust (Space ascend, Ctrl/X descend)
    if (this.keys.space) this.shipVel.y += 28 * dt;
    if (this.keys.ctrl || this.keys.x) this.shipVel.y -= 28 * dt;

    // Mild gravity to encourage landing when idle. Scaled by body gravity (lighter on moons).
    this.shipVel.y -= (this.gravity || 9.8) * 0.12 * dt;

    // Air drag (per-axis) — slightly more on vertical so it doesn't float forever
    const dragH = 0.45;
    const dragV = 0.6;
    this.shipVel.x *= Math.max(0, 1 - dragH * dt);
    this.shipVel.z *= Math.max(0, 1 - dragH * dt);
    this.shipVel.y *= Math.max(0, 1 - dragV * dt);

    // Clamp top speed
    const maxSpeed = (boost ? 95 : 45) * spMult;
    const sp2 = this.shipVel.length();
    if (sp2 > maxSpeed) this.shipVel.scaleInPlace(maxSpeed / sp2);

    // --- Integrate -------------------------------------------------------
    this.shipPos.addInPlace(this.shipVel.scale(dt));

    // Ground collision: gear feet rest at terrain height under the ship
    const shipGroundY = this._terrainHeight ? this._terrainHeight(this.shipPos.x, this.shipPos.z) : 0;
    if (this.shipPos.y < shipGroundY) {
      this.shipPos.y = shipGroundY;
      if (this.shipVel.y < 0) this.shipVel.y = 0;
      // Strong horizontal damping when on the ground (skidding to a halt)
      this.shipVel.x *= Math.pow(0.001, dt);
      this.shipVel.z *= Math.pow(0.001, dt);
      this.shipLanded = true;
      // Level out pitch on ground
      this.shipPitch = lerp(this.shipPitch, 0, dt * 6);
    } else if (this.shipPos.y > shipGroundY + 1.2) {
      this.shipLanded = false;
    }

    // World bounds: only a vertical ceiling — horizontal is infinite (terrain streams).
    const ceil = 250;
    if (this.shipPos.y > ceil) { this.shipPos.y = ceil; this.shipVel.y = 0; }

    // --- Apply transform -------------------------------------------------
    this.shipRoot.position = this.shipPos;
    this.shipRoot.rotation = new Vector3(this.shipPitch, this.shipYaw, this.shipRoll);

    // --- Thruster glow ---------------------------------------------------
    if (this.thrusterMat) {
      const glow = 0.25 + Math.max(0, this.shipThrottle) * (boost ? 1.3 : 0.8);
      this.thrusterMat.emissiveColor = new Color3(0.35 * glow, 0.55 * glow, 0.95 * glow);
    }
    if (this._thrusterRingMat) {
      const glow = 0.4 + Math.max(0, this.shipThrottle) * (boost ? 1.4 : 0.9);
      this._thrusterRingMat.emissiveColor = new Color3(0.45 * glow, 0.65 * glow, 1.0 * glow);
    }

    // --- Chase camera ----------------------------------------------------
    // Position behind & above the ship along its current heading. Only
    // animate the chase cam when it is actually the active camera (TPS).
    if (this.shipCameraMode === 'tps') {
      const camBehind = 11;
      const camUp = 4.2;
      const desired = new Vector3(
        this.shipPos.x - sy * camBehind,
        this.shipPos.y + camUp,
        this.shipPos.z - cy * camBehind
      );
      const cam = this.flightCamera.position.clone();
      const camLerp = Math.min(1, dt * 5);
      cam.x += (desired.x - cam.x) * camLerp;
      cam.y += (desired.y - cam.y) * camLerp;
      cam.z += (desired.z - cam.z) * camLerp;
      this.flightCamera.position = cam;
      this.flightCamera.setTarget(new Vector3(
        this.shipPos.x + sy * 3,
        this.shipPos.y + 1.4,
        this.shipPos.z + cy * 3
      ));
    }

    // --- HUD state -------------------------------------------------------
    const speedNow = this.shipVel.length();
    const altNow = Math.max(0, this.shipPos.y - shipGroundY);
    const canExit = altNow < 2.5 && speedNow < 2.5;
    const canLaunchNow = !!(this.spaceBodies && this.spaceBodies.length > 0);
    const changed =
      Math.abs((this.state.shipSpeed || 0) - speedNow) > 0.2 ||
      Math.abs((this.state.shipAltitude || 0) - altNow) > 0.3 ||
      Math.abs((this.state.shipThrottle || 0) - this.shipThrottle) > 0.05 ||
      this.state.shipBoost !== boost ||
      this.state.canExit !== canExit ||
      this.state.canLaunch !== canLaunchNow;
    this.state.shipSpeed = speedNow;
    this.state.shipAltitude = altNow;
    this.state.shipThrottle = this.shipThrottle;
    this.state.shipBoost = boost;
    this.state.canExit = canExit;
    this.state.canLaunch = canLaunchNow;
    if (changed) this._emit();
  }

  _tickSpace(dt) {
    // Gentle self-rotation so the planetary textures read as spinning worlds.
    if (this.spaceBodies) {
      for (let i = 0; i < this.spaceBodies.length; i++) {
        const m = this.spaceBodies[i].mesh;
        if (m) m.rotation.y += dt * (this.spaceBodies[i].kind === 'moon' ? 0.04 : 0.025 + (i % 3) * 0.01);
      }
    }
    // Inputs
    const throttleTarget = (this.keys.w ? 1 : 0) - (this.keys.s ? 0.5 : 0);
    const lerp = (a, b, t) => a + (b - a) * Math.min(1, t);
    this.shipThrottle = lerp(this.shipThrottle, throttleTarget, dt * 2);

    // Yaw via A/D (mouse drag also yaws/pitches via _onMouseMove → shipYaw/Pitch)
    const yawInput = (this.keys.a ? -1 : 0) + (this.keys.d ? 1 : 0);
    this.shipYaw += yawInput * 0.9 * dt;
    const desiredRoll = -yawInput * 0.35;
    this.shipRoll = lerp(this.shipRoll, desiredRoll, dt * 3);

    const boost = this.keys.shift;

    const cy = Math.cos(this.shipYaw), sy = Math.sin(this.shipYaw);
    const cp = Math.cos(this.shipPitch), sp = Math.sin(this.shipPitch);
    const forward = new Vector3(sy * cp, -sp, cy * cp);

    // No gravity, very light damping
    const thrustAccel = (boost ? 240 : 90) * this.shipThrottle;
    if (Math.abs(thrustAccel) > 0.001) {
      this.shipVel.addInPlace(forward.scale(thrustAccel * dt));
    }
    // Vertical (relative to world) — useful for repositioning
    if (this.keys.space) this.shipVel.y += 60 * dt;
    if (this.keys.ctrl || this.keys.x) this.shipVel.y -= 60 * dt;

    // Light drag so the ship can slow down
    const drag = 0.18;
    this.shipVel.scaleInPlace(Math.max(0, 1 - drag * dt));

    const maxSpeed = boost ? 600 : 220;
    const sp2 = this.shipVel.length();
    if (sp2 > maxSpeed) this.shipVel.scaleInPlace(maxSpeed / sp2);

    this.shipPos.addInPlace(this.shipVel.scale(dt));

    // Apply transform
    this.shipRoot.position = this.shipPos;
    this.shipRoot.rotation = new Vector3(this.shipPitch, this.shipYaw, this.shipRoll);

    // Thruster glow
    if (this.thrusterMat) {
      const glow = 0.25 + Math.max(0, this.shipThrottle) * (boost ? 1.5 : 0.9);
      this.thrusterMat.emissiveColor = new Color3(0.35 * glow, 0.55 * glow, 0.95 * glow);
    }
    if (this._thrusterRingMat) {
      const glow = 0.4 + Math.max(0, this.shipThrottle) * (boost ? 1.6 : 1.0);
      this._thrusterRingMat.emissiveColor = new Color3(0.45 * glow, 0.65 * glow, 1.0 * glow);
    }

    // Chase camera (longer behind for space scale). Skip updates when the
    // player has selected the cockpit/FPS view.
    if (this.shipCameraMode === 'tps') {
      const camBehind = 22;
      const camUp = 7;
      const desired = new Vector3(
        this.shipPos.x - sy * camBehind,
        this.shipPos.y + camUp,
        this.shipPos.z - cy * camBehind,
      );
      const cam = this.flightCamera.position.clone();
      const camLerp = Math.min(1, dt * 4);
      cam.x += (desired.x - cam.x) * camLerp;
      cam.y += (desired.y - cam.y) * camLerp;
      cam.z += (desired.z - cam.z) * camLerp;
      this.flightCamera.position = cam;
      this.flightCamera.setTarget(new Vector3(
        this.shipPos.x + sy * 8,
        this.shipPos.y + 1.6,
        this.shipPos.z + cy * 8,
      ));
    }

    // Approach detection: find nearest body
    let nearest = null;
    let nearestDist = Infinity;
    let approach = null;
    for (const b of this.spaceBodies) {
      const dx = this.shipPos.x - b.worldPos.x;
      const dy = this.shipPos.y - b.worldPos.y;
      const dz = this.shipPos.z - b.worldPos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const surfaceDist = dist - b.scaledRadius;
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = b;
      }
      // Approach threshold: within ~30 units of the body's surface
      if (surfaceDist < 30 && surfaceDist > -10) {
        if (!approach || surfaceDist < (approach._surfaceDist || Infinity)) {
          approach = { ...b, _surfaceDist: surfaceDist };
        }
      }
    }

    const newApproachTarget = approach
      ? { kind: approach.kind, planetIdx: approach.planetIdx, moonIdx: approach.moonIdx, name: approach.name, type: approach.type }
      : null;
    const targetChanged = (this.state.approachTarget?.name || null) !== (newApproachTarget?.name || null);

    // Station proximity — when the ship is within a station's dock radius we
    // surface a `nearStation` HUD prompt the player can click to open the
    // station trade overlay (sells from BOTH player + ship inventories).
    let nearStation = null;
    let nearStationDist = Infinity;
    if (this.spaceStations && this.spaceStations.length > 0) {
      for (const st of this.spaceStations) {
        // Animate beacons (cheap blink) — runs regardless of distance.
        st.beaconPhase += dt * 2.4;
        if (st.beaconA && st.beaconA.material) {
          const p = (Math.sin(st.beaconPhase) + 1) * 0.5;
          st.beaconA.material.emissiveColor.set(1, 0.25 + p * 0.3, 0.25 + p * 0.3);
        }
        if (st.beaconB && st.beaconB.material) {
          const p = (Math.sin(st.beaconPhase + Math.PI) + 1) * 0.5;
          st.beaconB.material.emissiveColor.set(0.30 * (0.6 + p * 0.4), 0.6 * (0.6 + p * 0.4), 1);
        }
        // Slow ring spin for ambience
        if (st.root) st.root.rotation.y += dt * 0.18;

        const dx = this.shipPos.x - st.worldPos.x;
        const dy = this.shipPos.y - st.worldPos.y;
        const dz = this.shipPos.z - st.worldPos.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < (st.dockRadius || 60) && dist < nearStationDist) {
          nearStationDist = dist;
          nearStation = { id: st.id, name: st.name, distance: dist };
        }
      }
    }
    const stationChanged = (this.state.nearStation?.id || null) !== (nearStation?.id || null);

    // HUD state — emit on meaningful change to avoid React thrash
    const speedNow = this.shipVel.length();
    const changed =
      Math.abs((this.state.shipSpeed || 0) - speedNow) > 0.5 ||
      Math.abs((this.state.shipThrottle || 0) - this.shipThrottle) > 0.05 ||
      this.state.shipBoost !== boost ||
      targetChanged ||
      stationChanged ||
      Math.abs((this.state.nearestBodyDistance || 0) - (nearest ? Math.max(0, nearestDist - (nearest.scaledRadius || 0)) : 0)) > 10;
    this.state.shipSpeed = speedNow;
    this.state.shipAltitude = 0;
    this.state.shipThrottle = this.shipThrottle;
    this.state.shipBoost = boost;
    this.state.canExit = false;
    this.state.approachTarget = newApproachTarget;
    this.state.nearStation = nearStation;
    this.state.nearestBodyName = nearest?.name || null;
    this.state.nearestBodyDistance = nearest ? Math.max(0, nearestDist - (nearest.scaledRadius || 0)) : 0;
    if (changed) this._emit();
  }
}

export default GameScene;

